/* ============================================================
 * köbi Label-Editor — App-Logik (klassisch)
 * ============================================================
 * Portiert aus Etiketten-Baukasten_Codex/etiketten-tool.html
 * Z. 469–2294. Asset-Pfade wurden auf state.assets-URLs umgebogen
 * (Frame-PDFs, Frame-Bilder, ICC-Profil, Fonts und PDF-Bibliotheken kommen
 * jetzt per fetch() aus dem Theme-Asset-Verzeichnis).
 *
 * Architektur:
 * - Klassisches Script (kein type="module"), nutzt window.PDFLib,
 *   pdfjsLib und fontkit aus den lokalen Script-Tags der Section.
 * - Bridge zum 3D-Modul über window.bottle*-Funktionen
 *   (definiert in koebi-label-editor-3d.js).
 * - Init wartet auf DOMContentLoaded UND PDF-Bibs (defer).
 * - Mobile-Frühreturn (Viewport <900px → kein Init, kein Three.js).
 * - Cart-Submit: PDF-Bytes via DataTransfer-API in hidden file
 *   input → Cart-Form-Submit → Shopify lädt File auf eigenes CDN
 *   hoch und legt es als Line-Item-Property an.
 *
 * Quelle der Funktions-Karte: KOEBI-LABEL-EDITOR.md §5.
 * ============================================================ */

(function () {
  'use strict';

  // ============================================================
  // Debug-Flag — auf true setzen für Variant-Sync-Logs in der Console.
  // Aktivierbar via window.koebiLabelDebug = true (vor Page-Load).
  // ============================================================
  // dlog checkt `window.koebiLabelDebug` jedes Mal LIVE, damit man's auch
  // nach dem Page-Load via Console aktivieren kann. Performance-Kosten = 1
  // Truthy-Check pro Log-Call, vernachlässigbar.
  const isDbg = () => !!window.koebiLabelDebug;
  const dlog  = (...args) => { if (isDbg()) console.log('[koebi-label]', ...args); };
  const dwarn = (...args) => console.warn('[koebi-label]', ...args);

  // ============================================================
  // Mobile-Frühreturn — DEAKTIVIERT seit 2026-05-13
  // ------------------------------------------------------------
  // Der Editor läuft jetzt auch auf Mobile (Layout stackt: Canvas
  // oben, Sidebar unten — siehe `koebi-label-editor.css` ≤899.98px).
  // Das 3D-Modul (`koebi-label-editor-3d.js`) bleibt auf Mobile
  // weiterhin per Frühreturn deaktiviert; die 3D-Vorschau-Buttons
  // sind dort per CSS ausgeblendet.
  //
  // Der Copy-Link-Button auf dem alten Mobile-Gate-Screen wird
  // nicht mehr gerendert, `setupMobileCopyButton()` darf aber
  // weiterhin existieren (defensiv, falls die Section ohne CSS
  // geladen wird oder ein zukünftiger Fallback gebraucht wird).
  // ============================================================
  const MOBILE_BREAKPOINT_PX = 900;

  // ============================================================
  // Konstanten (aus etiketten-tool.html Z.470–510)
  // ============================================================
  const LABEL_W   = 180;       // mm — Etiketten-Breite
  const LABEL_H   = 80;        // mm — Etiketten-Höhe
  const DESIGN_X  = 25;        // mm — Design-Bereich Offset X
  const DESIGN_W  = 130;       // mm — Design-Breite
  const DESIGN_H  = 85;        // mm — Design-Höhe (mit Bleed)
  const BLEED     = 2.5;       // mm — Anschnitt
  const MM2PT     = 72 / 25.4; // mm → PostScript-Pt
  const PRINT_BLEED_MM = BLEED;
  const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB Hard-Limit (Shopify)
  const PENDING_HERO_UPLOAD = {
    dbName: 'koebi-label-upload',
    storeName: 'uploads',
    key: 'pending-hero-upload',
    flag: 'koebiLabelPendingUpload',
  };

  // 10 Sorten — Quelle: SORTEN-Konstante Z.499
  // Im Theme-Port hat der Key bereits das `_BG`-Suffix, weil er
  // 1:1 als Frame-PDF-Cache-Key verwendet wird.
  const SORTEN = [
    { name: 'Alte B17',  key: 'AlteB17_BG' },
    { name: 'BBA',       key: 'BBA_BG' },
    { name: 'Böck',      key: 'Boeck_BG' },
    { name: 'Dominator', key: 'Dominator_BG' },
    { name: 'Helles',    key: 'Helles_BG' },
    { name: 'IPA',       key: 'IPA_BG' },
    { name: 'Limo',      key: 'Limo_BG' },
    { name: 'Radler',    key: 'Radler_BG' },
    { name: 'Sauphi',    key: 'Sauphi_BG' },
    { name: 'Zwickl',    key: 'Zwickl_BG' },
  ];

  // ----------------------------------------------------------
  // Phase 3 — Font-Variants pro Family
  // Pro Font: Liste an Schnitten {label, weight, italic, ttf}.
  // `ttf` zeigt auf jsDelivr-Mirror. Wenn weggelassen (Bricolage-
  // Theme-Asset): Fallback auf state.assets.bricolageFontUrl bzw.
  // Variable-TTF.
  // ----------------------------------------------------------
  // ----------------------------------------------------------
  // Font-URLs: ausschließlich Static-TTFs aus dem google/fonts-Repo.
  // Keine Variable Fonts — pdf-lib unterstützt sie nicht zuverlässig
  // mit fontkit, plus die Naming-Konvention im Repo variiert.
  // jsDelivr hat 'gh/google/fonts' 2026-05 mit 403 geblockt (zu groß),
  // deshalb rawcdn.githack.com als Mirror mit CORS-Headern.
  // ----------------------------------------------------------
  const _JD = 'https://rawcdn.githack.com/google/fonts/main';
  const FONT_VARIANTS = {
    'Bricolage Grotesque': [
      { label: 'Light',    weight: 300, italic: false, ttf: `${_JD}/ofl/bricolagegrotesque/static/BricolageGrotesque-Light.ttf` },
      { label: 'Regular',  weight: 400, italic: false, ttf: null /* lokales Theme-Asset */ },
      { label: 'Medium',   weight: 500, italic: false, ttf: `${_JD}/ofl/bricolagegrotesque/static/BricolageGrotesque-Medium.ttf` },
      { label: 'SemiBold', weight: 600, italic: false, ttf: `${_JD}/ofl/bricolagegrotesque/static/BricolageGrotesque-SemiBold.ttf` },
      { label: 'Bold',     weight: 700, italic: false, ttf: `${_JD}/ofl/bricolagegrotesque/static/BricolageGrotesque-Bold.ttf` },
    ],
    'DM Sans': [
      { label: 'Regular',       weight: 400, italic: false, ttf: `${_JD}/ofl/dmsans/static/DMSans-Regular.ttf` },
      { label: 'Italic',        weight: 400, italic: true,  ttf: `${_JD}/ofl/dmsans/static/DMSans-Italic.ttf` },
      { label: 'Medium',        weight: 500, italic: false, ttf: `${_JD}/ofl/dmsans/static/DMSans-Medium.ttf` },
      { label: 'Medium Italic', weight: 500, italic: true,  ttf: `${_JD}/ofl/dmsans/static/DMSans-MediumItalic.ttf` },
      { label: 'Bold',          weight: 700, italic: false, ttf: `${_JD}/ofl/dmsans/static/DMSans-Bold.ttf` },
      { label: 'Bold Italic',   weight: 700, italic: true,  ttf: `${_JD}/ofl/dmsans/static/DMSans-BoldItalic.ttf` },
    ],
    'Playfair Display': [
      { label: 'Regular',     weight: 400, italic: false, ttf: `${_JD}/ofl/playfairdisplay/static/PlayfairDisplay-Regular.ttf` },
      { label: 'Italic',      weight: 400, italic: true,  ttf: `${_JD}/ofl/playfairdisplay/static/PlayfairDisplay-Italic.ttf` },
      { label: 'Bold',        weight: 700, italic: false, ttf: `${_JD}/ofl/playfairdisplay/static/PlayfairDisplay-Bold.ttf` },
      { label: 'Bold Italic', weight: 700, italic: true,  ttf: `${_JD}/ofl/playfairdisplay/static/PlayfairDisplay-BoldItalic.ttf` },
      { label: 'Black',       weight: 900, italic: false, ttf: `${_JD}/ofl/playfairdisplay/static/PlayfairDisplay-Black.ttf` },
    ],
    'Roboto Slab': [
      { label: 'Light',   weight: 300, italic: false, ttf: `${_JD}/apache/robotoslab/static/RobotoSlab-Light.ttf` },
      { label: 'Regular', weight: 400, italic: false, ttf: `${_JD}/apache/robotoslab/static/RobotoSlab-Regular.ttf` },
      { label: 'Medium',  weight: 500, italic: false, ttf: `${_JD}/apache/robotoslab/static/RobotoSlab-Medium.ttf` },
      { label: 'Bold',    weight: 700, italic: false, ttf: `${_JD}/apache/robotoslab/static/RobotoSlab-Bold.ttf` },
      { label: 'Black',   weight: 900, italic: false, ttf: `${_JD}/apache/robotoslab/static/RobotoSlab-Black.ttf` },
    ],
    'Anton': [
      { label: 'Regular', weight: 400, italic: false, ttf: `${_JD}/ofl/anton/Anton-Regular.ttf` },
    ],
    'Caveat': [
      { label: 'Regular',  weight: 400, italic: false, ttf: `${_JD}/ofl/caveat/static/Caveat-Regular.ttf` },
      { label: 'Medium',   weight: 500, italic: false, ttf: `${_JD}/ofl/caveat/static/Caveat-Medium.ttf` },
      { label: 'SemiBold', weight: 600, italic: false, ttf: `${_JD}/ofl/caveat/static/Caveat-SemiBold.ttf` },
      { label: 'Bold',     weight: 700, italic: false, ttf: `${_JD}/ofl/caveat/static/Caveat-Bold.ttf` },
    ],
    'Bebas Neue': [
      { label: 'Regular', weight: 400, italic: false, ttf: `${_JD}/ofl/bebasneue/BebasNeue-Regular.ttf` },
    ],
    'Pacifico': [
      { label: 'Regular', weight: 400, italic: false, ttf: `${_JD}/ofl/pacifico/Pacifico-Regular.ttf` },
    ],
  };

  function getFontVariants(family) {
    return FONT_VARIANTS[family] || FONT_VARIANTS['Bricolage Grotesque'];
  }
  // Liefert die in FONT_VARIANTS gespeicherte Variant — fällt sonst auf Regular zurück.
  function findFontVariant(family, weight, italic) {
    const list = getFontVariants(family);
    return list.find(v => v.weight === weight && v.italic === !!italic)
        || list.find(v => v.weight === 400 && !v.italic)
        || list[0];
  }

  // Deckelfarben — Quelle: CAP_SWATCHES Z.489
  const CAP_SWATCHES = [
    { hex: '#1A1A1A', metalness: 0,    roughness: 0.195, title: 'Schwarz' },
    { hex: '#3C8ED5', metalness: 0,    roughness: 0.195, title: 'Blau'    },
    { hex: '#968658', metalness: 0.65, roughness: 0.5,   title: 'Gold'    },
    { hex: '#E80067', metalness: 0,    roughness: 0.195, title: 'Pink'    },
    { hex: '#FFE500', metalness: 0,    roughness: 0.195, title: 'Gelb'    },
    { hex: '#442C26', metalness: 0,    roughness: 0.195, title: 'Braun'   },
    { hex: '#2AC76B', metalness: 0,    roughness: 0.195, title: 'Grün'    },
  ];

  // ============================================================
  // State (aus etiketten-tool.html Z.512–534)
  // ============================================================
  const state = {
    fileBytes: null,
    fileName: '',
    fileType: '',
    pdfExportBytes: null,
    rasterExportBytes: null,
    rasterExportType: '',
    imageDataUrl: null,
    imageW: 0,
    imageH: 0,
    selectedSorte: null,
    variant: 'Schwarz',
    bgColor: '#ffffff',
    bgColorCmyk: null,
    pdfCmykPalette: null,
    designPreviewSource: null,
    designPreviewCacheKey: '',
    designPreviewCacheCanvas: null,
    previewDesignCanvas: null,
    previewDesignBounds: null,
    framePreviewCacheKey: '',
    framePreviewCacheCanvas: null,
    colorDebug: {
      badge: 'Manuell / RGB',
      text:  'Direkt gesetzte Hintergrundfarbe; Export übernimmt denselben sichtbaren Wert.',
      tone:  'rgb',
    },
    showGuides: true,
    assets: null,
    frameUrls: {},                // Slug → Metafeld-PDF-URL (aus data-frame-urls)
    sortenMhdCharge: {},          // FrameKey → {mhd, charge} (aus Sorten-Metafeldern)
    // MHD/Charge-Druck-Modus (Session 2, 2026-05-22):
    //   true  = MHD + Chargennummer werden direkt aufs Etikett gedruckt (Standard für ≤Schwelle-Flaschen).
    //   false = weißes Etikettierer-Feld auf dem Etikett, MHD/Charge werden später mechanisch
    //           vom Etikettierer aufgedruckt (für ≥Schwelle-Flaschen, Rollendruck).
    // Wird durch UI-Checkbox direkt überschrieben oder vom Variant-Auto-Toggle gesetzt.
    directPrintMhd: true,
    directPrintThreshold: 400,    // Aus data-direct-print-threshold (Section-Setting)
    cartVariantId: '',
    mode: 'page',                 // 'page' | 'product' (aus data-mode)
    variantTitleToSorteKey: {},   // Map "Helles" → "Helles_BG"
    cartRequiredInfo: { bySorte: {}, byVariant: {} },
    framePdfDocCache: new Map(),
    framePdfBytesCache: new Map(),
    frameImageCache: new Map(),
    // Custom-Text + Font (Phase 2 – Text-Panel; Phase 3.2: Color + Style)
    customText: '',
    customFont: 'Bricolage Grotesque',
    customTextSize: 32,
    customLineHeight: 1.2,        // Zeilenabstand-Multiplikator (slider-gesteuert, 0.8–2.5)
    customTextColor: '#000000',  // Phase 3.2: vom Color-Picker, Default schwarz
    customTextStyle: 'Regular',  // Phase 3.2: Schnitt-Label aus FONT_VARIANTS (z. B. "Bold Italic")
    customFontCache: {},          // Key: `${family}|${styleLabel}` → TTF-Bytes
    // ----------------------------------------------------------
    // Phase 3 – Drag/Scale für Design + Text
    // designOffsetX/Y in mm, vom Default-Anker (DESIGN_X / vertikal-zentriert)
    // designScale: 1.0 = volle DESIGN_W (130 mm). Range 0.5–2.0.
    // textOffsetX/Y in mm, vom Default-Anker (Etikett-Mitte). Größe bleibt am pt-Slider.
    // _drag: aktiver Drag-Zustand { kind:'design'|'text', startMmX, startMmY, startOffX, startOffY }
    // previewTextBounds: Bounding-Box des Custom-Texts in Canvas-Pixeln (für Hit-Test).
    // ----------------------------------------------------------
    designOffsetX: 0,
    designOffsetY: 0,
    designScale: 1,
    textOffsetX: 0,
    textOffsetY: 0,
    _drag: null,
    previewTextBounds: null,
    // Session C (2026-05-21) — Re-Edit-Modus.
    // Wird gesetzt, sobald der Editor mit ?reedit=<orderId>&line=...
    // &exp=...&token=... aufgerufen wird und das App-Proxy-Backend
    // einen gueltigen Order-State zurueckliefert. Solange `reedit`
    // truthy ist, schreibt `submitToCart()` NICHT in den Cart,
    // sondern ruft `/apps/label/order/save` auf — dadurch ueberschreibt
    // der Save das LineItem-Metafield `custom.reedit_state` an der
    // bestehenden Order, statt eine neue Bestellung zu erzeugen.
    //
    // Shape:
    //   {
    //     orderId, lineItemId, exp, token,  // direkt aus URL
    //     orderName: '#1006', orderGid: 'gid://...',
    //     lineItemGid: 'gid://...',
    //     reeditVersion: 1,
    //     quelldateiGid: 'gid://...',       // Quelle: Server-Antwort
    //     quelldateiFilename: '...',
    //     quelldateiMissing: false,
    //     hasUploadedNewSource: false       // wird true, sobald ein
    //                                       // neuer File via Upload
    //                                       // ueber den re-edit-Flow
    //                                       // ueberschrieben wurde.
    //   }
    reedit: null,
  };

  // ============================================================
  // DOM-Refs (befüllt im init)
  // ============================================================
  const dom = {};

  function openPendingHeroUploadDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB nicht verfuegbar'));
        return;
      }

      const request = window.indexedDB.open(PENDING_HERO_UPLOAD.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(PENDING_HERO_UPLOAD.storeName)) {
          db.createObjectStore(PENDING_HERO_UPLOAD.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('IndexedDB konnte nicht geoeffnet werden'));
    });
  }

  async function readPendingHeroUpload() {
    const db = await openPendingHeroUploadDb();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(PENDING_HERO_UPLOAD.storeName, 'readonly');
        const request = tx.objectStore(PENDING_HERO_UPLOAD.storeName).get(PENDING_HERO_UPLOAD.key);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error || new Error('Upload konnte nicht gelesen werden'));
      });
    } finally {
      db.close();
    }
  }

  async function clearPendingHeroUpload() {
    try {
      window.sessionStorage.removeItem(PENDING_HERO_UPLOAD.flag);
    } catch (error) {
      // sessionStorage can be blocked; IndexedDB is the source of truth.
    }

    const db = await openPendingHeroUploadDb();
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(PENDING_HERO_UPLOAD.storeName, 'readwrite');
        tx.objectStore(PENDING_HERO_UPLOAD.storeName).delete(PENDING_HERO_UPLOAD.key);
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error || new Error('Upload konnte nicht geloescht werden'));
        tx.onabort = () => reject(tx.error || new Error('Upload-Loeschen abgebrochen'));
      });
    } finally {
      db.close();
    }
  }

  async function consumePendingHeroUpload() {
    const hasPendingFlag = (() => {
      try {
        return window.sessionStorage.getItem(PENDING_HERO_UPLOAD.flag) === '1';
      } catch (error) {
        return false;
      }
    })();

    if (!hasPendingFlag && window.location.hash !== '#koebi-label-editor') return;

    try {
      const pending = await readPendingHeroUpload();
      if (!pending || (!pending.blob && !pending.bgColor)) {
        if (hasPendingFlag) await clearPendingHeroUpload();
        return;
      }

      const bgColor = normalizeHex(pending.bgColor);
      const file = pending.blob
        ? (pending.blob instanceof File
          ? pending.blob
          : new File([pending.blob], pending.name || 'etikett-upload', {
              type: pending.type || pending.blob.type || '',
              lastModified: pending.lastModified || Date.now(),
            }))
        : null;

      if (file) await handleFile(file);
      if (bgColor) {
        applyColor(bgColor, {
          debug: {
            badge: pending.source === 'koebi-label-configurator' ? 'Konfigurator / RGB' : 'Uebergabe / RGB',
            text:  'Hintergrundfarbe aus der vorherigen Auswahl uebernommen.',
            tone:  'rgb',
          },
        });
      }
      await clearPendingHeroUpload();

      if (dom.uploadZone && typeof dom.uploadZone.focus === 'function') {
        dom.uploadZone.classList.remove('upload-zone--handoff-highlight');
        void dom.uploadZone.offsetWidth;
        dom.uploadZone.classList.add('upload-zone--handoff-highlight');
        window.setTimeout(() => {
          if (dom.uploadZone) {
            dom.uploadZone.classList.remove('upload-zone--handoff-highlight');
          }
        }, 1700);
        dom.uploadZone.focus({ preventScroll: true });
      }
    } catch (error) {
      dwarn('Hero-Upload konnte nicht uebernommen werden:', error);
    }
  }

  // ============================================================
  // Re-Edit-Modus (Session C, 2026-05-21)
  // ------------------------------------------------------------
  // Wird vom Druckbogen-Plugin im Admin angestossen: Tim klickt auf
  // einer Order → Plugin signiert HMAC-Token, redirected zum Editor
  // mit ?reedit=<orderIdNumeric>&line=<lineItemIdNumeric>&exp=<unix>
  // &token=<hexHmac>#koebi-label-editor.
  //
  // Beim Editor-Init pruefen wir die URL-Params, POSTen sie an die
  // App-Proxy-Route /apps/label/order, kriegen Editor-State +
  // Quelldatei-URL zurueck und hydrieren den Editor 1:1 auf den
  // gespeicherten Zustand.
  //
  // submitToCart() liest spaeter state.reedit aus und entscheidet
  // dort, ob ein normaler Cart-Add oder ein Re-Edit-Save (POST an
  // /apps/label/order/save) gemacht wird.
  // ============================================================
  const REEDIT_LOAD_ENDPOINT = '/apps/label/order';
  const REEDIT_SAVE_ENDPOINT = '/apps/label/order/save';

  function readReeditUrlParams() {
    const p = new URLSearchParams(window.location.search);
    const orderId    = (p.get('reedit')   || '').trim();
    const lineItemId = (p.get('line')     || '').trim();
    const exp        = (p.get('exp')      || '').trim();
    const token      = (p.get('token')    || '').trim();
    const shortCode  = (p.get('short')    || '').trim();
    if (!orderId || !lineItemId || !exp || !token) return null;
    return { orderId, lineItemId, exp, token, shortCode };
  }

  function replaceReeditUrlWithShortLink(shortCode) {
    if (!shortCode || !/^[0-9A-Za-z]{8,12}$/.test(shortCode)) return;
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    const shortPath = `/apps/label/r/${encodeURIComponent(shortCode)}`;
    try {
      window.history.replaceState({ koebiReeditShort: shortCode }, '', shortPath);
    } catch (err) {
      console.warn('[koebi-label] Re-Edit-Shortlink konnte nicht in die Adresszeile gesetzt werden:', err);
    }
  }

  async function consumeReeditFromUrl() {
    const params = readReeditUrlParams();
    if (!params) return;

    showStatus('Bestellung wird zum Bearbeiten geladen…', 'loading');

    // Re-Edit hat Vorrang vor Pending-Hero-Upload — wir hatten oben
    // schon ggf. einen Hero-Snapshot eingespielt, der wird hier
    // ueberschrieben. Das ist gewuenscht: wer mit Re-Edit-Link kommt,
    // bearbeitet einen konkreten Auftrag, nicht den letzten Hero-Look.
    try {
      const body = new FormData();
      body.append('orderId',    params.orderId);
      body.append('lineItemId', params.lineItemId);
      body.append('exp',        params.exp);
      body.append('token',      params.token);

      let r;
      try {
        r = await fetch(REEDIT_LOAD_ENDPOINT, { method: 'POST', body });
      } catch (netErr) {
        console.error('[koebi-label] Re-Edit-Netzwerk-Fehler:', netErr);
        showStatus('Re-Edit-Daten konnten nicht geladen werden (Netzwerk).', 'error');
        return;
      }

      // Server-Antwort lesen — auch im Fehlerfall kommt JSON mit
      // einem `error`-Code, den wir auf eine sprechende Meldung
      // mappen wollen (z.B. „Link abgelaufen, bitte im Admin neu
      // oeffnen").
      let payload = null;
      try {
        payload = await r.json();
      } catch (parseErr) {
        console.warn('[koebi-label] Re-Edit-Antwort kein JSON:', parseErr);
      }

      if (!r.ok || !payload || !payload.ok) {
        const code = (payload && payload.error) || '';
        let msg = 'Re-Edit-Link konnte nicht geöffnet werden.';
        if (code === 'expired') {
          msg = 'Der Re-Edit-Link ist abgelaufen. Bitte den Link im Admin neu erzeugen.';
        } else if (code === 'order_not_found' || code === 'line_item_not_found') {
          msg = 'Die Bestellung oder das Etikett wurde nicht gefunden.';
        } else if (code === 'bad_signature' || code === 'malformed_signature' || code === 'missing_fields' || code === 'unauthorized') {
          msg = 'Der Re-Edit-Link ist ungültig.';
        }
        showStatus(msg, 'error');
        return;
      }

      // Editor-State + Sorte/Frame/BG/Text/Design hydrieren
      try {
        applyEditorStateToEditor(payload.editorState, {
          fallbackSorte:  payload.lineItem?.sorte || '',
          fallbackFrame:  payload.lineItem?.frame || '',
          fallbackBgHex:  payload.lineItem?.bgHex || '',
        });
      } catch (applyErr) {
        console.warn('[koebi-label] Editor-State-Hydrate teilweise fehlgeschlagen:', applyErr);
      }

      // Quelldatei laden, falls vorhanden
      const quelldateiMissing = !payload.quelldatei || !payload.quelldatei.url;
      if (!quelldateiMissing) {
        try {
          await hydrateFileFromCdn(
            payload.quelldatei.url,
            payload.quelldatei.fileName || 'quelldatei',
            payload.quelldatei.mimeType || '',
          );
        } catch (fileErr) {
          console.warn('[koebi-label] Quelldatei-Hydrate fehlgeschlagen:', fileErr);
          showStatus('Originalbild konnte nicht geladen werden — bitte neu hochladen.', 'error');
        }
      }

      // Re-Edit-Context an state haengen — submitToCart liest das
      state.reedit = {
        orderId:            params.orderId,
        lineItemId:         params.lineItemId,
        exp:                params.exp,
        token:              params.token,
        shortCode:          params.shortCode || '',
        orderName:          payload.order?.name || '',
        orderGid:           payload.order?.id || '',
        lineItemGid:        payload.lineItem?.id || '',
        reeditVersion:      Number(payload.reeditVersion || 0),
        quelldateiFilename: payload.quelldatei?.fileName || '',
        quelldateiMissing,
        // Wir hatten beim Hydrate die alte Quelldatei in state.fileBytes
        // geladen — falls sie unveraendert bleibt, signalisieren wir
        // dem Save-Endpoint per `keepQuelldateiGid` die alte GID.
        // Sobald der Kunde ein anderes Bild hochlaedt (handleFile
        // wird wieder aufgerufen, ueberschreibt fileBytes), bleibt
        // hasUploadedNewSource true und der Save laedt das neue Bild
        // statt nur die GID zu „behalten".
        keepQuelldateiGidHint: '', // wird vom Backend nicht zurueckgegeben — Save schickt einfach state.fileBytes
        hasUploadedNewSource: false,
      };

      replaceReeditUrlWithShortLink(state.reedit.shortCode);

      // Re-Edit-Banner setzen. Der Editor zeigt jetzt persistent:
      // "Re-Edit der Bestellung #1006 — Speichern überschreibt das
      // Etikett auf dieser Bestellung."
      const v = state.reedit.reeditVersion;
      const versionSuffix = v > 0 ? ` (v${v + 1})` : '';
      const orderLabel = state.reedit.orderName || '';
      let bannerMsg = `Bestehende Bestellung wird bearbeitet${versionSuffix}`;
      if (orderLabel) bannerMsg += ` — ${orderLabel}`;
      bannerMsg += '. Speichern überschreibt das Etikett, ohne eine neue Bestellung anzulegen.';
      if (quelldateiMissing) {
        bannerMsg += ' Originalbild fehlt — bitte vor dem Speichern neu hochladen.';
      }
      // 'info' = neutral (kein .error/.success-Modifier) + persistent
       // (Auto-Dismiss greift nur bei 'success'). Rot waere optisch ein
       // Fehler-Banner, was den Kunden verunsichert — der Re-Edit-Modus
       // ist explizit kein Fehlerzustand.
      showStatus(bannerMsg, 'info');

      // Primary-Button-Label: „In den Warenkorb legen" passt im Re-Edit-
      // Modus nicht, weil kein neuer Cart-Add stattfindet. Semantisch
      // korrekt: „Änderungen speichern". Der Click-Handler bleibt
      // identisch (generatePDF('print') → submitToCart → submitReeditSave).
      if (dom.btnPrint) {
        // Dediziertes Label-Span (data-koebi-label-print-label) gezielt
        // ansprechen — NICHT den ersten Span (das ist der Spinner).
        // Fallbacks fuer aeltere Markup-Staende beibehalten.
        const inner = dom.btnPrint.querySelector('[data-koebi-label-print-label]')
                   || dom.btnPrint.querySelector('span');
        if (inner) inner.textContent = 'Änderungen speichern';
        else dom.btnPrint.textContent = 'Änderungen speichern';
      }

      // Focus-Mode: nur die Editor-Section ist im Viewport. Header,
      // Footer, Age-Gate, Cart-Drawer blendet theme.liquid bereits
      // server-side via {%- unless koebi_reedit_mode -%} aus. Geschwister-
      // Sections unter <main> versteckt die Inline-<style>-Regel in
      // sections/koebi-label-editor.liquid (ID-basiert, FOUC-frei).
      //
      // body.koebi-reedit-mode wird ebenfalls server-side gesetzt (siehe
      // layout/theme.liquid). Wir setzen die Klasse hier defensiv noch
      // einmal client-side, falls der Editor in einem Custom-Layout
      // laeuft, das den koebi_reedit_mode-Branch von theme.liquid nicht
      // hat (z.B. wenn jemand spaeter die Section in ein eigenes Template
      // einbaut).
      try {
        document.body.classList.add('koebi-reedit-mode');
      } catch (e) { /* defensive */ }

      // Buttons sichtbar machen — Re-Edit hat sonst dieselbe UI wie
      // ein frischer Editor. updateButton kennt den Re-Edit-Modus
      // nicht; wir trigger es manuell.
      try { updateButton(); } catch (e) { /* defensive */ }
    } catch (err) {
      console.error('[koebi-label] Re-Edit-Hydrate fehlgeschlagen:', err);
      showStatus('Re-Edit konnte nicht geladen werden.', 'error');
    }
  }

  /**
   * Hydriert den Editor-Zustand aus einem _Editor-State-JSON-Snapshot.
   * Setzt state-Felder UND die zugehoerigen DOM-Inputs/Selects, damit
   * die UI das selbe Bild zeigt wie der gespeicherte Zustand.
   */
  function applyEditorStateToEditor(snapshot, fallbacks) {
    // Snapshot ist optional — wenn er fehlt (z.B. Alt-Bestellungen
    // vor Phase 4c), faellt der Editor auf die `fallbacks` aus den
    // Cart-Properties (_Sorte, _Rahmen, _Hintergrundfarbe) zurueck.
    const fb = fallbacks || {};
    snapshot = (snapshot && typeof snapshot === 'object') ? snapshot : {};

    // Sorte ----------------------------------------------------
    const sorteKey = (snapshot.sorte || '').toString();
    const sorteFallback = sortenKeyFromLabel(fb.fallbackSorte || '');
    const finalSorte = sorteKey || sorteFallback;
    if (finalSorte) {
      state.selectedSorte = finalSorte;
      if (dom.sorteSelect) {
        dom.sorteSelect.value = finalSorte;
      }
    }

    // Frame (Schwarz/Weiss) -----------------------------------
    const frame = (snapshot.frame || fb.fallbackFrame || '').toString();
    if (frame === 'Schwarz' || frame === 'Weiss') {
      state.variant = frame;
    }

    // Hintergrundfarbe ----------------------------------------
    const bgHex = ((snapshot.bg && snapshot.bg.hex) || fb.fallbackBgHex || '').toString();
    if (/^#[0-9A-Fa-f]{6}$/.test(bgHex)) {
      // applyColor setzt CMYK-Felder, normalisiert, triggert ggf.
      // Auto-Frame — wir override den Frame danach nochmal, falls
      // der Snapshot eine explizit andere Variante festgehalten hat
      // (Weiss-Frame trotz hellem BG kann der Kunde manuell waehlen
      // — auch wenn das Theme das aktuell nicht UI-exposed; defensiv).
      applyColor(bgHex);
      const cmyk = (snapshot.bg && Array.isArray(snapshot.bg.cmyk)) ? snapshot.bg.cmyk : null;
      if (cmyk && cmyk.length === 4) {
        state.bgColorCmyk = cmyk.map((v) => Number(v) || 0);
        setCmykFields(state.bgColorCmyk);
      }
      if (frame === 'Schwarz' || frame === 'Weiss') {
        state.variant = frame;
      }
    }

    // Design-Transform ----------------------------------------
    const d = snapshot.design || {};
    state.designOffsetX = Number(d.offsetX) || 0;
    state.designOffsetY = Number(d.offsetY) || 0;
    state.designScale   = Number(d.scale)   || 1;
    if (dom.designScaleRange) {
      dom.designScaleRange.value = String(Math.round(state.designScale * 100));
      updateSliderProgress(dom.designScaleRange);
    }
    if (dom.designScaleValue) {
      dom.designScaleValue.textContent = `${Math.round(state.designScale * 100)} %`;
    }

    // Custom-Text ---------------------------------------------
    const t = snapshot.text || {};
    state.customText        = (t.content || '').toString();
    state.customFont        = (t.font    || state.customFont).toString();
    state.customTextStyle   = (t.style   || state.customTextStyle).toString();
    state.customTextSize    = Number(t.size)       || state.customTextSize;
    state.customLineHeight  = Number(t.lineHeight) || state.customLineHeight;
    state.customTextColor   = (t.color || state.customTextColor).toString();
    state.textOffsetX       = Number(t.offsetX) || 0;
    state.textOffsetY       = Number(t.offsetY) || 0;

    if (dom.customText)       dom.customText.value       = state.customText;
    if (dom.customFont)       dom.customFont.value       = state.customFont;
    if (typeof populateStyleSelect === 'function') {
      try { populateStyleSelect(state.customFont); } catch (e) { /* defensive */ }
    }
    if (dom.customTextStyle)  dom.customTextStyle.value  = state.customTextStyle;
    if (dom.customTextSize)   {
      dom.customTextSize.value = String(state.customTextSize);
      updateSliderProgress(dom.customTextSize);
    }
    if (dom.customTextSizeValue) {
      dom.customTextSizeValue.textContent = `${Math.round(state.customTextSize)} pt`;
    }
    if (dom.customLineHeight) {
      dom.customLineHeight.value = String(state.customLineHeight);
      updateSliderProgress(dom.customLineHeight);
    }
    if (dom.customLineHeightValue) {
      dom.customLineHeightValue.textContent = state.customLineHeight.toFixed(2);
    }
    if (dom.customTextColor)    dom.customTextColor.value    = state.customTextColor;
    if (dom.customTextColorHex) dom.customTextColorHex.value = state.customTextColor.replace('#', '');

    // Direkt-aufdrucken-Modus aus Snapshot — falls nicht gesetzt (alte Bestellungen
    // vor Session 2): Default true beibehalten, also nicht überschreiben.
    if (typeof snapshot.directPrintMhd === 'boolean') {
      state.directPrintMhd = snapshot.directPrintMhd;
    }

    // Frame-Cache + Preview neu anstossen
    resetFramePreviewCache();
    updatePreview();
  }

  /**
   * Sucht den Sorten-KEY (intern z.B. „Helles_BG") aus einem Label
   * (z.B. „Helles"). Verwendet die Variant-Title-Map aus dem Section-
   * Data-Attribut, weil der Editor-Snapshot den KEY speichert und die
   * Order-Property `_Sorte` das LABEL. Fallback: leerer String.
   */
  function sortenKeyFromLabel(label) {
    if (!label) return '';
    // Direkt vorhandene Map nutzen
    const direct = state.variantTitleToSorteKey || {};
    if (direct[label]) return direct[label];
    // Fallback: 1:1 (manche Sorten haben gleichen Key wie Label)
    return label;
  }

  /**
   * Laedt eine Datei von einer URL und uebergibt sie an handleFile.
   * Wird sowohl im Re-Edit-Init genutzt (Quelldatei aus Shopify Files
   * holen) als auch beim Save-Path (neue Druckdatei rendern).
   */
  async function hydrateFileFromCdn(url, fileName, mimeType) {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const finalType = mimeType || blob.type || '';
    const finalName = fileName || 'quelldatei';
    const fileObj = new File([blob], finalName, { type: finalType });
    await handleFile(fileObj, {
      applyPdfPaletteColor: false,
      resetTransform: false,
    });
  }

  // Preview-Render-State (aus Quelle Z.958–962)
  let previewDebounce       = null;
  let previewRequestVersion = 0;
  let previewRendering      = false;
  let previewNeedsRerender  = false;
  let previewRafScheduled   = false; // Phase 3: rAF-Throttle für Drag

  // Asset-Caches für PDF-Export
  let _outputProfileBytes = undefined;
  let _bricolageFont      = null;

  // Eyedropper
  let _pickerCleanup = null;

  // ============================================================
  // Init
  // ============================================================
  function init() {
    const root = document.querySelector('[data-koebi-label-editor]');
    if (!root) return;

    // Asset-URLs aus data-Attributes der Section
    state.assets = {
      modelUrl:         root.dataset.modelUrl || '',
      iccProfileUrl:    root.dataset.iccProfileUrl || '',
      pdfWorkerUrl:     root.dataset.pdfWorkerUrl || '',
      bricolageFontUrl: root.dataset.bricolageFontUrl || '',
      // Frame-Asset-Base aus ICC-URL ableiten (gleicher CDN-Pfad).
      frameAssetBase:   root.dataset.iccProfileUrl
        ? root.dataset.iccProfileUrl.replace(/[^\/]+$/, '')
        : '',
    };

    // Font-URL-Map aus data-fonts-map (Shopify-CDN-URLs für jeden Schnitt).
    // Wird in loadGoogleFontBytes als Primary-URL genutzt vor dem CDN-Fallback.
    try {
      state.fontsMap = root.dataset.fontsMap ? JSON.parse(root.dataset.fontsMap) : {};
    } catch (e) {
      console.warn('[koebi-label] data-fonts-map JSON-Parse fehlgeschlagen:', e);
      state.fontsMap = {};
    }

    // Frame-PDF-URLs aus data-frame-urls (Metafeld `custom.frame_pdf`
    // pro Sorten-Produkt). Schlüssel = Slug (lowercase, ohne _BG).
    // Leere Werte → frameAssetUrl fällt auf Theme-Asset-Convention zurück.
    try {
      state.frameUrls = root.dataset.frameUrls ? JSON.parse(root.dataset.frameUrls) : {};
    } catch (e) {
      console.warn('[koebi-label] data-frame-urls JSON-Parse fehlgeschlagen:', e);
      state.frameUrls = {};
    }

    // MHD/Charge-Defaults pro Sorte (aus Metafeldern custom.mhd_current /
    // custom.charge_current am Sorten-Produkt). Werden im Storefront unterhalb
    // der Schwelle direkt aufs Etikett gedruckt (Demo-Werte). Schlüssel =
    // Frame-Key wie state.selectedSorte ("Helles_BG", "BBA_BG", …).
    try {
      state.sortenMhdCharge = root.dataset.sortenMhdCharge
        ? JSON.parse(root.dataset.sortenMhdCharge)
        : {};
    } catch (e) {
      console.warn('[koebi-label] data-sorten-mhd-charge JSON-Parse fehlgeschlagen:', e);
      state.sortenMhdCharge = {};
    }

    state.cartVariantId = root.dataset.cartVariantId || root.dataset.variantId || '';
    state.mode          = root.dataset.mode === 'product' ? 'product' : 'page';

    // Direct-Print-Threshold aus Section-Setting (Default 400).
    // Bei Variant-Wechsel: bottle_count >= Threshold → Etikettierer-Modus (weißes Feld),
    // sonst direkt aufdrucken.
    const thresholdRaw = parseInt(root.dataset.directPrintThreshold || '400', 10);
    state.directPrintThreshold = Number.isFinite(thresholdRaw) && thresholdRaw > 0 ? thresholdRaw : 400;

    try {
      const cartDataEl = root.querySelector('[data-koebi-label-cart-data]');
      state.cartRequiredInfo = cartDataEl
        ? JSON.parse(cartDataEl.textContent)
        : { bySorte: {}, byVariant: {} };
    } catch (e) {
      console.warn('[koebi-label] cart-data JSON-Parse fehlgeschlagen:', e);
      state.cartRequiredInfo = { bySorte: {}, byVariant: {} };
    }

    // Variant-Title → Sorten-Key Mapping (für Produkt-Mode)
    try {
      state.variantTitleToSorteKey = root.dataset.variantTitleMap
        ? JSON.parse(root.dataset.variantTitleMap)
        : {};
    } catch (e) {
      console.warn('[koebi-label] data-variant-title-map JSON-Parse fehlgeschlagen:', e);
      state.variantTitleToSorteKey = {};
    }

    cacheDomRefs(root);

    // Initiale Sorte aus aktueller Produkt-Variante (Produkt-Mode)
    const initialVariantTitle = (root.dataset.currentVariantTitle || '').trim();
    if (state.mode === 'product' && initialVariantTitle && state.variantTitleToSorteKey[initialVariantTitle]) {
      state.selectedSorte = state.variantTitleToSorteKey[initialVariantTitle];
    }

    populateSortenSelect();
    populateStyleSelect(state.customFont); // Phase 3.2: Schnitt-Dropdown initial befüllen
    renderCapSwatches();
    bindUiEvents();
    setupCanvasDrop();
    setupUpload();
    setupAccordion();
    setupCanvasInteraction(); // Phase 3: Drag/Scale auf Preview-Canvas
    setupStickyHeaderTracking(root); // Sticky-Top dynamisch zur Header-Sichtbarkeit
    setupVariantChangeListener();
    prefillA4Fields();
    renderColorDebugHint();

    // Produkt-Mode: Standard-Shopify-Buy-Button auf der Produktseite
    // patchen (CTA-Text + Smooth-Scroll zum Editor).
    if (state.mode === 'product') {
      patchProductFormButton();
    }

    // Editor-Shell sichtbar machen
    if (dom.app) dom.app.hidden = false;

    // pdf.js Worker konfigurieren (wenn pdfjsLib geladen ist)
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        state.assets && state.assets.pdfWorkerUrl
          ? state.assets.pdfWorkerUrl
          : 'pdf.worker.min.js';
    }

    // Erst-Render (Frame ohne Design)
    updatePreview();
    updateButton();
    consumePendingHeroUpload();

    // Session C — Re-Edit-Hydrate. URL-Param-Check ist intern; wenn
    // keine reedit-Params da sind, ist das ein No-Op und der Editor
    // verhaelt sich exakt wie vor Session C.
    consumeReeditFromUrl();
  }

  function cacheDomRefs(root) {
    dom.root             = root;
    dom.app              = root.querySelector('[data-koebi-label-app]');
    dom.uploadZone       = root.querySelector('[data-koebi-label-dropzone]');
    dom.fileInput        = root.querySelector('#pdfInput');
    dom.fileinfo         = root.querySelector('[data-koebi-label-fileinfo]');
    dom.filename         = root.querySelector('[data-koebi-label-filename]');
    dom.btnRemoveFile    = root.querySelector('#btnRemoveFile');
    dom.sorteSelect      = root.querySelector('[data-koebi-label-sorte]');
    // Variant-Toggle und Text-Color-Toggle sind seit dem Produkt-Mode-
    // Refactor entfernt — die Frame-Wahl bestimmt die Auto-Kontrast-Logik.
    dom.colorPicker      = root.querySelector('[data-koebi-label-color-picker]');
    dom.colorSwatch      = root.querySelector('[data-koebi-label-bg-swatch]');
    dom.hexInput         = root.querySelector('[data-koebi-label-hex]');
    dom.cmykRow          = root.querySelector('[data-koebi-label-cmyk-row]');
    dom.cmykInputs       = dom.cmykRow ? dom.cmykRow.querySelectorAll('input[data-cmyk]') : [];
    dom.colorDebugHint   = root.querySelector('[data-koebi-label-color-debug]');
    dom.btnEyedropper    = root.querySelector('#btnEyedropper');
    dom.previewCanvas    = root.querySelector('[data-koebi-label-canvas]');
    dom.previewWrap      = root.querySelector('#previewWrap');
    dom.previewDims      = root.querySelector('[data-koebi-label-dims]');
    dom.emptyState       = root.querySelector('[data-koebi-label-empty]');
    dom.statusBar        = root.querySelector('[data-koebi-label-status]');
    dom.statusText       = root.querySelector('[data-koebi-label-status-text]');
    dom.guidesToggle     = root.querySelector('[data-koebi-label-guides]');
    dom.btnCheck         = root.querySelector('[data-koebi-label-action="check"]');
    dom.btnPrint         = root.querySelector('[data-koebi-label-action="print"]');
    dom.btnReset         = root.querySelector('[data-koebi-label-reset]');
    dom.btnDirectPrint   = root.querySelector('#btnDirectPrint');
    // customerName/mhd/charge sind im UI entfernt; defensive null-refs
    // bleiben unten unaufgerufen (alte Code-Pfade prüfen `if (dom.x)`).
    dom.bottleCanvas     = root.querySelector('[data-koebi-label-3d-canvas]');
    dom.bottleLoading    = root.querySelector('[data-koebi-label-3d-loading]');
    dom.bottleCapWrap    = root.querySelector('[data-koebi-label-cap-swatches]');
    dom.bottleAccordionHeader = root.querySelector('#bottleAccordionHeader');
    dom.bottleAccordionBody   = root.querySelector('#bottleAccordionBody');
    dom.btnBottleApply   = root.querySelector('#btnBottleApply');
    dom.btnBottleVideo   = root.querySelector('#btnBottleVideo');
    dom.btnBottleImage   = root.querySelector('#btnBottleImage');
    dom.confirmOverlay   = root.querySelector('#confirmOverlay');
    dom.confirmText      = root.querySelector('#confirmText');
    dom.btnConfirmOk     = root.querySelector('#btnConfirmOk');
    dom.btnConfirmCancel = root.querySelector('#btnConfirmCancel');
    dom.btnSave          = root.querySelector('#btnSave');
    dom.btnAdmin         = root.querySelector('#btnAdmin');
    dom.cartForm         = root.querySelector('[data-koebi-label-cart-form]');
    dom.cartFileInputPrint  = root.querySelector('[data-koebi-label-cart-file-print]');
    dom.cartFilenamePrint   = root.querySelector('[data-koebi-label-cart-filename-print]');
    // Quelldatei (Kunden-Original) — wird VOR dem Cart-Submit ueber den
    // App-Proxy (POST /apps/label/upload) ins Plugin geladen, das via
    // stagedUploadsCreate + fileCreate in Shopify Files speichert und die
    // GenericFile-GID zurueckgibt. Hier landen nur GID (~80 B) und Filename
    // als Hidden-Text-Properties — kein File-Upload mehr durchs Cart-Form.
    // Additiv: wenn die Inputs nicht im DOM existieren (alte Liquid-Version)
    // ODER der Proxy-Upload fehlschlaegt, bleibt der Cart-Submit funktions-
    // faehig — Druckdatei reicht fuer die Produktion. Re-Edit im Admin geht
    // dann fuer diese Order nicht; Plugin laesst Re-Edit-Button verschwinden.
    dom.cartSourceGid       = root.querySelector('[data-koebi-label-cart-source-gid]');
    dom.cartFilenameSource  = root.querySelector('[data-koebi-label-cart-filename-source]');
    dom.cartRequiredInfo   = root.querySelector('[data-koebi-label-cart-required-info]');
    dom.cartBottleCount    = root.querySelector('[data-koebi-label-cart-bottle-count]');
    dom.cartSorte          = root.querySelector('[data-koebi-label-cart-sorte]');
    dom.cartVariant        = root.querySelector('[data-koebi-label-cart-variant]');
    dom.cartBg             = root.querySelector('[data-koebi-label-cart-bg]');
    dom.cartEditorState    = root.querySelector('[data-koebi-label-cart-editor-state]');
    // cartCustomer/cartMhd/cartCharge sind im Cart-Form entfernt;
    // ggf. vorhandene Inputs werden in submitToCart defensiv geleert.
    dom.cartCustomer     = root.querySelector('[data-koebi-label-cart-customer]');
    dom.cartMhd          = root.querySelector('[data-koebi-label-cart-mhd]');
    dom.cartCharge       = root.querySelector('[data-koebi-label-cart-charge]');
    // Phase 2 – Custom-Text-Panel
    dom.customText       = root.querySelector('[data-koebi-label-text]');
    dom.customFont       = root.querySelector('[data-koebi-label-font]');
    dom.customTextSize   = root.querySelector('[data-koebi-label-text-size]');
    dom.customTextSizeValue = root.querySelector('[data-koebi-label-text-size-value]');
    dom.customLineHeight = root.querySelector('[data-koebi-label-line-height]');
    dom.customLineHeightValue = root.querySelector('[data-koebi-label-line-height-value]');
    // Phase 3.2 – Schriftschnitt + Farbe
    dom.customTextStyle    = root.querySelector('[data-koebi-label-weight]');
    dom.customTextColor    = root.querySelector('[data-koebi-label-text-color-picker]');
    dom.customTextColorHex = root.querySelector('[data-koebi-label-text-color-hex]');
    dom.bottlePreviewBtn = root.querySelector('[data-koebi-label-bottle-preview]');
    dom.bottleBackBtn    = root.querySelector('[data-koebi-label-bottle-back]');
    dom.bottleCloseBtn   = root.querySelector('[data-koebi-label-bottle-close]');
    dom.bottleStage      = root.querySelector('[data-koebi-label-3d-stage]');
    // Phase 3 – Drag/Scale-Controls
    dom.designTransformBox = root.querySelector('[data-koebi-label-design-transform]');
    dom.designScaleRange   = root.querySelector('[data-koebi-label-design-scale]');
    dom.designScaleValue   = root.querySelector('[data-koebi-label-design-scale-value]');
    dom.btnDesignReset     = root.querySelector('[data-koebi-label-design-reset]');
    dom.btnTextReset       = root.querySelector('[data-koebi-label-text-reset]');
  }

  // ============================================================
  // Slider-Progress-Helper
  // ------------------------------------------------------------
  // Setzt CSS-Variable `--editor-progress` auf den Range-Input,
  // damit das CSS via linear-gradient den linken Track-Anteil
  // füllt (Filled-Track-Look). Wird beim init und bei jedem
  // input-Event aufgerufen.
  // ============================================================
  function updateSliderProgress(rangeEl) {
    if (!rangeEl) return;
    const min = parseFloat(rangeEl.min) || 0;
    const max = parseFloat(rangeEl.max) || 100;
    const val = parseFloat(rangeEl.value) || 0;
    const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
    rangeEl.style.setProperty('--editor-progress', pct + '%');
  }

  // ============================================================
  // Phase 3.2 — Schriftschnitt-Dropdown dynamisch befüllen.
  // Bei Font-Wechsel: Optionen aus FONT_VARIANTS aufbauen, ersten
  // Schnitt als Default wählen, state.customTextStyle aktualisieren.
  // Style-ID = label (z. B. "Bold Italic") — passt zum bestehenden
  // FONT_VARIANTS-Schema {label, weight, italic, ttf}.
  // ============================================================
  function getVariantByLabel(family, label) {
    const list = getFontVariants(family);
    return list.find(v => v.label === label) || list.find(v => v.label === 'Regular') || list[0];
  }
  function populateStyleSelect(family) {
    if (!dom.customTextStyle) return;
    const variants = getFontVariants(family);
    const selectEl = dom.customTextStyle;
    selectEl.innerHTML = '';
    variants.forEach(v => {
      const opt = document.createElement('option');
      opt.value = v.label;
      opt.textContent = v.label;
      selectEl.appendChild(opt);
    });
    // Wenn aktueller Style in der neuen Liste existiert: behalten,
    // sonst auf 'Regular' (oder ersten Eintrag) zurückfallen.
    const stillValid = variants.some(v => v.label === state.customTextStyle);
    if (!stillValid) {
      state.customTextStyle = (variants.find(v => v.label === 'Regular') || variants[0]).label;
    }
    selectEl.value = state.customTextStyle;
    // Single-Variante: Dropdown disabled (UX-Hint, dass nichts zu wählen ist)
    selectEl.disabled = variants.length <= 1;
  }

  // ============================================================
  // Font-Preload-Helper (Session B Polish, 2026-05-21)
  // ------------------------------------------------------------
  // Stellt sicher, dass eine Family+Style-Combo im Browser-FontFace-
  // Cache liegt, bevor renderPreview() aufgerufen wird. Sonst rendert
  // der erste Canvas-Frame nach Font-/Schnitt-Wechsel mit Bricolage-
  // Fallback (font-display: swap), und erst beim naechsten Trigger
  // (Size, LineHeight) ist der Font geladen — Bug-Symptom: „beim
  // Schriftart-Aendern springt erst Bricolage, dann beim Groesse-
  // Aendern auf die richtige".
  //
  // Timeout 1200 ms: bei normalem Netz reichen ~100 ms, mobil oder
  // schwacher Verbindung bis ~1 s. Daraufgesattelt der renderPreview-
  // interne 500 ms-Race als Safety-Net.
  async function ensureFontFaceReady(fontFamily, styleLabel) {
    if (!document.fonts || typeof document.fonts.load !== 'function') return;
    const family = fontFamily || 'Bricolage Grotesque';
    const variant = getVariantByLabel(family, styleLabel);
    const weight  = variant ? variant.weight : 400;
    const italic  = variant ? variant.italic : false;
    // Subset-Hint mit Buchstaben aus dem aktuellen customText, sonst
    // generisches Alphabet — sorgt dafuer, dass Browser das volle Set
    // laedt, nicht nur leere Glyph-Range.
    const hint = (state.customText || 'AaBbCcDdEe').slice(0, 50);
    const probe = `${italic ? 'italic ' : ''}${weight} 16px "${family}"`;
    try {
      await Promise.race([
        document.fonts.load(probe, hint),
        new Promise((res) => setTimeout(res, 1200)),
      ]);
    } catch (e) { /* still proceed mit Fallback */ }
  }

  // Hex normalisieren (#RGB → #RRGGBB, lowercase, strict)
  function normalizeHex(hex) {
    if (!hex) return null;
    let h = String(hex).trim().toLowerCase();
    if (!h.startsWith('#')) h = '#' + h;
    if (/^#[0-9a-f]{3}$/.test(h)) {
      h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    }
    return /^#[0-9a-f]{6}$/.test(h) ? h : null;
  }

  function hexToRgb01(hex) {
    const h = normalizeHex(hex) || '#000000';
    return {
      r: parseInt(h.substring(1, 3), 16) / 255,
      g: parseInt(h.substring(3, 5), 16) / 255,
      b: parseInt(h.substring(5, 7), 16) / 255,
    };
  }

  // Phase 3 – Sichtbarkeit der Transform-Controls steuern.
  // Design-Box: nur wenn ein File hochgeladen ist.
  // Text-Reset: nur wenn Custom-Text vorhanden ist.
  function updateTransformUiVisibility() {
    const hasFile = !!state.fileBytes;
    const hasText = !!(state.customText && state.customText.trim());
    if (dom.designTransformBox) dom.designTransformBox.hidden = !hasFile;
    if (dom.btnTextReset)       dom.btnTextReset.hidden       = !hasText;
  }

  function populateSortenSelect() {
    // Wenn keine state.selectedSorte gesetzt ist, Helles als Fallback wählen.
    const fallbackKey = SORTEN.find(s => s.key === 'Helles_BG')?.key || SORTEN[0]?.key;
    if (!state.selectedSorte) state.selectedSorte = fallbackKey;

    if (!dom.sorteSelect) return;
    dom.sorteSelect.innerHTML = '';
    SORTEN.forEach((s) => {
      const opt = document.createElement('option');
      opt.value = s.key;
      opt.textContent = s.name;
      dom.sorteSelect.appendChild(opt);
    });
    if (state.selectedSorte) dom.sorteSelect.value = state.selectedSorte;
  }

  // MHD- und Charge-Defaults kommen aus den Sorten-Produkt-Metafeldern
  // custom.mhd_current und custom.charge_current — von Tim nach jedem Sud
  // manuell aktualisiert. Im Storefront werden die Werte unterhalb der
  // Schwelle direkt auf das Etikett gedruckt (Demo-Vorschau); ab Schwelle
  // bleibt das Etikett leer und der Etikettierer beschriftet später
  // mechanisch. Das Plugin liest dieselben Metafelder als Default-Werte
  // für die manuellen Inputs in der Order-Action-Extension.
  // prefillA4Fields ist ein No-Op aus Backwards-Kompatibilität — die alte
  // Auto-Datum-Formel ist raus, getAutoMhd/getAutoCharge lesen jetzt aus
  // state.sortenMhdCharge (gefüllt im init() aus data-sorten-mhd-charge).
  function prefillA4Fields() { /* no-op — Werte stammen aus state.sortenMhdCharge */ }
  function getAutoMhd() {
    const entry = state.sortenMhdCharge && state.sortenMhdCharge[state.selectedSorte];
    return (entry && entry.mhd) || '';
  }
  function getAutoCharge() {
    const entry = state.sortenMhdCharge && state.sortenMhdCharge[state.selectedSorte];
    return (entry && entry.charge) || '';
  }

  // ============================================================
  // UI-Event-Wiring
  // ============================================================
  function bindUiEvents() {
    // Sorten-Select
    if (dom.sorteSelect) {
      dom.sorteSelect.addEventListener('change', e => onSorteChange(e.target.value));
    }

    // Variant-Toggle (Schwarz / Weiß) ist entfernt — die Frame-Wahl
    // bestimmt jetzt chooseFrameVariantForBgColor() automatisch.

    // Color-Picker
    if (dom.colorPicker) {
      dom.colorPicker.addEventListener('input', e => onColorPicker(e.target.value));
    }

    // HEX-Input
    if (dom.hexInput) {
      dom.hexInput.addEventListener('input', e => onHexInput(e.target.value));
    }

    // CMYK-Inputs
    if (dom.cmykInputs && dom.cmykInputs.length) {
      dom.cmykInputs.forEach(inp => inp.addEventListener('input', () => onCmykInput()));
    }

    // Eyedropper
    if (dom.btnEyedropper) {
      dom.btnEyedropper.addEventListener('click', () => openEyedropper());
    }

    // Hilfslinien-Toggle
    if (dom.guidesToggle) {
      dom.guidesToggle.addEventListener('change', () => {
        state.showGuides = dom.guidesToggle.checked;
        updatePreview();
      });
    }

    // Export-Buttons
    if (dom.btnCheck) dom.btnCheck.addEventListener('click', () => {
      // Pre-open Tab synchron im Click-Handler, sonst greift der Popup-Blocker
      // weil window.open nach mehreren awaits als nicht-user-initiated gilt.
      const previewWin = window.open('about:blank', '_blank');
      generatePDF('check', previewWin);
    });
    if (dom.btnPrint) dom.btnPrint.addEventListener('click', () => generatePDF('print'));
    if (dom.btnReset) dom.btnReset.addEventListener('click', () => resetAll());
    if (dom.btnDirectPrint) dom.btnDirectPrint.addEventListener('click', () => printSheet());
    if (dom.btnRemoveFile) dom.btnRemoveFile.addEventListener('click', () => resetAll());

    // Customer-Name / MHD / Charge UI entfernt — keine Listener mehr.

    // Bottle-Accordion
    if (dom.bottleAccordionHeader && dom.bottleAccordionBody) {
      dom.bottleAccordionHeader.addEventListener('click', () => {
        const open = dom.bottleAccordionHeader.getAttribute('aria-expanded') === 'true';
        const next = !open;
        dom.bottleAccordionHeader.setAttribute('aria-expanded', String(next));
        dom.bottleAccordionHeader.classList.toggle('open', next);
        dom.bottleAccordionBody.classList.toggle('open', next);
        if (next) {
          dom.bottleAccordionBody.removeAttribute('hidden');
          if (typeof window.bottleRefreshView === 'function') {
            requestAnimationFrame(() => window.bottleRefreshView());
          }
        } else {
          dom.bottleAccordionBody.setAttribute('hidden', '');
        }
      });
    }

    // Bottle-Action-Buttons
    if (dom.btnBottleApply) {
      dom.btnBottleApply.addEventListener('click', () => {
        if (typeof window.bottleApplyLabel === 'function') window.bottleApplyLabel();
      });
    }
    if (dom.btnBottleImage) {
      dom.btnBottleImage.addEventListener('click', () => {
        if (typeof window.bottleSaveImage === 'function') window.bottleSaveImage();
      });
    }
    if (dom.btnBottleVideo) {
      dom.btnBottleVideo.addEventListener('click', () => {
        if (typeof window.bottleSaveVideo === 'function') window.bottleSaveVideo();
      });
    }

    // Window-Resize → Preview neu rendern
    window.addEventListener('resize', () => updatePreview());

    // Confirm-Overlay-Buttons (defensive Wiring; Admin-Modus ist im Theme nie aktiv)
    if (dom.btnConfirmCancel) {
      dom.btnConfirmCancel.addEventListener('click', () => closeConfirm());
    }
    if (dom.confirmOverlay) {
      dom.confirmOverlay.addEventListener('click', e => {
        if (e.target === dom.confirmOverlay) closeConfirm();
      });
    }

    // Admin-Buttons (im Theme nie aktiv – Detection wird NICHT portiert,
    // Buttons bleiben im DOM, sind aber per CSS .admin-only versteckt)
    if (dom.btnSave)  dom.btnSave.addEventListener('click', () => { /* no-op */ });
    if (dom.btnAdmin) dom.btnAdmin.addEventListener('click', () => { /* no-op */ });

    // Phase 2 – Custom-Text-Panel
    if (dom.customText) {
      dom.customText.addEventListener('input', () => {
        state.customText = dom.customText.value;
        updateTransformUiVisibility(); // Phase 3: Reset-Button ein-/ausblenden
        schedulePreviewRender();
      });
    }
    if (dom.customFont) {
      dom.customFont.addEventListener('change', async () => {
        state.customFont = dom.customFont.value;
        // Phase 3.2: Schnitt-Dropdown an neue Font anpassen
        populateStyleSelect(state.customFont);
        // Font *vor* dem Render in den Browser-FontFace-Cache holen.
        // Sonst rendert renderPreview() in die Bricolage-Fallback-Race
        // (font-display: swap), und erst beim naechsten User-Input (Size,
        // LineHeight) ist der Font im Cache. Bis 1.2 s warten — danach
        // rendert renderPreview() mit Fallback und triggert sich selbst
        // nochmal, sobald der Font fertig geladen ist (siehe Schedule-Loop).
        await ensureFontFaceReady(state.customFont, state.customTextStyle);
        schedulePreviewRender();
      });
    }
    if (dom.customTextSize) {
      updateSliderProgress(dom.customTextSize);
      dom.customTextSize.addEventListener('input', () => {
        state.customTextSize = parseInt(dom.customTextSize.value, 10) || 32;
        if (dom.customTextSizeValue) dom.customTextSizeValue.textContent = state.customTextSize;
        updateSliderProgress(dom.customTextSize);
        schedulePreviewRender();
      });
    }
    if (dom.customLineHeight) {
      updateSliderProgress(dom.customLineHeight);
      dom.customLineHeight.addEventListener('input', () => {
        const v = parseFloat(dom.customLineHeight.value);
        state.customLineHeight = Number.isFinite(v) ? v : 1.2;
        if (dom.customLineHeightValue) {
          dom.customLineHeightValue.textContent = state.customLineHeight.toFixed(2);
        }
        updateSliderProgress(dom.customLineHeight);
        schedulePreviewRender();
      });
    }
    // Phase 3.2 – Schriftschnitt
    if (dom.customTextStyle) {
      dom.customTextStyle.addEventListener('change', async () => {
        state.customTextStyle = dom.customTextStyle.value;
        // Schnitt-Wechsel = haeufig auch separate Font-Datei (Caveat-Bold ist
        // nicht dieselbe wie Caveat-Regular). Preload analog Font-Change,
        // sonst rendert renderPreview() mit Regular-Fallback bevor Bold da ist.
        await ensureFontFaceReady(state.customFont, state.customTextStyle);
        schedulePreviewRender();
      });
    }
    // Phase 3.2 – Schriftfarbe (Picker + Hex-Input bidirektional)
    if (dom.customTextColor) {
      dom.customTextColor.addEventListener('input', () => {
        const hex = normalizeHex(dom.customTextColor.value);
        if (hex) {
          state.customTextColor = hex;
          if (dom.customTextColorHex) dom.customTextColorHex.value = hex;
          schedulePreviewRender();
        }
      });
    }
    if (dom.customTextColorHex) {
      dom.customTextColorHex.addEventListener('input', () => {
        const hex = normalizeHex(dom.customTextColorHex.value);
        if (hex) {
          state.customTextColor = hex;
          if (dom.customTextColor) dom.customTextColor.value = hex;
          schedulePreviewRender();
        }
      });
    }
    if (dom.bottlePreviewBtn) {
      dom.bottlePreviewBtn.addEventListener('click', () => enter3DMode());
    }
    if (dom.bottleBackBtn) {
      dom.bottleBackBtn.addEventListener('click', () => exit3DMode());
    }
    // Mobile-Overlay-Close (X-Button im Stage-Header). Auf Desktop ist der
    // Button per CSS ausgeblendet, der Listener bleibt aber harmlos.
    if (dom.bottleCloseBtn) {
      dom.bottleCloseBtn.addEventListener('click', () => exit3DMode());
    }

    // Phase 3 – Design-Skalierung + Reset-Buttons
    if (dom.designScaleRange) {
      updateSliderProgress(dom.designScaleRange);
      dom.designScaleRange.addEventListener('input', () => {
        // `parseInt(...) || 100` schluckt 0 (falsy) und ersetzt es durch
        // 100 — dadurch sprang der Slider-Wert beim Erreichen von 0 %
        // visuell auf 100 %. `Number.isFinite` lässt 0 als gültigen Wert
        // durch und fängt nur echte NaN-Fälle (leerer Input) ab.
        const raw = parseInt(dom.designScaleRange.value, 10);
        const pct = Number.isFinite(raw) ? raw : 100;
        state.designScale = pct / 100;
        if (dom.designScaleValue) dom.designScaleValue.textContent = String(pct);
        updateSliderProgress(dom.designScaleRange);
        // Off-Canvas-Cache wird in getDesignPreviewCanvas neu erstellt (key = drawW),
        // daher kein expliziter resetDesignPreviewCache nötig.
        clampDesignCenter();
        // `_scrubbing`-Flag aktiviert in updatePreview() den rAF-Pfad
        // (Frame-Sync ~60 fps) statt der 40 ms-Debounce-Variante. Greift
        // nur während aktiver Slider-Bewegung.
        state._scrubbing = true;
        updatePreview();
      });
      dom.designScaleRange.addEventListener('change', () => {
        // Slider losgelassen → zurück auf normalen Render-Pfad.
        state._scrubbing = false;
      });
    }
    if (dom.btnDesignReset) {
      dom.btnDesignReset.addEventListener('click', () => resetDesignTransform());
    }
    if (dom.btnTextReset) {
      dom.btnTextReset.addEventListener('click', () => resetTextTransform());
    }
  }

  // ============================================================
  // Produkt-Mode: Variant-Change-Listener
  // ------------------------------------------------------------
  // Im Produkt-Mode kommt die Sorte vom Standard-Shopify-
  // variant_picker oben auf der Produktseite. Wir hören auf das
  // Dawn-PubSub-Event `optionValueSelectionChange` (bevorzugt) und
  // beobachten zusätzlich den hidden `<input name="id">` der
  // Product-Form als Notnagel (MutationObserver), falls das
  // PubSub-Setup im köbi-Theme abweicht.
  // ============================================================
  async function syncSorteFromVariantTitle(title, variantId) {
    if (!title) return;
    // Falls keine Variant-ID übergeben: aus Lookup (Title→ID) auflösen.
    if (!variantId && _productVariantLookup) {
      variantId = Object.keys(_productVariantLookup).find(
        id => _productVariantLookup[id] === title
      );
    }
    const key = state.variantTitleToSorteKey[title];
    dlog('sync:', title, '→ key:', key, '→ id:', variantId, '(state.selectedSorte was:', state.selectedSorte, ')');
    if (variantId) state.cartVariantId = String(variantId);
    if (key) {
      if (dom.sorteSelect) dom.sorteSelect.value = key;
      // Auch wenn key === state.selectedSorte: Frame-Cache invalidieren und
      // re-rendern. Sicherheitsnetz für Race-Bedingungen beim Initial-Load.
      if (key !== state.selectedSorte) {
        onSorteChange(key);
      } else {
        resetFramePreviewCache();
        updatePreview();
      }
    }
  }

  function readActiveVariantTitle() {
    // Robuster Fallback ohne PubSub: aktuellen Variant-Title aus
    // den Standard-Variant-Picker-Controls lesen.
    // Versucht mehrere Selektor-Pattern (Dawn-Standard + Custom-Themes).
    const checkedRadio = document.querySelector(
      'variant-radios input[type=radio]:checked, ' +
      '.product-form input[type=radio]:checked, ' +
      'fieldset.product-form__input input[type=radio]:checked'
    );
    if (checkedRadio && checkedRadio.value) return checkedRadio.value.trim();

    const select = document.querySelector(
      'variant-selects select, variant-radios select, ' +
      '.product-form select[name^="options"]'
    );
    if (select && select.value) return select.value.trim();

    // Letzte Option: aktiver Pill-Button (häufig bei Custom-Variant-Pickern)
    const activePill = document.querySelector(
      '.product-form__input--pill .is-active, ' +
      '.product-form__input button[aria-checked="true"], ' +
      '.product-form__input button.is-selected, ' +
      '.product-form__input label.is-active'
    );
    if (activePill) {
      const t = (activePill.textContent || '').trim();
      if (t) return t;
    }
    return '';
  }

  // Lookup-Tabelle: variant.id → {option1, option2, option3, title}.
  // Promise-Lock verhindert doppeltes Fetch bei parallelen Calls.
  let _productVariantLookup = null;
  let _productVariantLookupPromise = null;

  function loadProductVariantLookup() {
    if (_productVariantLookup) return Promise.resolve(_productVariantLookup);
    if (_productVariantLookupPromise) return _productVariantLookupPromise;

    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return Promise.resolve(null);
    const handle = m[1];

    _productVariantLookupPromise = (async () => {
      try {
        // `cache: 'no-cache'` zwingt den Browser, beim Server zu revalidieren,
        // damit neu angelegte Varianten (z.B. nach productVariantsBulkCreate)
        // sofort verfügbar sind. `force-cache` hatte hier den Lookup-Stale-Bug
        // produziert (2026-05-22): Mutation-Observer meldete die neue Variant-
        // ID, syncFromVariantId machte aber Early-Out weil `lookup[newId]`
        // undefined war → Auto-Toggle lief nie.
        const r = await fetch(`/products/${handle}.js`, { cache: 'no-cache' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const lookup = {};
        (data.variants || []).forEach(v => {
          lookup[String(v.id)] = {
            option1: v.option1 || '',
            option2: v.option2 || '',
            option3: v.option3 || '',
            title:   v.title   || '',
          };
        });
        _productVariantLookup = lookup;
        return lookup;
      } catch (e) {
        dwarn('Variant-Lookup-Fetch fehlgeschlagen:', e);
        return null;
      } finally {
        _productVariantLookupPromise = null;
      }
    })();

    return _productVariantLookupPromise;
  }

  // Findet im Variant-Object den Sorten-Title, indem alle Options +
  // der Variant-Title gegen das Mapping geprüft werden.
  function findSorteInVariant(variantData) {
    if (!variantData || !state.variantTitleToSorteKey) return '';
    // Direkter Match: option1, option2, option3
    for (const cand of [variantData.option1, variantData.option2, variantData.option3]) {
      if (cand && state.variantTitleToSorteKey[cand]) return cand;
    }
    // Fallback: Variant-Title kann zusammengesetzt sein, z. B. "Helles / 16"
    const fullTitle = variantData.title || '';
    if (fullTitle) {
      for (const sortenName of Object.keys(state.variantTitleToSorteKey)) {
        // includes mit Wort-Boundary (verhindert "Bock" matched in "Bockwurst")
        const re = new RegExp('(^|[\\s/-])' + sortenName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '($|[\\s/-])');
        if (re.test(fullTitle)) return sortenName;
      }
    }
    return '';
  }

  async function syncFromVariantId(newId) {
    if (!newId) return;
    const lookup = await loadProductVariantLookup();
    if (!lookup) {
      dlog('syncFromVariantId: lookup leer/fetched fehlgeschlagen');
      return;
    }
    const variantData = lookup[String(newId)];
    if (!variantData) {
      dlog('syncFromVariantId: lookup kennt newId nicht — Cache veraltet?', String(newId), 'verfügbare IDs:', Object.keys(lookup));
      return;
    }
    const title = findSorteInVariant(variantData);
    dlog('Variant-ID', newId, '→ options:',
      [variantData.option1, variantData.option2, variantData.option3].filter(Boolean),
      '→ Sorte:', title || '(no match)');
    if (title) {
      syncSorteFromVariantTitle(title, newId);
    } else {
      // Selbst wenn keine Sorte im Variant ist, die Cart-Variant-ID merken.
      state.cartVariantId = String(newId);
    }
    // Nach Variant-Wechsel: bottle_count kann sich geändert haben → MHD-Modus
    // automatisch anpassen. autoSetDirectPrint… liest die aktuelle Variante via
    // getCartRequiredInfo() (= state.selectedSorte + cartVariantId) und vergleicht
    // mit state.directPrintThreshold.
    autoSetDirectPrintMhdFromBottleCount();
    updatePreview();
  }

  function setupVariantChangeListener() {
    if (state.mode !== 'product') return;

    // Variant-Lookup vorladen
    loadProductVariantLookup();

    // ─── 1) Initial-Sync via Variant-ID + Multi-Option-Lookup ──────
    // Liest die Variant-ID aus `<input name="id">` (von Shopify gesetzt),
    // schaut im Variant-Lookup-JSON nach allen Options (Sorte kann
    // option1, option2 oder option3 sein) und matched die erste, die
    // im Mapping vorhanden ist.
    (async () => {
      const productForm = document.querySelector('product-form form, form[action*="/cart/add"]');
      const variantInput = productForm ? productForm.querySelector('input[name="id"]') : null;
      const initialId = variantInput && variantInput.value;
      if (initialId) {
        dlog('initial-sync starting, variant-id:', initialId);
        await syncFromVariantId(initialId);
      } else {
        console.warn('[koebi-label] initial-sync: keine Variant-ID gefunden');
      }
    })();

    // ─── 2) Dawn-PubSub (direktes Variant-Object) ───────────────────
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.optionValueSelectionChange) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (!variant) return;
          const title = variant.option1 || variant.title || '';
          dlog('PubSub variant-change:', title, variant.id);
          syncSorteFromVariantTitle(title, variant.id);
        });
      } catch (e) {
        console.warn('[koebi-label] PubSub-Subscription fehlgeschlagen:', e);
      }
    }

    // ─── 3) Event-Delegation auf document.body ──────────────────────
    // Capture-Phase + Bubble-Phase. Überlebt jeglichen DOM-Re-Render
    // durch <variant-selects>. Filtert auf die Picker-Container.
    const isInsidePicker = (el) => el && el.closest && (
      el.closest('fieldset.product-form__input') ||
      el.closest('variant-selects') ||
      el.closest('variant-radios')
    );

    // Hilfs-Funktion: aktuelle Variant-ID aus dem Shopify-Form lesen
    // (wird von Shopify nach jedem Picker-Change automatisch gesetzt).
    const getCurrentVariantId = () => {
      const f = document.querySelector('product-form form, form[action*="/cart/add"]');
      const i = f ? f.querySelector('input[name="id"]') : null;
      return (i && i.value) || '';
    };

    const handleDelegatedChange = (e) => {
      const t = e.target;
      if (!t || !isInsidePicker(t)) return;
      // 80ms Delay: Shopify-Theme-JS aktualisiert input[name="id"] erst
      // nach unseren Listenern. Wir warten kurz, dann lesen wir die neue ID.
      setTimeout(() => {
        const id = getCurrentVariantId();
        if (id && id !== state.cartVariantId) {
          dlog('delegated change → variant-id:', id);
          syncFromVariantId(id);
        }
      }, 80);
    };
    document.body.addEventListener('change', handleDelegatedChange, true);
    document.body.addEventListener('change', handleDelegatedChange, false);

    // Click-Delegation als Backup
    document.body.addEventListener('click', (e) => {
      const t = e.target;
      if (!t || !isInsidePicker(t)) return;
      setTimeout(() => {
        const id = getCurrentVariantId();
        if (id && id !== state.cartVariantId) {
          dlog('delegated click → variant-id:', id);
          syncFromVariantId(id);
        }
      }, 80);
    }, true);

    // ─── 4) Hidden input[name="id"]-Mutation ────────────────────────
    const productForm = document.querySelector('product-form form, form[action*="/cart/add"]');
    const variantInput = productForm ? productForm.querySelector('input[name="id"]') : null;
    if (variantInput) {
      try {
        const obs = new MutationObserver(() => {
          const id = variantInput.value;
          if (id && id !== state.cartVariantId) {
            dlog('mutation → variant-id:', id);
            syncFromVariantId(id);
          }
        });
        obs.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      } catch (e) { /* old browsers */ }
    }

    // ─── 5) Polling via ID statt Title ──────────────────────────────
    // Robuster bei Multi-Options-Produkten: ID ändert sich exakt einmal
    // pro Picker-Wechsel, egal welche Option betroffen ist. Multi-Option
    // Lookup macht dann den Match auf Sorte.
    setInterval(() => {
      const id = getCurrentVariantId();
      if (id && id !== state.cartVariantId) {
        dlog('polling drift → variant-id:', id, '(was:', state.cartVariantId, ')');
        syncFromVariantId(id);
      }
    }, 250);
  }

  // ============================================================
  // Produkt-Mode: Standard-Buy-Button-Patch
  // ------------------------------------------------------------
  // Ersetzt das CTA-Verhalten des Standard-Shopify-Buy-Buttons
  // oben auf der Produktseite: statt "In den Warenkorb" wird er
  // zu "Etikett gestalten" und scrollt smooth zum Editor.
  // Die alte Submit-Logik wird verhindert (`type=button`).
  // Buy-It-Now (Dynamic Checkout) wird ausgeblendet — der echte
  // Cart-Add läuft über das interne Cart-Form des Editors.
  // ============================================================
  function patchProductFormButton() {
    // Editor selbst hat ein eigenes Cart-Form — das schließen wir aus.
    const productForm = document.querySelector(
      'product-form form:not([data-koebi-label-cart-form]), form[action*="/cart/add"]:not([data-koebi-label-cart-form])'
    );
    if (!productForm) return;
    // Nicht den Editor-internen Cart-Form patchen.
    if (productForm.closest('[data-koebi-label-editor]')) return;

    const submitBtn = productForm.querySelector('button[name="add"], button[type="submit"]');
    if (!submitBtn) return;

    // CTA-Text patchen — Dawn nutzt <span> in dem Button.
    const span = submitBtn.querySelector(
      'span:not(.icon):not([class*="loading"]):not(.visually-hidden)'
    ) || submitBtn;
    span.textContent = 'Etikett gestalten';

    // Original-Submit verhindern, stattdessen smooth-scroll zum Editor.
    submitBtn.setAttribute('type', 'button');
    submitBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const editorRoot = document.querySelector('[data-koebi-label-editor]');
      if (editorRoot) {
        editorRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          const uploadZone = editorRoot.querySelector('[data-koebi-label-dropzone]');
          if (uploadZone && typeof uploadZone.focus === 'function') {
            uploadZone.focus();
          }
        }, 600);
      }
    }, { capture: true });

    // Buy-It-Now-Button (Dynamic Checkout) ausblenden.
    const dynamicCheckout = productForm.querySelector('.shopify-payment-button');
    if (dynamicCheckout) dynamicCheckout.style.display = 'none';
  }

  // ============================================================
  // Frame-Asset-Pfad-Helpers (NEU im Theme-Port)
  // ============================================================
  // Wandelt einen Sorten-Key mit `_BG`-Suffix in den Slug für die
  // Theme-Asset-Filenames um:
  //   `Helles_BG`   → `helles`
  //   `AlteB17_BG`  → `alteb17`
  //   `Boeck_BG`    → `boeck`
  function frameKeyToSlug(key) {
    if (!key) return '';
    return String(key).replace(/_BG$/i, '').toLowerCase();
  }

  function variantToSlug(variant) {
    return variant === 'Weiss' ? 'weiss' : 'schwarz';
  }

  function frameAssetUrl(key, ext, variant) {
    const slug = frameKeyToSlug(key);
    if (!slug) return '';

    // Primary für PDFs: Metafeld-URL aus Sorten-Produkt (Session-1-Pipeline).
    // PNG-Previews bleiben Theme-Asset, ändern sich selten und sind nur fürs
    // Live-Preview im Editor relevant.
    if (ext === 'pdf') {
      const metaUrl = state.frameUrls && state.frameUrls[slug];
      if (metaUrl) return metaUrl;
    }

    // Fallback: Theme-Asset (Naming-Convention koebi-frame-<slug>[.<ext>|-<variant>.png])
    if (!state.assets || !state.assets.frameAssetBase) return '';
    if (ext === 'png' && variant) {
      return `${state.assets.frameAssetBase}koebi-frame-${slug}-${variantToSlug(variant)}.png`;
    }
    return `${state.assets.frameAssetBase}koebi-frame-${slug}.${ext}`;
  }

  // ============================================================
  // Sorten / Variant
  // ============================================================
  function onSorteChange(key) {
    dlog('onSorteChange:', key, '(was:', state.selectedSorte, ')');
    state.selectedSorte = key;
    resetFramePreviewCache();
    updatePreview();
    updateButton();
  }

  function setVariant(variant) {
    if (state.variant === variant) return;
    state.variant = variant;
    resetFramePreviewCache();
    updatePreview();
  }

  // Auto-Schwarz/Weiß-Frame-Wahl je nach Hintergrund-Luminanz.
  // Heller Hintergrund → schwarzer Frame; dunkler Hintergrund → weißer Frame.
  function chooseFrameVariantForBgColor(r, g, b) {
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 128 ? 'Schwarz' : 'Weiss';
  }

  function chooseFrameVariantForHex(hex) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex || '')) return state.variant || 'Schwarz';
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return chooseFrameVariantForBgColor(r, g, b);
  }

  // ============================================================
  // Cap-Swatches (3D-Deckelfarben)
  // ============================================================
  function renderCapSwatches() {
    if (!dom.bottleCapWrap) return;
    dom.bottleCapWrap.innerHTML = '';

    CAP_SWATCHES.forEach((s) => {
      const w = document.createElement('span');
      w.className = 'swatch-wrap';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bottle-cap-swatch';
      btn.style.background = s.hex;
      btn.title = s.title || s.hex;
      btn.addEventListener('click', () => {
        if (typeof window.bottleSetCapColor === 'function') {
          window.bottleSetCapColor(btn, s.hex, s.metalness, s.roughness);
        }
      });

      w.appendChild(btn);
      dom.bottleCapWrap.appendChild(w);
    });
  }

  // ============================================================
  // Helpers (clamp / hex)
  // ============================================================
  function clamp01(v)  { return Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0)); }
  function clamp255(v) { return Math.max(0, Math.min(255, Math.round(Number.isFinite(v) ? v : 0))); }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(v => clamp255(v).toString(16).padStart(2, '0')).join('');
  }

  function sanitizeName(str) {
    return String(str || '')
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-zA-Z0-9\-_ ]/g, '')
      .replace(/\s+/g, '_');
  }

  function sortenLabelFromKey(key) {
    return SORTEN.find(s => s.key === key)?.name || 'Etikett';
  }

  function getCartRequiredInfo() {
    const cartData = state.cartRequiredInfo || {};
    const byVariant = cartData.byVariant || {};
    const bySorte = cartData.bySorte || {};
    const variantData = byVariant[String(state.cartVariantId || '')] || {};
    const sorteData = bySorte[state.selectedSorte] || {};
    const bottleCount = Number(variantData.bottle_count) > 0
      ? variantData.bottle_count
      : sorteData.bottle_count;

    if (!variantData.pflichtangabe && !sorteData.pflichtangabe && !(Number(bottleCount) > 0)) {
      return null;
    }

    return {
      pflichtangabe: variantData.pflichtangabe || sorteData.pflichtangabe || '',
      bottle_count: bottleCount || 0,
    };
  }

  // Auto-Toggle des MHD/Charge-Druckmodus anhand der gewählten Variant-Bottle-Count.
  // ≥ Threshold (Section-Setting, Default 400) → Etikettierer-Modus (weißes Feld).
  // Sonst direkt aufdrucken. Wird in den _Editor-State serialisiert; im Druckbogen-
  // Plugin kann Tim den Wert vor dem Druck noch manuell überschreiben.
  function autoSetDirectPrintMhdFromBottleCount() {
    const data = getCartRequiredInfo();
    const bottleCount = data ? parseInt(data.bottle_count, 10) : 0;
    const threshold = state.directPrintThreshold || 400;
    dlog(
      'autoSetDirectPrintMhd:',
      'cartVariantId=', state.cartVariantId,
      'selectedSorte=', state.selectedSorte,
      'bottleCount=', bottleCount,
      'threshold=', threshold,
      'data=', data,
    );
    if (!Number.isFinite(bottleCount) || bottleCount <= 0) {
      dlog('autoSetDirectPrintMhd: bottleCount fehlt — state.directPrintMhd bleibt:', state.directPrintMhd);
      return;
    }
    const next = bottleCount < threshold;
    if (next !== state.directPrintMhd) {
      dlog('autoSetDirectPrintMhd: state.directPrintMhd', state.directPrintMhd, '→', next);
    }
    state.directPrintMhd = next;
  }

  function syncCartRequiredInfoInputs() {
    const data = getCartRequiredInfo();
    const pflichtangabe = data && data.pflichtangabe ? String(data.pflichtangabe).trim() : '';
    const bottleCount = data ? parseInt(data.bottle_count, 10) : 0;

    // Auto-Toggle anstoßen — bottle_count kann sich beim Variant-Wechsel ändern.
    autoSetDirectPrintMhdFromBottleCount();

    if (dom.cartRequiredInfo) {
      dom.cartRequiredInfo.value = pflichtangabe;
      dom.cartRequiredInfo.disabled = !pflichtangabe;
    }

    if (dom.cartBottleCount) {
      if (Number.isFinite(bottleCount) && bottleCount > 0) {
        dom.cartBottleCount.value = String(bottleCount);
        dom.cartBottleCount.disabled = false;
      } else {
        dom.cartBottleCount.value = '';
        dom.cartBottleCount.disabled = true;
      }
    }
  }

  function slugify(key) {
    return frameKeyToSlug(key);
  }

  // ============================================================
  // Vorschau-Caches
  // ============================================================
  function resetDesignPreviewCache(options = {}) {
    if (options.source !== false) state.designPreviewSource = null;
    state.designPreviewCacheKey = '';
    state.designPreviewCacheCanvas = null;
    state.previewDesignCanvas = null;
    state.previewDesignBounds = null;
  }

  function resetFramePreviewCache() {
    state.framePreviewCacheKey = '';
    state.framePreviewCacheCanvas = null;
  }

  function getRenderableSize(source) {
    return {
      width:  source?.naturalWidth || source?.videoWidth || source?.width  || 0,
      height: source?.naturalHeight || source?.videoHeight || source?.height || 0,
    };
  }

  function normalizeFrameCanvasColors(canvas) {
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 10) continue;
      const avg = (d[i] + d[i + 1] + d[i + 2]) / 3;
      if (avg < 80) {
        d[i] = 0; d[i + 1] = 0; d[i + 2] = 0;
      } else if (avg > 200) {
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
      }
    }
    ctx.putImageData(imgData, 0, 0);
    return canvas;
  }

  // ============================================================
  // Frame-PDF / Frame-Image — Asset-Pfade auf state.assets umgebogen
  // ============================================================
  async function getFramePdfDocument(framePdfKey) {
    if (state.framePdfDocCache.has(framePdfKey)) return state.framePdfDocCache.get(framePdfKey);
    const url = frameAssetUrl(framePdfKey, 'pdf');
    if (!url) return null;
    let bytes = state.framePdfBytesCache.get(framePdfKey);
    if (!bytes) {
      try {
        const r = await fetch(url, { cache: 'force-cache' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        bytes = new Uint8Array(await r.arrayBuffer());
        state.framePdfBytesCache.set(framePdfKey, bytes);
      } catch (e) {
        console.warn('Frame-PDF konnte nicht geladen werden:', url, e);
        return null;
      }
    }
    if (!window.pdfjsLib) return null;
    const docPromise = window.pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
    state.framePdfDocCache.set(framePdfKey, docPromise);
    return docPromise;
  }

  async function getFrameImage(framePdfKey, variant) {
    const cacheKey = `${framePdfKey}|${variant}`;
    if (state.frameImageCache.has(cacheKey)) return state.frameImageCache.get(cacheKey);
    const url = frameAssetUrl(framePdfKey, 'png', variant);
    if (!url) return null;
    const imgPromise = new Promise((res, rej) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    state.frameImageCache.set(cacheKey, imgPromise);
    return imgPromise;
  }

  async function getFramePdfBytes(framePdfKey) {
    if (state.framePdfBytesCache.has(framePdfKey)) {
      return state.framePdfBytesCache.get(framePdfKey);
    }
    const url = frameAssetUrl(framePdfKey, 'pdf');
    if (!url) return null;
    try {
      const r = await fetch(url, { cache: 'force-cache' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const bytes = new Uint8Array(await r.arrayBuffer());
      state.framePdfBytesCache.set(framePdfKey, bytes);
      return bytes;
    } catch (e) {
      console.warn('Frame-PDF konnte nicht geladen werden:', url, e);
      return null;
    }
  }

  async function getFramePngBytes(framePdfKey, variant) {
    const url = frameAssetUrl(framePdfKey, 'png', variant);
    if (!url) return null;
    try {
      const r = await fetch(url, { cache: 'force-cache' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return new Uint8Array(await r.arrayBuffer());
    } catch (e) {
      console.warn('Frame-PNG konnte nicht geladen werden:', url, e);
      return null;
    }
  }

  // ============================================================
  // Vorschau-Pipeline (Quelle Z.680–1067)
  // ============================================================
  async function getDesignPreviewCanvas(destW) {
    if (!state.fileBytes || !state.designPreviewSource) return null;
    const { width: srcW, height: srcH } = getRenderableSize(state.designPreviewSource);
    if (!srcW || !srcH) return null;
    const drawW = Math.max(1, Math.ceil(destW));
    const drawH = Math.max(1, Math.ceil(drawW / (srcW / srcH)));
    const key = `${drawW}x${drawH}`;
    if (state.designPreviewCacheCanvas && state.designPreviewCacheKey === key) {
      return state.designPreviewCacheCanvas;
    }
    const offC = document.createElement('canvas');
    offC.width  = drawW;
    offC.height = drawH;
    offC.getContext('2d', { colorSpace: 'srgb', willReadFrequently: true })
        .drawImage(state.designPreviewSource, 0, 0, drawW, drawH);
    state.designPreviewCacheKey = key;
    state.designPreviewCacheCanvas = offC;
    return offC;
  }

  async function getFramePreviewCanvas(labelW, labelH) {
    const frameKey = `${state.selectedSorte}|${state.variant}|${Math.max(1, Math.ceil(labelW))}x${Math.max(1, Math.ceil(labelH))}`;
    if (state.framePreviewCacheCanvas && state.framePreviewCacheKey === frameKey) {
      return state.framePreviewCacheCanvas;
    }

    const out = document.createElement('canvas');
    out.width  = Math.max(1, Math.ceil(labelW));
    out.height = Math.max(1, Math.ceil(labelH));

    const framePdfKey = state.selectedSorte;
    const framePageNum = state.variant === 'Weiss' ? 2 : 1;

    let usedPdf = false;
    if (window.pdfjsLib) {
      try {
        const framePdf = await getFramePdfDocument(framePdfKey);
        if (framePdf) {
          const framePage = await framePdf.getPage(framePageNum);
          const vp  = framePage.getViewport({ scale: 1 });
          const sc2 = out.width / vp.width;
          await framePage.render({
            canvasContext: out.getContext('2d'),
            viewport: framePage.getViewport({ scale: sc2 }),
            background: 'transparent',
          }).promise;
          normalizeFrameCanvasColors(out);
          usedPdf = true;
        }
      } catch (e) {
        console.warn('Frame-PDF render err:', e);
      }
    }

    if (!usedPdf) {
      try {
        const img = await getFrameImage(framePdfKey, state.variant);
        if (!img) {
          state.framePreviewCacheKey = frameKey;
          state.framePreviewCacheCanvas = null;
          return null;
        }
        out.getContext('2d').drawImage(img, 0, 0, out.width, out.height);
      } catch (e) {
        state.framePreviewCacheKey = frameKey;
        state.framePreviewCacheCanvas = null;
        return null;
      }
    }

    state.framePreviewCacheKey = frameKey;
    state.framePreviewCacheCanvas = out;
    return out;
  }

  function schedulePreviewRender() {
    if (previewRendering) {
      previewNeedsRerender = true;
      return;
    }
    previewRendering = true;
    const renderVersion = previewRequestVersion;
    Promise.resolve(renderPreview(renderVersion))
      .catch(err => console.warn('Preview render err', err))
      .finally(() => {
        previewRendering = false;
        if (previewNeedsRerender || renderVersion !== previewRequestVersion) {
          previewNeedsRerender = false;
          schedulePreviewRender();
        }
      });
  }

  function updatePreview() {
    previewRequestVersion += 1;
    clearTimeout(previewDebounce);
    // Während eines aktiven Drags ODER eines Slider-Scrubs: rAF statt
    // 40 ms-setTimeout — synchronisiert mit dem Display-Refresh und gibt
    // smooth ~60 fps. `_scrubbing` wird vom Design-Scale-Slider gesetzt
    // (input → true, change → false), damit das Slider-Drag wie der
    // Pointer-Drag mit Frame-Sync rendert statt mit 25fps-Debounce.
    if (state._drag || state._scrubbing) {
      if (!previewRafScheduled) {
        previewRafScheduled = true;
        requestAnimationFrame(() => {
          previewRafScheduled = false;
          schedulePreviewRender();
        });
      }
      return;
    }
    previewDebounce = setTimeout(schedulePreviewRender, 40);
  }

  async function renderPreview(expectedVersion = previewRequestVersion) {
    dlog('renderPreview, sorte:', state.selectedSorte, 'variant:', state.variant, 'fileBytes:', !!state.fileBytes);
    if (!state.selectedSorte || !dom.previewCanvas || !dom.previewWrap) return;
    const canvas = dom.previewCanvas;
    const wrap   = dom.previewWrap;
    const empty  = dom.emptyState;
    const ctx    = canvas.getContext('2d', { colorSpace: 'srgb', willReadFrequently: true });
    state.previewDesignCanvas = null;
    state.previewDesignBounds = null;
    state.previewTextBounds   = null;

    const PREV_BLEED = PRINT_BLEED_MM;          // 2.5 mm
    const totalW_mm  = LABEL_W + PREV_BLEED * 2; // 185 mm
    const totalH_mm  = LABEL_H + PREV_BLEED * 2; // 85 mm
    const aspect     = totalW_mm / totalH_mm;
    const dpr        = Math.min(window.devicePixelRatio || 1, 3);

    const contW = wrap.clientWidth || 800;
    const dispW = contW;
    const dispH = dispW / aspect;

    const SC    = (dispW * dpr) / totalW_mm;
    const prevW = Math.round(totalW_mm * SC);
    const prevH = Math.round(totalH_mm * SC);
    const oX    = Math.round(PREV_BLEED * SC);
    const oY    = Math.round(PREV_BLEED * SC);

    canvas.width  = prevW;
    canvas.height = prevH;
    canvas.style.width  = `${Math.round(dispW)}px`;
    canvas.style.height = `${Math.round(dispH)}px`;

    // 0. Bleed area + label area mit Hintergrundfarbe füllen
    ctx.fillStyle = state.bgColor;
    ctx.fillRect(0, 0, prevW, prevH);

    // 1. Etikett-Fläche (redundant, explizit)
    ctx.fillStyle = state.bgColor;
    ctx.fillRect(oX, oY, LABEL_W * SC, LABEL_H * SC);

    // 2. Kunden-Design im Design-Bereich (mit Phase-3 Scale + Offset + Clipping)
    if (state.fileBytes) {
      try {
        // Skalierung: drawW = DESIGN_W * scale (in mm-Pixeln)
        // Offset: state.designOffsetX/Y in mm → SC umrechnen
        // Default-X: linke Design-Bereich-Kante (DESIGN_X)
        // Default-Y: vertikal-zentriert im Etikett
        const scale  = state.designScale || 1;
        const drawW  = Math.max(1, DESIGN_W * SC * scale);
        const offC   = await getDesignPreviewCanvas(drawW);
        if (expectedVersion !== previewRequestVersion) return;
        if (offC) {
          // Phase 3 — Center-Anchor: Default-Center liegt bei (DESIGN_X + DESIGN_W/2, LABEL_H/2).
          // Bei Scale != 1 wird vom Center aus skaliert, nicht von der linken Kante.
          const centerX = oX + (DESIGN_X + DESIGN_W / 2 + state.designOffsetX) * SC;
          const centerY = oY + (LABEL_H / 2 + state.designOffsetY) * SC;
          const drawX   = Math.round(centerX - offC.width  / 2);
          const drawY   = Math.round(centerY - offC.height / 2);
          // Phase 3 — Clip auf Design-Bereich. Horizontal beschneiden (25–155 mm),
          // vertikal aber inkl. Bleed (0 bis prevH), damit das Design oben/unten
          // bis an den Beschnitt-Rand reichen darf — Tim druckt mit Bleed.
          // Bounds werden mit voller Größe gespeichert, damit der Drag-Hit-Test
          // großzügig bleibt.
          ctx.save();
          ctx.beginPath();
          ctx.rect(oX + DESIGN_X * SC, 0, DESIGN_W * SC, prevH);
          ctx.clip();
          ctx.drawImage(offC, drawX, drawY, offC.width, offC.height);
          ctx.restore();
          state.previewDesignCanvas = offC;
          state.previewDesignBounds = { x: drawX, y: drawY, width: offC.width, height: offC.height };
        }
      } catch (e) { console.warn('Design preview err', e); }
    } else {
      state.previewDesignCanvas = null;
      state.previewDesignBounds = null;
    }

    // 3. Frame-Overlay (PDF-Vektor oder PNG)
    try {
      const frameCanvas = await getFramePreviewCanvas(LABEL_W * SC, LABEL_H * SC);
      if (expectedVersion !== previewRequestVersion) return;
      if (frameCanvas) ctx.drawImage(frameCanvas, oX, oY, frameCanvas.width, frameCanvas.height);
    } catch (e) { console.warn('Frame preview err', e); }

    // 3b. Custom-Text mittig im Design-Bereich
    if (state.customText && state.customText.trim()) {
      try {
        await drawCustomTextOnPreview(ctx, SC, oX, oY, expectedVersion);
        if (expectedVersion !== previewRequestVersion) return;
      } catch (e) { console.warn('Custom-Text preview err', e); }
    }

    // 3c. MHD/Charge oder weißes Etikettierer-Feld — Canvas-Pendant zu
    //     drawMhdChargeOrField im PDF-Build. Zeigt dem Customer im Live-
    //     Preview, was später in die PDF kommt.
    try {
      drawMhdChargeOrFieldOnPreview(ctx, SC, oX, oY);
    } catch (e) { console.warn('MHD/Charge preview err', e); }

    // 4a. Bleed-Darkening — IMMER sichtbar, unabhängig vom Hilfslinien-Toggle.
    //     Markiert den Anschnittbereich, der vom Drucker abgeschnitten wird.
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, 0,           prevW, oY);
    ctx.fillRect(0, prevH - oY,  prevW, oY);
    ctx.fillRect(0, oY,          oX,    prevH - oY * 2);
    ctx.fillRect(prevW - oX, oY, oX,    prevH - oY * 2);
    ctx.restore();

    // 4b. Hilfslinien (Trim-Box + Design-Bereich-Linien) — nur bei Toggle.
    if (state.showGuides) {
      ctx.save();
      drawGuides(ctx, SC, oX, oY, dpr);
      ctx.restore();
    }

    canvas.style.display = 'block';
    if (empty) empty.style.display = 'none';
    if (dom.previewDims) {
      dom.previewDims.textContent =
        `${LABEL_W + PREV_BLEED * 2} × ${LABEL_H + PREV_BLEED * 2} mm (inkl. ${String(PREV_BLEED).replace('.', ',')} mm Beschnitt)`;
    }
  }

  // ============================================================
  // 3D-Mode — Toggle zwischen 2D-Vorschau und 3D-Flaschen-Stage.
  // ------------------------------------------------------------
  // enter3DMode():
  //   1. Bricht laufende Custom-Text-Edits sauber ab (defensive),
  //   2. Wendet das aktuelle Etikett als Textur auf die Flasche an
  //      (window.bottleApplyLabel ruft intern renderCleanLabelCanvas),
  //   3. Setzt data-3d-mode="on" am Root → CSS dimmt die Sidebar,
  //      blendet die 2D-Vorschau aus, zeigt die 3D-Stage und switcht
  //      das Action-Set.
  //   4. requestAnimationFrame → bottleRefreshView, damit der
  //      Three.js-Renderer den jetzt nicht mehr `display:none`-Wrap
  //      neu vermisst und mit korrekter Größe rendert.
  //
  // exit3DMode(): einfach data-3d-mode="off" — kein Texture-Cleanup,
  // damit ein erneutes Öffnen ohne Re-Render direkt wieder das letzte
  // Bild zeigt.
  // ============================================================
  // Akkordeon-Helfer für den 3D-Mode: collapseTextAccordion() merkt sich
  // den aktuellen Open-State im closure-State `accordionWasOpen` und fährt
  // das Panel ein. restoreTextAccordion() fährt es wieder aus, falls es
  // vorher offen war. Macht nichts, wenn der Trigger nicht existiert
  // (z. B. wenn das Markup ohne Text-Panel gerendert wurde).
  let accordionWasOpen = false;
  function collapseTextAccordion() {
    if (!dom || !dom.root) return;
    const trigger = dom.root.querySelector('[data-panel="text"] [data-koebi-accordion-trigger]');
    const body    = dom.root.querySelector('[data-panel="text"] [data-koebi-accordion-body]');
    if (!trigger || !body) return;
    accordionWasOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (accordionWasOpen) {
      trigger.setAttribute('aria-expanded', 'false');
      body.dataset.open = 'false';
    }
  }
  function restoreTextAccordion() {
    if (!dom || !dom.root) return;
    if (!accordionWasOpen) return;
    const trigger = dom.root.querySelector('[data-panel="text"] [data-koebi-accordion-trigger]');
    const body    = dom.root.querySelector('[data-panel="text"] [data-koebi-accordion-body]');
    if (!trigger || !body) return;
    trigger.setAttribute('aria-expanded', 'true');
    body.dataset.open = 'true';
    accordionWasOpen = false;
  }

  // ESC-Handler — wird beim Öffnen des 3D-Mode registriert und beim
  // Schließen wieder entfernt. Modulscope-Funktion (statt anonymes
  // Lambda), damit removeEventListener dieselbe Referenz sieht.
  function on3DEscape(e) {
    if (e.key === 'Escape') exit3DMode();
  }

  // Portal-State: auf Mobile wird die Stage nach <body> verschoben, damit
  // kein transform/filter/will-change-Vorfahr im Theme den `position: fixed`-
  // Container einsperrt (sonst landet unsere Stage IM Editor-Container statt
  // im Viewport — Theme-Header könnte drüberliegen). Wir merken uns den
  // Original-Parent + Next-Sibling, um beim Exit zurückzuverschieben.
  let stagePortalParent = null;
  let stagePortalNext   = null;

  function portalStageToBody() {
    const stage = dom && dom.bottleStage;
    if (!stage || stage.parentNode === document.body) return;
    stagePortalParent = stage.parentNode;
    stagePortalNext   = stage.nextSibling;
    document.body.appendChild(stage);
    console.log('[koebi-3D] Stage in <body> portaliert');
  }
  function restoreStageFromPortal() {
    const stage = dom && dom.bottleStage;
    if (!stage || !stagePortalParent) return;
    if (stagePortalNext && stagePortalNext.parentNode === stagePortalParent) {
      stagePortalParent.insertBefore(stage, stagePortalNext);
    } else {
      stagePortalParent.appendChild(stage);
    }
    stagePortalParent = null;
    stagePortalNext   = null;
  }

  // Apply-Retry: auf Mobile räumt der Init-Promise zwar das GLB ab, aber
  // gelegentlich greift die erste bottleApplyLabel-Texture noch nicht (z. B.
  // wenn der Renderer im selben Frame setSize/Composer-Setup macht). Ein
  // zweites Apply nach ~250 ms ist Race-Safety mit minimalem Side-Effect.
  function applyLabelWithRetry() {
    const apply = () => {
      if (typeof window.bottleApplyLabel !== 'function') {
        console.warn('[koebi-3D] bottleApplyLabel nicht verfügbar — Init evtl. fehlgeschlagen?');
        return;
      }
      try {
        const r = window.bottleApplyLabel();
        if (r && typeof r.then === 'function') {
          r.catch(err => console.warn('[koebi-3D] applyLabel async-Fehler:', err));
        }
      } catch (e) {
        console.warn('[koebi-3D] applyLabel sync-Fehler:', e);
      }
    };
    apply();
    setTimeout(apply, 250);
    setTimeout(apply, 800);
  }

  function enter3DMode() {
    const editorRoot = dom && dom.root;
    if (!editorRoot) return;
    console.log('[koebi-3D] enter3DMode — mobile?',
      window.matchMedia('(max-width: 899.98px)').matches);
    // Text-Panel einfahren falls offen — der State wird gesichert, damit
    // exit3DMode() ihn wiederherstellen kann.
    collapseTextAccordion();

    const isMobile = window.matchMedia('(max-width: 899.98px)').matches;

    // Mobile: Stage in <body> portalen BEVOR data-3d-mode gesetzt wird, damit
    // die Slide-In-Animation am korrekten Containing-Block startet.
    if (isMobile) {
      portalStageToBody();
      // data-3d-mode muss am NEUEN Parent-Pfad selektierbar bleiben — wir
      // setzen es auch am Stage-Element selbst, damit unsere CSS-Regeln
      // gleichermaßen am body-portalisierten Stage greifen.
      if (dom.bottleStage) dom.bottleStage.setAttribute('data-3d-active', 'on');
    }
    editorRoot.setAttribute('data-3d-mode', 'on');

    // Scroll-Lock NUR auf Mobile. Auf Desktop bleibt das Verhalten
    // exakt wie vorher (Inline-Mode-Switch, Body bleibt scrollbar).
    // Lock auf BEIDEN: html und body — manche Themes scrollen <html>,
    // andere <body>; doppelter Lock verhindert Scroll-Bleed.
    if (isMobile) {
      document.documentElement.classList.add('koebi-label-3d-open');
      document.body.classList.add('koebi-label-3d-open');
    }
    // ESC schließt das Overlay (Desktop: Inline-Mode-Switch; Mobile: Overlay).
    document.addEventListener('keydown', on3DEscape);

    // Lazy-Init des 3D-Moduls. Auf Desktop ist bottleInit() bereits beim
    // Script-Load aufgerufen worden und gibt das bereits-fertige Promise
    // zurück (instant). Auf Mobile startet hier die Three.js-Initialisierung.
    const initP = (typeof window.bottleInit === 'function')
      ? window.bottleInit()
      : Promise.resolve();
    console.log('[koebi-3D] bottleInit-Promise:', initP);

    // Nach Init: Etikett-Textur applyen (mit Retry) + Renderer-Resize.
    // Während Three.js auf Mobile noch lädt, ist die Stage schon sichtbar
    // (CSS-Switch via data-3d-mode passierte synchron) — der Loading-State
    // im Canvas-Wrap zeigt den User „3D-Modell wird geladen…".
    initP.then(() => {
      console.log('[koebi-3D] Init resolved — labelMesh? bottleApplyLabel?',
        typeof window.bottleApplyLabel);
      applyLabelWithRetry();
      // Renderer-Resize nach dem CSS-Switch erzwingen (vorher war der Wrap
      // `display:none` und hatte 0×0 Pixel — der Three.js-setSize-Pfad
      // hätte ohne diesen Re-Trigger mit 0 weitergerechnet).
      requestAnimationFrame(() => {
        if (typeof window.bottleRefreshView === 'function') {
          try { window.bottleRefreshView(); } catch (e) { console.warn('[koebi-3D] refreshView:', e); }
        }
      });
    }).catch(err => console.warn('[koebi-3D] init:', err));
  }

  function exit3DMode() {
    const editorRoot = dom && dom.root;
    if (!editorRoot) return;
    editorRoot.setAttribute('data-3d-mode', 'off');
    if (dom.bottleStage) dom.bottleStage.removeAttribute('data-3d-active');
    // Scroll-Lock + ESC-Handler abbauen — html UND body, analog zu enter.
    document.documentElement.classList.remove('koebi-label-3d-open');
    document.body.classList.remove('koebi-label-3d-open');
    document.removeEventListener('keydown', on3DEscape);
    // Stage zurück in den Original-Container portalen — nach der
    // Close-Animation (sonst springt das Element optisch um).
    setTimeout(restoreStageFromPortal, 300);
    // Falls das Text-Panel vor dem 3D-Mode offen war: wieder ausfahren.
    restoreTextAccordion();
  }

  // ============================================================
  // renderCleanLabelCanvas() — saubere Etikettenvorlage für die
  // 3D-Textur.
  // ------------------------------------------------------------
  // Liefert einen frischen Off-Screen-Canvas im Format LABEL_W × LABEL_H
  // (180 × 80 mm, OHNE 2,5 mm Beschnitt-Zugabe) ohne Hilfslinien und
  // ohne Bleed-Darkening. Ziel-Auflösung: 10 px/mm = 1800×800 px,
  // skaliert sauber auf die Flaschen-UV-Map.
  //
  // Pipeline parallel zu renderPreview(), aber:
  //   - Off-Screen Canvas statt #previewCanvas,
  //   - kein 2.5 mm Bleed,
  //   - Bleed-Darkening (4a) übersprungen,
  //   - Guides (4b) übersprungen.
  //
  // Wird von koebi-label-editor-3d.js / window.bottleApplyLabel
  // aufgerufen, wenn der User auf „3D-Vorschau" klickt.
  // ============================================================
  window.renderCleanLabelCanvas = async function renderCleanLabelCanvas() {
    const PX_PER_MM = 10;                       // 10 px/mm → 1800×800 px
    const SC        = PX_PER_MM;
    const cleanW    = LABEL_W * SC;             // 1800
    const cleanH    = LABEL_H * SC;             // 800
    // Cache-Reset für Design + Frame, damit auch im Edge-Case identischer
    // Dimensionen (z. B. neuer Upload mit gleicher Skalierung) der frische
    // Zustand verwendet wird. Quellen-Daten bleiben unangetastet.
    resetDesignPreviewCache({ source: false });
    resetFramePreviewCache();
    const off = document.createElement('canvas');
    off.width  = cleanW;
    off.height = cleanH;
    const ctx = off.getContext('2d', { colorSpace: 'srgb', willReadFrequently: false });

    // 0. Etiketten-Hintergrundfarbe
    ctx.fillStyle = state.bgColor || '#FFFFFF';
    ctx.fillRect(0, 0, cleanW, cleanH);

    // 1. Kunden-Design
    if (state.fileBytes) {
      try {
        const scale = state.designScale || 1;
        const drawW = Math.max(1, DESIGN_W * SC * scale);
        const offC  = await getDesignPreviewCanvas(drawW);
        if (offC) {
          const centerX = (DESIGN_X + DESIGN_W / 2 + state.designOffsetX) * SC;
          const centerY = (LABEL_H / 2 + state.designOffsetY) * SC;
          const drawX   = Math.round(centerX - offC.width  / 2);
          const drawY   = Math.round(centerY - offC.height / 2);
          ctx.save();
          ctx.beginPath();
          ctx.rect(DESIGN_X * SC, 0, DESIGN_W * SC, cleanH);
          ctx.clip();
          ctx.drawImage(offC, drawX, drawY, offC.width, offC.height);
          ctx.restore();
        }
      } catch (e) { console.warn('[renderCleanLabelCanvas] design:', e); }
    }

    // 2. Frame-Overlay
    try {
      const frameCanvas = await getFramePreviewCanvas(LABEL_W * SC, LABEL_H * SC);
      if (frameCanvas) ctx.drawImage(frameCanvas, 0, 0, frameCanvas.width, frameCanvas.height);
    } catch (e) { console.warn('[renderCleanLabelCanvas] frame:', e); }

    // 3. Custom-Text — inline gezeichnet (NICHT über drawCustomTextOnPreview),
    //    damit kein Cancel-Check und kein Side-Effect auf state.previewText-
    //    Bounds (das ist 2D-Hit-Test-State und darf vom 3D-Pfad nicht
    //    überschrieben werden). Logik 1:1 portiert aus drawCustomTextOnPreview.
    const txt = (state.customText || '').trim() ? state.customText : '';
    if (txt) {
      const fontFamily = state.customFont || 'Bricolage Grotesque';
      const variant    = getVariantByLabel(fontFamily, state.customTextStyle);
      const fontWeight = variant ? variant.weight : 400;
      const fontItalic = variant ? variant.italic : false;
      const sizeMm     = state.customTextSize * 0.3528;     // pt → mm
      const sizePx     = sizeMm * SC;                       // mm → Canvas-px
      const textCss    = normalizeHex(state.customTextColor) || '#000000';

      // Font sicher geladen (Timeout 200 ms — sonst rendert er in Fallback).
      try {
        const probe = `${fontItalic ? 'italic ' : ''}${fontWeight} ${Math.round(sizePx)}px "${fontFamily}"`;
        if (document.fonts && typeof document.fonts.load === 'function') {
          await Promise.race([
            document.fonts.load(probe, txt.slice(0, 50)),
            new Promise(res => setTimeout(res, 200)),
          ]);
        }
      } catch (e) { /* still proceed mit Fallback-Font */ }

      const lines      = txt.split('\n');
      const lineHeight = sizePx * (state.customLineHeight || 1.2);
      // oX=0, oY=0 — Clean-Canvas hat keinen Bleed-Offset.
      const centerX = (DESIGN_X + DESIGN_W / 2 + state.textOffsetX) * SC;
      const centerY = (LABEL_H / 2 + state.textOffsetY) * SC;
      // `textBaseline='middle'` zentriert die EM-Box (Font-Metrik) auf der
      // gegebenen y-Koordinate. Dadurch bleibt der Text bei Änderungen von
      // Schriftgröße ODER Zeilenabstand exakt um centerY zentriert —
      // alphabetic + `+sizePx` würde dagegen mit wachsender Größe nach unten
      // driften, weil Cap-Höhe ≠ Schriftgröße.
      const firstMiddleY = centerY - ((lines.length - 1) * lineHeight) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(DESIGN_X * SC, 0, DESIGN_W * SC, cleanH);
      ctx.clip();
      ctx.fillStyle    = textCss;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${fontItalic ? 'italic ' : ''}${fontWeight} ${Math.round(sizePx)}px "${fontFamily}", "Bricolage Grotesque", system-ui, sans-serif`;
      lines.forEach((line, idx) => {
        ctx.fillText(line, centerX, firstMiddleY + idx * lineHeight);
      });
      ctx.restore();
    }

    // 3b. MHD/Charge oder weißes Feld — auch in der 3D-Textur sichtbar
    //     machen, damit die Flaschen-Vorschau dem Live-Preview entspricht.
    //     `oX=0, oY=0` weil renderCleanLabelCanvas ohne Bleed-Rahmen rendert.
    try {
      drawMhdChargeOrFieldOnPreview(ctx, SC, 0, 0);
    } catch (e) { console.warn('[renderCleanLabelCanvas] mhd:', e); }

    // 4. KEIN Bleed-Darkening, KEINE Guides — das war ja der Witz dieser Funktion.

    return off;
  };

  function drawGuides(ctx, SC, oX, oY, dpr) {
    const lW = LABEL_W * SC;
    const lH = LABEL_H * SC;
    const dX = oX + DESIGN_X * SC;
    const dW = DESIGN_W * SC;
    const lw = 1 * (dpr || 1); // 1px Hilfslinien (DPR-skaliert für Retina)

    ctx.save();

    // Trim-Box
    ctx.setLineDash([]);
    ctx.strokeStyle = '#E5005B';
    ctx.lineWidth = lw;
    ctx.strokeRect(oX + lw / 2, oY + lw / 2, lW - lw, lH - lw);

    // Design-Bereich vertikale Trennlinien
    ctx.strokeStyle = '#E5005B';
    ctx.globalAlpha = 0.85;
    ctx.setLineDash([6 * (dpr || 1), 4 * (dpr || 1)]);
    ctx.lineWidth = lw;
    ctx.beginPath(); ctx.moveTo(dX,      oY); ctx.lineTo(dX,      oY + lH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(dX + dW, oY); ctx.lineTo(dX + dW, oY + lH); ctx.stroke();

    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  function toggleGuides() {
    state.showGuides = !state.showGuides;
    if (dom.guidesToggle) dom.guidesToggle.checked = state.showGuides;
    updatePreview();
  }

  // ============================================================
  // Phase 3 — Drag/Scale auf Preview-Canvas
  // ------------------------------------------------------------
  // Pointer-Events (down/move/up/cancel). Hit-Test:
  //   1. previewTextBounds (Text liegt visuell oben)
  //   2. previewDesignBounds
  // Constraint: Center des verschobenen Elements muss in der
  // sichtbaren Etikett-Fläche (LABEL_W × LABEL_H) bleiben.
  // ============================================================

  // Liefert {mmX, mmY} relativ zur OBEREN-LINKEN Etikett-Ecke (ohne Bleed).
  // pxX/pxY sind CSS-Pixel relativ zum canvas-Element.
  function pointerCssToMm(pxX, pxY, canvas) {
    const r = canvas.getBoundingClientRect();
    const totalW = LABEL_W + PRINT_BLEED_MM * 2;
    const mmPerPx = totalW / r.width;
    return {
      mmX: pxX * mmPerPx - PRINT_BLEED_MM,
      mmY: pxY * mmPerPx - PRINT_BLEED_MM,
    };
  }

  // Liefert Punkt in CANVAS-internen Pixeln (für Bounds-Test gegen previewDesignBounds).
  function pointerToCanvasPx(ev, canvas) {
    const r = canvas.getBoundingClientRect();
    const cssX = ev.clientX - r.left;
    const cssY = ev.clientY - r.top;
    // canvas.width ist die interne Pixel-Auflösung (mit dpr); css-Größe ist r.width.
    const sx = canvas.width  / r.width;
    const sy = canvas.height / r.height;
    return { x: cssX * sx, y: cssY * sy, cssX, cssY };
  }

  function pointInRect(x, y, rect) {
    if (!rect) return false;
    return x >= rect.x && x <= rect.x + rect.width
        && y >= rect.y && y <= rect.y + rect.height;
  }

  // Constraint: Center darf nicht aus der Etikett-Fläche raus.
  function clampDesignCenter() {
    // Default-Center des Designs (mm vom Etikett-TL)
    // Horizontal: linke Kante DESIGN_X, Breite DESIGN_W * scale → Center bei DESIGN_X + DESIGN_W*scale/2
    // Default-Offset 0 ergibt Center bei DESIGN_X + DESIGN_W/2 (Default-Layout)
    // Wir clampen so, dass Default-Center + offset im Etikett bleibt:
    const defaultCenterX = DESIGN_X + DESIGN_W / 2;
    const defaultCenterY = LABEL_H / 2;
    const cx = defaultCenterX + state.designOffsetX;
    const cy = defaultCenterY + state.designOffsetY;
    const cxClamped = Math.max(0, Math.min(LABEL_W, cx));
    const cyClamped = Math.max(0, Math.min(LABEL_H, cy));
    state.designOffsetX = cxClamped - defaultCenterX;
    state.designOffsetY = cyClamped - defaultCenterY;
  }
  function clampTextCenter() {
    const defaultCenterX = DESIGN_X + DESIGN_W / 2;
    const defaultCenterY = LABEL_H / 2;
    const cx = defaultCenterX + state.textOffsetX;
    const cy = defaultCenterY + state.textOffsetY;
    const cxClamped = Math.max(0, Math.min(LABEL_W, cx));
    const cyClamped = Math.max(0, Math.min(LABEL_H, cy));
    state.textOffsetX = cxClamped - defaultCenterX;
    state.textOffsetY = cyClamped - defaultCenterY;
  }

  function setupCanvasInteraction() {
    const canvas = dom.previewCanvas;
    if (!canvas) return;

    // Cursor: Hover signalisiert Greifbarkeit.
    canvas.addEventListener('pointermove', (ev) => {
      if (state._drag) return; // wird unten gehandhabt
      if (!state.fileBytes && !(state.customText && state.customText.trim())) {
        canvas.style.cursor = '';
        return;
      }
      const p = pointerToCanvasPx(ev, canvas);
      const overText   = pointInRect(p.x, p.y, state.previewTextBounds);
      const overDesign = pointInRect(p.x, p.y, state.previewDesignBounds);
      canvas.style.cursor = (overText || overDesign) ? 'grab' : '';
    });

    canvas.addEventListener('pointerleave', () => {
      if (!state._drag) canvas.style.cursor = '';
    });

    canvas.addEventListener('pointerdown', (ev) => {
      const p = pointerToCanvasPx(ev, canvas);
      // Text hat Vorrang — er liegt visuell oben.
      let kind = null;
      if (state.customText && state.customText.trim() && pointInRect(p.x, p.y, state.previewTextBounds)) {
        kind = 'text';
      } else if (state.fileBytes && pointInRect(p.x, p.y, state.previewDesignBounds)) {
        kind = 'design';
      }
      if (!kind) return;
      ev.preventDefault();
      try { canvas.setPointerCapture(ev.pointerId); } catch (e) { /* setPointerCapture optional */ }
      const start = pointerCssToMm(p.cssX, p.cssY, canvas);
      state._drag = {
        kind,
        pointerId:  ev.pointerId,
        startMmX:   start.mmX,
        startMmY:   start.mmY,
        startOffX:  kind === 'text' ? state.textOffsetX : state.designOffsetX,
        startOffY:  kind === 'text' ? state.textOffsetY : state.designOffsetY,
      };
      canvas.style.cursor = 'grabbing';
      canvas.classList.add('is-dragging');
    });

    canvas.addEventListener('pointermove', (ev) => {
      const drag = state._drag;
      if (!drag || drag.pointerId !== ev.pointerId) return;
      ev.preventDefault();
      const p   = pointerToCanvasPx(ev, canvas);
      const now = pointerCssToMm(p.cssX, p.cssY, canvas);
      const dxMm = now.mmX - drag.startMmX;
      const dyMm = now.mmY - drag.startMmY;
      if (drag.kind === 'text') {
        state.textOffsetX = drag.startOffX + dxMm;
        state.textOffsetY = drag.startOffY + dyMm;
        clampTextCenter();
      } else {
        state.designOffsetX = drag.startOffX + dxMm;
        state.designOffsetY = drag.startOffY + dyMm;
        clampDesignCenter();
      }
      updatePreview();
    });

    function endDrag(ev) {
      const drag = state._drag;
      if (!drag) return;
      if (ev && drag.pointerId !== ev.pointerId) return;
      try { canvas.releasePointerCapture(drag.pointerId); } catch (e) { /* */ }
      state._drag = null;
      canvas.style.cursor = '';
      canvas.classList.remove('is-dragging');
    }
    canvas.addEventListener('pointerup',     endDrag);
    canvas.addEventListener('pointercancel', endDrag);
  }

  // ============================================================
  // Custom-Text-Vorschau (Phase 2)
  // ------------------------------------------------------------
  // Zeichnet den state.customText mittig zentriert im Design-
  // Bereich auf den Vorschau-Canvas. Multi-line via "\n" wird
  // unterstützt; Line-Height = 1.2 × font-size. Vor dem Zeichnen
  // wird `document.fonts.load(...)` abgewartet, damit Google-Fonts
  // nicht in Fallback-Helvetica gerendert werden.
  // ============================================================
  // Canvas-Pendant zu drawMhdChargeOrField (PDF-Build). Wird sowohl im
  // 2D-Editor-Preview (renderPreview) als auch in der 3D-Textur
  // (renderCleanLabelCanvas) aufgerufen. Argumente:
  //   ctx — 2D-Canvas-Context, schon mit Hintergrund + Frame gemalt
  //   SC  — Scale-Faktor mm → Canvas-Pixel
  //   oX  — X-Offset zur Trim-Kante in Canvas-Pixel (im 2D-Pfad = Bleed,
  //         im 3D-Pfad = 0)
  //   oY  — Y-Offset zur Trim-Oberkante in Canvas-Pixel
  // Zeichnet exakt das, was später ins PDF kommt: MHD/Charge bei
  // directPrintMhd=true, weißes Feld bei false + dunklem BG.
  function drawMhdChargeOrFieldOnPreview(ctx, SC, oX, oY) {
    const hex = (state.bgColor || '#FFFFFF').replace('#', '');
    const cr = parseInt(hex.substring(0, 2), 16) / 255;
    const cg = parseInt(hex.substring(2, 4), 16) / 255;
    const cb = parseInt(hex.substring(4, 6), 16) / 255;
    const luminance = cr * 0.299 + cg * 0.587 + cb * 0.114;
    const bgIsDark = luminance < 0.5;

    if (state.directPrintMhd) {
      const mhd = (getAutoMhd() || '').trim();
      const charge = (getAutoCharge() || '').trim();
      if (!mhd && !charge) return;

      const fontSizeMm = 6 * 0.3528;        // 6 pt → mm
      const fontSizePx = fontSizeMm * SC;
      const textColorCss = bgIsDark ? '#FFFFFF' : '#000000';

      // 1:1 wie im PDF-Build. Tim misst in InDesign Top-of-Cap (13 mm / 16 mm),
      // Canvas/PDF brauchen Baseline — daher +1.5 mm Cap-Height-Versatz bei 6 pt.
      const textX = oX + 4 * SC;
      const mhdBaselineY = oY + 14.5 * SC;
      const chargeBaselineY = oY + 17.5 * SC;

      ctx.save();
      ctx.fillStyle = textColorCss;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.font = `400 ${Math.round(fontSizePx)}px "Bricolage Grotesque", system-ui, sans-serif`;
      if (mhd) ctx.fillText(mhd, textX, mhdBaselineY);
      if (charge) ctx.fillText(charge, textX, chargeBaselineY);
      ctx.restore();
      return;
    }

    // Etikettierer-Modus: weißes Feld nur bei dunklem BG.
    if (!bgIsDark) return;

    // Box bei 3.6 mm (0.4 mm Bearing-Ausgleich zum Frame-Text), Maße 18×16 mm.
    const fieldX = oX + 3.6 * SC;
    const fieldY = oY + 13 * SC;
    const fieldW = 18 * SC;
    const fieldH = 16 * SC;
    const radius = 0.4 * SC;

    ctx.save();
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(fieldX, fieldY, fieldW, fieldH, radius);
    } else {
      // Fallback für ältere Browser ohne CanvasRenderingContext2D.roundRect.
      ctx.moveTo(fieldX + radius, fieldY);
      ctx.lineTo(fieldX + fieldW - radius, fieldY);
      ctx.arcTo(fieldX + fieldW, fieldY, fieldX + fieldW, fieldY + radius, radius);
      ctx.lineTo(fieldX + fieldW, fieldY + fieldH - radius);
      ctx.arcTo(fieldX + fieldW, fieldY + fieldH, fieldX + fieldW - radius, fieldY + fieldH, radius);
      ctx.lineTo(fieldX + radius, fieldY + fieldH);
      ctx.arcTo(fieldX, fieldY + fieldH, fieldX, fieldY + fieldH - radius, radius);
      ctx.lineTo(fieldX, fieldY + radius);
      ctx.arcTo(fieldX, fieldY, fieldX + radius, fieldY, radius);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  async function drawCustomTextOnPreview(ctx, SC, oX, oY, expectedVersion) {
    const txt = state.customText || '';
    if (!txt.trim()) return;
    const fontFamily = state.customFont || 'Bricolage Grotesque';
    const variant    = getVariantByLabel(fontFamily, state.customTextStyle);
    const fontWeight = variant ? variant.weight : 400;
    const fontItalic = variant ? variant.italic : false;
    // mm-Größe als CSS-px-Größe interpretieren und mit SC skalieren —
    // SC ist (dpr·devicePixelRatio · prevW)/totalW_mm, d. h. mm → Canvas-px.
    // state.customTextSize ist in pt; 1 pt = 0.3528 mm.
    const sizeMm = state.customTextSize * 0.3528;
    const sizePx = sizeMm * SC;

    // Phase 3.2: Schriftfarbe aus State (statt Auto-Schwarz/Weiß)
    const textCss = normalizeHex(state.customTextColor) || '#000000';

    // Font sicher geladen — mit Timeout-Fallback (200 ms).
    try {
      const probe = `${fontItalic ? 'italic ' : ''}${fontWeight} ${Math.round(sizePx)}px "${fontFamily}"`;
      if (document.fonts && typeof document.fonts.load === 'function') {
        await Promise.race([
          document.fonts.load(probe, txt.slice(0, 50)),
          new Promise(res => setTimeout(res, 500)),
        ]);
      }
    } catch (e) { /* still proceed */ }
    // Versions-Check NUR wenn explizit eine Erwartung übergeben wurde. Bei
    // null/undefined (z. B. Aufruf aus renderCleanLabelCanvas für die 3D-
    // Textur) überspringen wir die Cancel-Logik — das wäre sonst die einzige
    // Stelle, an der der Text auf der Flasche verloren gehen kann, weil
    // previewRequestVersion während des fonts.load-await inkrementiert wird.
    if (expectedVersion != null && expectedVersion !== previewRequestVersion) return;

    const lines      = txt.split('\n');
    const lineHeight = sizePx * (state.customLineHeight || 1.2);

    // Center im Etikett (in Canvas-px) + Phase-3-Text-Offset (mm → px via SC)
    const centerX = oX + (DESIGN_X + DESIGN_W / 2 + state.textOffsetX) * SC;
    const centerY = oY + (LABEL_H / 2 + state.textOffsetY) * SC;
    // `textBaseline='middle'` zentriert die EM-Box der Font-Metrik auf der
    // gegebenen y-Koordinate. Dadurch bleibt der Text bei Änderungen von
    // Schriftgröße ODER Zeilenabstand exakt um centerY zentriert.
    const firstMiddleY = centerY - ((lines.length - 1) * lineHeight) / 2;
    // Für Hit-Test-Bounds: visuelle Block-Höhe ≈ (N-1) Zeilenabstände + sizePx.
    const blockH = (lines.length - 1) * lineHeight + sizePx;

    ctx.save();
    // Phase 3 — Clip auf Design-Bereich. Horizontal: 25–155 mm. Vertikal: gesamte
    // Canvas-Höhe inkl. Bleed (siehe renderPreview).
    ctx.beginPath();
    ctx.rect(oX + DESIGN_X * SC, 0, DESIGN_W * SC, ctx.canvas.height);
    ctx.clip();
    ctx.fillStyle    = textCss;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    // Phase 3.2: Italic + weight in den Font-String einbauen
    ctx.font         = `${fontItalic ? 'italic ' : ''}${fontWeight} ${Math.round(sizePx)}px "${fontFamily}", "Bricolage Grotesque", system-ui, sans-serif`;

    // Längste Zeile messen — für Hit-Test-Bounding-Box (vor dem Clip ausreichend, wir
    // brauchen den vollen Text-Footprint für den Drag-Hit-Test, nicht nur den sichtbaren).
    let maxLineW = 0;
    lines.forEach(line => {
      const m = ctx.measureText(line);
      if (m && m.width > maxLineW) maxLineW = m.width;
    });

    lines.forEach((line, idx) => {
      ctx.fillText(line, centerX, firstMiddleY + idx * lineHeight);
    });
    ctx.restore();

    // Bounds in Canvas-Pixeln speichern (für Pointer-Hit-Test).
    // Padding (8px je Seite) macht das Greifen großzügiger.
    const pad = 8;
    state.previewTextBounds = {
      x: Math.round(centerX - maxLineW / 2 - pad),
      y: Math.round(centerY - blockH / 2 - pad),
      width:  Math.round(maxLineW + pad * 2),
      height: Math.round(blockH   + pad * 2),
    };
  }

  // EXIF-Orientation direkt aus einem JPEG lesen. Die Werte 2–8 umfassen
  // neben Rotationen auch horizontale/vertikale Spiegelungen. Die eigentliche
  // Transformation ueberlassen wir bewusst dem Browser-Decoder: Genau dessen
  // sichtbare Pixel werden anschliessend ohne Orientation-Metadaten exportiert.
  function readJpegExifOrientation(bytes) {
    const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (raw.length < 4 || raw[0] !== 0xff || raw[1] !== 0xd8) return 1;
    const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
    let offset = 2;

    while (offset + 4 <= raw.length) {
      if (raw[offset] !== 0xff) break;
      const marker = raw[offset + 1];
      offset += 2;
      if (marker === 0xd8 || marker === 0x01) continue;
      if (marker === 0xd9 || marker === 0xda) break;
      if (offset + 2 > raw.length) break;
      const segmentLength = view.getUint16(offset, false);
      if (segmentLength < 2 || offset + segmentLength > raw.length) break;

      if (marker === 0xe1 && segmentLength >= 14) {
        const exifStart = offset + 2;
        const isExif = raw[exifStart] === 0x45 && raw[exifStart + 1] === 0x78
          && raw[exifStart + 2] === 0x69 && raw[exifStart + 3] === 0x66
          && raw[exifStart + 4] === 0x00 && raw[exifStart + 5] === 0x00;
        if (isExif) {
          const tiffStart = exifStart + 6;
          if (tiffStart + 8 > offset + segmentLength) return 1;
          const byteOrder = view.getUint16(tiffStart, false);
          const littleEndian = byteOrder === 0x4949;
          if (!littleEndian && byteOrder !== 0x4d4d) return 1;
          if (view.getUint16(tiffStart + 2, littleEndian) !== 42) return 1;
          const ifdOffset = view.getUint32(tiffStart + 4, littleEndian);
          const ifdStart = tiffStart + ifdOffset;
          if (ifdStart + 2 > offset + segmentLength) return 1;
          const entryCount = view.getUint16(ifdStart, littleEndian);
          for (let i = 0; i < entryCount; i++) {
            const entry = ifdStart + 2 + i * 12;
            if (entry + 12 > offset + segmentLength) break;
            if (view.getUint16(entry, littleEndian) !== 0x0112) continue;
            const type = view.getUint16(entry + 2, littleEndian);
            const count = view.getUint32(entry + 4, littleEndian);
            if (type !== 3 || count < 1) return 1;
            const orientation = view.getUint16(entry + 8, littleEndian);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }
      }
      offset += segmentLength;
    }
    return 1;
  }

  function loadImageFromBlob(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Bild konnte vom Browser nicht dekodiert werden'));
      };
      img.src = url;
    });
  }

  function canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Der Browser konnte keine Druckversion des Bildes erzeugen'));
        }, mimeType, quality);
      } catch (error) {
        reject(error);
      }
    });
  }

  async function normalizeRasterImage(file, fileType, originalBytes) {
    const original = originalBytes instanceof Uint8Array
      ? originalBytes
      : new Uint8Array(originalBytes);
    const originalMime = file.type || (fileType === 'jpg' ? 'image/jpeg' : 'image/png');
    const decoded = await loadImageFromBlob(new Blob([original], { type: originalMime }));
    if (!decoded.naturalWidth || !decoded.naturalHeight) {
      throw new Error('Das Bild hat keine lesbaren Abmessungen');
    }

    const orientation = fileType === 'jpg' ? readJpegExifOrientation(original) : 1;
    // JPEG ohne Rotation/Spiegelung unveraendert lassen: keine Generations-
    // verluste und keine groessere PDF. Die Bytes benoetigen keine EXIF-
    // Korrektur und sind damit bereits eine gueltige gemeinsame Quelle.
    if (fileType === 'jpg' && orientation === 1) {
      return {
        bytes: original,
        type: 'jpg',
        previewSource: decoded,
        width: decoded.naturalWidth,
        height: decoded.naturalHeight,
        orientation,
      };
    }

    const canvas = document.createElement('canvas');
    canvas.width = decoded.naturalWidth;
    canvas.height = decoded.naturalHeight;
    const ctx = canvas.getContext('2d', { colorSpace: 'srgb' });
    if (!ctx) throw new Error('Canvas ist in diesem Browser nicht verfuegbar');
    // HTMLImageElement bildet alle acht EXIF-Orientierungen inklusive
    // Spiegelungen bereits so ab, wie sie in der Live-Vorschau sichtbar sind.
    ctx.drawImage(decoded, 0, 0, canvas.width, canvas.height);

    let exportBlob;
    let exportType;
    if (fileType === 'jpg') {
      exportType = 'jpg';
      exportBlob = await canvasToBlob(canvas, 'image/jpeg', 0.95);
      // Manche stark komprimierten Smartphone-JPEGs wachsen bei Quality 0.95
      // unverhaeltnismaessig. 0.92 bleibt hochwertig und begrenzt PDF-Groessen.
      const sizeLimit = Math.max(original.byteLength * 1.35, original.byteLength + 512 * 1024);
      if (exportBlob.size > sizeLimit) {
        const compactBlob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
        if (compactBlob.size < exportBlob.size) exportBlob = compactBlob;
      }
    } else {
      exportType = 'png';
      exportBlob = await canvasToBlob(canvas, 'image/png');
    }

    const exportBytes = new Uint8Array(await exportBlob.arrayBuffer());
    const previewSource = await loadImageFromBlob(exportBlob);
    return {
      bytes: exportBytes,
      type: exportType,
      previewSource,
      width: previewSource.naturalWidth,
      height: previewSource.naturalHeight,
      orientation,
    };
  }

  function normalizePdfRotation(angle) {
    const normalized = ((Number(angle) || 0) % 360 + 360) % 360;
    if (![0, 90, 180, 270].includes(normalized)) {
      throw new Error(`Nicht unterstuetzte PDF-Seitenrotation: ${normalized} Grad`);
    }
    return normalized;
  }

  // PDF-Viewer wenden /Rotate erst bei der Anzeige an. pdf-lib.embedPdf()
  // bettet dagegen nur den unrotierten Seiteninhalt ein. Deshalb wird die
  // sichtbare erste Seite einmal vektorbasiert in eine rotationsfreie Seite
  // mit Ursprung (0, 0) ueberfuehrt. CropBox, Vektoren, Text, Transparenzen
  // und eingebettete Bilder bleiben dabei erhalten; state.fileBytes bleibt
  // weiterhin die unveraenderte Originaldatei fuer Upload und Re-Edit.
  async function normalizePdfPageForExport(originalBytes) {
    const PDFLib = window.PDFLib;
    if (!PDFLib) throw new Error('PDF-Bibliothek nicht geladen');
    const { PDFDocument, degrees } = PDFLib;
    const original = originalBytes instanceof Uint8Array
      ? originalBytes
      : new Uint8Array(originalBytes);
    const sourcePdf = await PDFDocument.load(original);
    if (sourcePdf.getPageCount() < 1) throw new Error('PDF enthaelt keine Seite');

    const sourcePage = sourcePdf.getPage(0);
    const rotation = normalizePdfRotation(sourcePage.getRotation().angle);
    const crop = sourcePage.getCropBox();
    if (!(crop.width > 0) || !(crop.height > 0)) {
      throw new Error('PDF-Seite hat keine gueltige CropBox');
    }

    const normalizedPdf = await PDFDocument.create();
    const embeddedPage = await normalizedPdf.embedPage(sourcePage, {
      left: crop.x,
      bottom: crop.y,
      right: crop.x + crop.width,
      top: crop.y + crop.height,
    });
    const rawW = embeddedPage.width;
    const rawH = embeddedPage.height;
    const swapsAxes = rotation === 90 || rotation === 270;
    const visibleW = swapsAxes ? rawH : rawW;
    const visibleH = swapsAxes ? rawW : rawH;
    const page = normalizedPdf.addPage([visibleW, visibleH]);

    // PDF /Rotate ist im Uhrzeigersinn definiert, die pdf-lib-Matrix arbeitet
    // mathematisch gegen den Uhrzeigersinn. Translation haelt die rotierte
    // Bounding-Box jeweils im positiven Seitenbereich.
    const ccwRotation = (360 - rotation) % 360;
    let x = 0;
    let y = 0;
    if (ccwRotation === 90) x = rawH;
    else if (ccwRotation === 180) { x = rawW; y = rawH; }
    else if (ccwRotation === 270) y = rawW;
    page.drawPage(embeddedPage, {
      x,
      y,
      width: rawW,
      height: rawH,
      rotate: degrees(ccwRotation),
    });

    return {
      bytes: new Uint8Array(await normalizedPdf.save()),
      rotation,
      width: visibleW,
      height: visibleH,
    };
  }

  // ============================================================
  // Color-Debug-Hint
  // ============================================================
  function renderColorDebugHint() {
    const wrap = dom.colorDebugHint;
    if (!wrap) return;
    const info = state.colorDebug || {};
    wrap.className = `color-debug-hint tone-${info.tone || 'rgb'}`;
    wrap.textContent = `${info.badge || 'Manuell / RGB'} — ${info.text || ''}`;
  }

  function setColorDebugHint(info) {
    state.colorDebug = {
      badge: info?.badge || 'Manuell / RGB',
      text:  info?.text  || 'Direkt gesetzte Hintergrundfarbe; Export übernimmt denselben sichtbaren Wert.',
      tone:  info?.tone  || 'rgb',
    };
    renderColorDebugHint();
  }

  // ============================================================
  // Farb-Logik (Quelle Z.754–788)
  // ============================================================
  function applyColor(hex, options = {}) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    const normalizedHex = hex.toLowerCase();
    const cmyk = Array.isArray(options.cmyk) ? options.cmyk.map(clamp01) : null;
    state.bgColor = normalizedHex;
    state.bgColorCmyk = cmyk;
    setColorDebugHint(options.debug);
    if (dom.colorSwatch) dom.colorSwatch.style.background = normalizedHex;
    if (dom.colorPicker) dom.colorPicker.value = normalizedHex;
    if (dom.hexInput)    dom.hexInput.value = normalizedHex.replace('#', '');
    setCmykFields(cmyk);

    // Auto-Frame-Wahl: Luminanz des neuen Hintergrunds bestimmt
    // Schwarz/Weiß-Frame. setVariant invalidiert den Frame-Cache.
    const autoVariant = chooseFrameVariantForHex(normalizedHex);
    if (autoVariant !== state.variant) {
      setVariant(autoVariant);
    } else {
      updatePreview();
    }
  }

  function applyCmykColor(cmyk, options = {}) {
    const normalized = (cmyk || [0, 0, 0, 0]).map(clamp01);
    const [r, g, b] = swopForward(...normalized);
    applyColor(rgbToHex(r, g, b), {
      cmyk: normalized,
      debug: options.debug || {
        badge: 'Manuell / CMYK',
        text:  'Hintergrundfarbe stammt aus den CMYK-Feldern und wird auch so exportiert.',
        tone:  'cmyk',
      },
    });
  }

  function onColorPicker(val) {
    applyColor(val);
  }

  function onHexInput(val) {
    const hex = '#' + String(val || '').replace(/[^0-9a-fA-F]/g, '');
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) applyColor(hex);
  }

  function setCmykFields(cmyk) {
    if (!dom.cmykInputs || !dom.cmykInputs.length) return;
    const fmt = v => Math.round(v * 100);
    const [c, m, y, k] = cmyk || [0, 0, 0, 0];
    dom.cmykInputs.forEach(inp => {
      const which = inp.dataset.cmyk;
      if (which === 'c') inp.value = fmt(c);
      if (which === 'm') inp.value = fmt(m);
      if (which === 'y') inp.value = fmt(y);
      if (which === 'k') inp.value = fmt(k);
    });
  }

  function getCmykFieldValue(which) {
    if (!dom.cmykInputs) return 0;
    for (const inp of dom.cmykInputs) {
      if (inp.dataset.cmyk === which) return parseFloat(inp.value || 0);
    }
    return 0;
  }

  function onCmykInput() {
    const c = clamp01(getCmykFieldValue('c') / 100);
    const m = clamp01(getCmykFieldValue('m') / 100);
    const y = clamp01(getCmykFieldValue('y') / 100);
    const k = clamp01(getCmykFieldValue('k') / 100);
    applyCmykColor([c, m, y, k], {
      debug: {
        badge: 'Manuell / CMYK',
        text:  'Hintergrundfarbe stammt aus den CMYK-Feldern und wird auch so exportiert.',
        tone:  'cmyk',
      },
    });
  }

  // ============================================================
  // SWOP-Forward / Inverse / FindBest (Quelle Z.1127–1199)
  // Identisch zu pdf.js DeviceCmykCS-Koeffizienten v3.11.174.
  // ============================================================
  function swopForward(c, m, y, k) {
    return [
      255 +
        c * (-4.387332384609988 * c + 54.48615194189176 * m + 18.82290502165302 * y + 212.25662451639585 * k - 285.2331026137004) +
        m * (1.7149763477362134 * m - 5.6096736904047315 * y - 17.873870861415444 * k - 5.497006427196366) +
        y * (-2.5217340131683033 * y - 21.248923337353073 * k + 17.5119270841813) +
        k * (-21.86122147463605 * k - 189.48180835922747),
      255 +
        c * (8.841041422036149 * c + 60.118027045597366 * m + 6.871425592049007 * y + 31.159100130055922 * k - 79.2970844816548) +
        m * (-15.310361306967817 * m + 17.575251261109482 * y + 131.35250912493976 * k - 190.9453302588951) +
        y * (4.444339102852739 * y + 9.8632861493405 * k - 24.86741582555878) +
        k * (-20.737325471181034 * k - 187.80453709719578),
      255 +
        c * (0.8842522430003296 * c + 8.078677503112928 * m + 30.89978309703729 * y - 0.23883238689178934 * k - 14.183576799673286) +
        m * (10.49593273432072 * m + 63.02378494754052 * y + 50.606957656360734 * k - 112.23884253719248) +
        y * (0.03296041114873217 * y + 115.60384449646641 * k - 193.58209356861505) +
        k * (-22.33816807309886 * k - 180.12613974708367)
    ];
  }

  function swopInverse(tR, tG, tB) {
    const cl = v => Math.max(0, Math.min(1, v));
    const r = tR / 255, g = tG / 255, b = tB / 255;
    const kn = 1 - Math.max(r, g, b);
    const dn = kn < 1 ? 1 - kn : 1;
    let c = cl(kn < 1 ? (1 - r - kn) / dn : 0);
    let m = cl(kn < 1 ? (1 - g - kn) / dn : 0);
    let y = cl(kn < 1 ? (1 - b - kn) / dn : 0);
    let k = cl(kn);

    const lr = 0.08, eps = 1e-4;
    for (let i = 0; i < 400; i++) {
      const [R, G, B] = swopForward(c, m, y, k);
      const eR = (R - tR) / 255, eG = (G - tG) / 255, eB = (B - tB) / 255;
      if (eR * eR + eG * eG + eB * eB < 1e-9) break;
      const n = eps * 255;
      const [Rc, Gc, Bc] = swopForward(c + eps, m, y, k);
      const [Rm, Gm, Bm] = swopForward(c, m + eps, y, k);
      const [Ry, Gy, By] = swopForward(c, m, y + eps, k);
      const [Rk, Gk, Bk] = swopForward(c, m, y, k + eps);
      c = cl(c - lr * ((Rc - R) * eR + (Gc - G) * eG + (Bc - B) * eB) / n);
      m = cl(m - lr * ((Rm - R) * eR + (Gm - G) * eG + (Bm - B) * eB) / n);
      y = cl(y - lr * ((Ry - R) * eR + (Gy - G) * eG + (By - B) * eB) / n);
      k = cl(k - lr * ((Rk - R) * eR + (Gk - G) * eG + (Bk - B) * eB) / n);
    }
    return [c, m, y, k];
  }

  function findBestCmyk(r, g, b) {
    if (state.pdfCmykPalette && state.pdfCmykPalette.length > 0) {
      let best = null, bestDist = Infinity, secondBestDist = Infinity;
      for (const cmyk of state.pdfCmykPalette) {
        const [rp, gp, bp] = swopForward(...cmyk);
        const d = (rp - r) ** 2 + (gp - g) ** 2 + (bp - b) ** 2;
        if (d < bestDist) {
          secondBestDist = bestDist;
          bestDist = d;
          best = cmyk;
        } else if (d < secondBestDist) {
          secondBestDist = d;
        }
      }
      const ambiguous = Math.abs(secondBestDist - bestDist) < 1;
      return { cmyk: best, exact: bestDist < 36 && !ambiguous, dist: bestDist, source: 'palette', ambiguous };
    }
    return { cmyk: swopInverse(r, g, b), exact: false, dist: Infinity, source: 'inverse', ambiguous: false };
  }

  function bgPdfColor(PDFLib, r, g, b) {
    if (state.bgColorCmyk) {
      const [c, m, y, k] = state.bgColorCmyk;
      return PDFLib.cmyk(c, m, y, k);
    }
    return PDFLib.rgb(clamp01(r), clamp01(g), clamp01(b));
  }

  // ============================================================
  // Canvas drag&drop / Upload
  // ============================================================
  function setupCanvasDrop() {
    const wrap = dom.previewWrap;
    if (!wrap) return;
    wrap.addEventListener('dragover', e => {
      e.preventDefault();
      e.stopPropagation();
      wrap.classList.add('drag-active');
    });
    wrap.addEventListener('dragleave', e => {
      if (!wrap.contains(e.relatedTarget)) wrap.classList.remove('drag-active');
    });
    wrap.addEventListener('drop', e => {
      e.preventDefault();
      e.stopPropagation();
      wrap.classList.remove('drag-active');
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });
  }

  // ============================================================
  // Akkordeon-Toggle (Text-Panel)
  // ------------------------------------------------------------
  // Trigger ist ein <button aria-expanded>; Body ein Container
  // mit data-koebi-accordion-body. CSS animiert max-height.
  // ============================================================
  function setupAccordion() {
    const triggers = dom.root.querySelectorAll('[data-koebi-accordion-trigger]');
    triggers.forEach(trigger => {
      const accordion = trigger.closest('[data-koebi-accordion]');
      const body = accordion && accordion.querySelector('[data-koebi-accordion-body]');
      if (!body) return;
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const next = !expanded;
        trigger.setAttribute('aria-expanded', String(next));
        body.dataset.open = String(next);
      });
    });
  }

  function setupUpload() {
    const zone = dom.uploadZone;
    const inp  = dom.fileInput;
    if (!zone || !inp) return;

    // State-aware Click: wenn Datei hochgeladen ist (`.has-file`),
    // entfernt der Klick die Datei (gleiche UX wie Tim wünscht:
    // „zum Entfernen hier klicken"). Sonst öffnet er den File-Picker.
    function activateZone() {
      if (zone.classList.contains('has-file')) {
        resetAll();
      } else {
        inp.value = '';
        inp.click();
      }
    }
    zone.addEventListener('click', activateZone);
    zone.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateZone();
      }
    });
    zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('dragover'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
    zone.addEventListener('drop', e => {
      e.preventDefault();
      zone.classList.remove('dragover');
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) handleFile(f);
    });
    inp.addEventListener('change', () => { if (inp.files && inp.files[0]) handleFile(inp.files[0]); });
  }

  // Detect file type from mime + extension
  function getFileType(file) {
    const m = (file.type || '').toLowerCase();
    const n = (file.name || '').toLowerCase();
    if (m === 'application/pdf' || n.endsWith('.pdf'))   return 'pdf';
    if (m === 'image/png'       || n.endsWith('.png'))   return 'png';
    if (m === 'image/jpeg'      || n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'jpg';
    if (m.startsWith('image/'))                           return 'image';
    return null;
  }

  // ============================================================
  // PDF-CMYK-Stream-Extract (Quelle Z.833–878)
  // ============================================================
  function collectCmykMatchesFromText(text, seen, out) {
    if (!text) return;
    const pdfNum = '([+-]?(?:\\d*\\.\\d+|\\d+\\.\\d*|\\d+))';
    const re = new RegExp(`${pdfNum}\\s+${pdfNum}\\s+${pdfNum}\\s+${pdfNum}\\s+[kK](?:\\s|$)`, 'g');
    let rm;
    while ((rm = re.exec(text)) !== null) {
      const a = [parseFloat(rm[1]), parseFloat(rm[2]), parseFloat(rm[3]), parseFloat(rm[4])];
      if (!a.every(v => Number.isFinite(v) && v >= 0 && v <= 1)) continue;
      const key = a.map(v => v.toFixed(4)).join('_');
      if (!seen.has(key)) { seen.add(key); out.push(a); }
    }
  }

  async function inflatePdfStream(streamBytes) {
    if (typeof DecompressionStream === 'undefined') throw new Error('DecompressionStream nicht verfuegbar');
    for (const trim of [0, 1, 2]) {
      if (streamBytes.length <= trim) continue;
      try {
        const src = trim ? streamBytes.subarray(0, streamBytes.length - trim) : streamBytes;
        const ds = new DecompressionStream('deflate');
        const inflated = await new Response(new Blob([src]).stream().pipeThrough(ds)).arrayBuffer();
        return new Uint8Array(inflated);
      } catch (e) { /* nächster Versuch */ }
    }
    throw new Error('Flate-Stream konnte nicht dekomprimiert werden');
  }

  async function collectCmykMatchesFromDecodedStreams(bytes, seen, out) {
    if (typeof DecompressionStream === 'undefined') return;
    const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const latin1 = new TextDecoder('latin1').decode(raw);
    const streamRe = /(\d+)\s+(\d+)\s+obj([\s\S]*?)stream\r?\n/g;
    let sm;
    while ((sm = streamRe.exec(latin1)) !== null) {
      const header = sm[3] || '';
      if (!/\/FlateDecode\b/.test(header)) continue;
      const streamStart = sm.index + sm[0].length;
      const streamEnd = latin1.indexOf('endstream', streamStart);
      if (streamEnd === -1 || streamEnd <= streamStart) continue;
      try {
        const inflated = await inflatePdfStream(raw.subarray(streamStart, streamEnd));
        collectCmykMatchesFromText(new TextDecoder('latin1').decode(inflated), seen, out);
      } catch (e) { /* pdf.js Operator-API bleibt Primärquelle */ }
      streamRe.lastIndex = streamEnd;
    }
  }

  // ============================================================
  // File-Handling (Quelle Z.880–955) + 20-MB-Größenlimit
  // ============================================================
  async function handleFile(file, options = {}) {
    if (!file) return;

    const shouldApplyPdfPaletteColor = options.applyPdfPaletteColor !== false;
    const shouldResetTransform = options.resetTransform !== false;

    if (file.size > MAX_FILE_BYTES) {
      showStatus('Datei größer als 20 MB — bitte komprimiere die Datei vor dem Upload.', 'error');
      return;
    }

    const ftype = getFileType(file);
    if (!ftype) {
      showStatus('Format nicht unterstützt. Bitte PDF, PNG, JPG, TIFF, WebP oder BMP.', 'error');
      return;
    }

    const bytes = await file.arrayBuffer();
    try {
      // PDF-Seiten und Rasterbilder vor dem State-Commit normalisieren.
      // Schlaegt die Aufbereitung fehl, bleibt ein zuvor geladenes
      // Kundendesign intakt und falsch ausgerichtete Originalbytes werden
      // niemals als Export-Fallback verwendet. Re-Edit-Hydrierung laeuft
      // ueber denselben handleFile-Pfad.
      let normalizedPdf = null;
      let normalizedRaster = null;
      if (ftype === 'pdf') {
        try {
          normalizedPdf = await normalizePdfPageForExport(bytes);
        } catch (error) {
          const markedError = error instanceof Error ? error : new Error(String(error || 'PDF-Normalisierung fehlgeschlagen'));
          markedError.isPdfNormalizationError = true;
          throw markedError;
        }
      } else {
        try {
          normalizedRaster = await normalizeRasterImage(file, ftype, bytes);
        } catch (error) {
          const markedError = error instanceof Error ? error : new Error(String(error || 'Bildnormalisierung fehlgeschlagen'));
          markedError.isRasterNormalizationError = true;
          throw markedError;
        }
      }

      state.fileBytes        = bytes;
      state.fileName         = file.name;
      state.fileType         = ftype;
      state.pdfExportBytes   = normalizedPdf ? normalizedPdf.bytes : null;
      state.rasterExportBytes = normalizedRaster ? normalizedRaster.bytes : null;
      state.rasterExportType  = normalizedRaster ? normalizedRaster.type : '';
      state.imageDataUrl     = null;
      // Session C — wenn ein User im Re-Edit-Flow eine neue Quelldatei
      // hochlaedt, markieren wir den State so dass der naechste Save
      // tatsaechlich neue Bytes ans Plugin schickt (statt nur die alte
      // GID „behalten"). Beim Initial-Hydrate ueber consumeReeditFromUrl
      // → hydrateFileFromCdn ist state.reedit zu dem Zeitpunkt noch
      // null, deshalb gibts hier keinen False-Positive — die Reihenfolge
      // ist garantiert: erst handleFile (Hydrate), dann state.reedit zu
      // setzen.
      if (state.reedit) {
        state.reedit.hasUploadedNewSource = true;
        state.reedit.quelldateiMissing = false;
      }
      resetDesignPreviewCache();
      resetFramePreviewCache();

      if (ftype === 'pdf') {
        if (!window.pdfjsLib) throw new Error('pdf.js nicht geladen');
        // Auch die Live-Vorschau liest die normalisierte, rotationsfreie
        // Seite. Damit sind Preview und alle Exportvarianten garantiert auf
        // derselben Seitengeometrie.
        const pdf  = await window.pdfjsLib.getDocument({ data: normalizedPdf.bytes.slice() }).promise;
        const page = await pdf.getPage(1);
        const vp   = page.getViewport({ scale: 1 });
        state.imageW = vp.width  / MM2PT;
        state.imageH = vp.height / MM2PT;

        // CMYK-Palette extrahieren
        try {
          const ops  = await page.getOperatorList();
          const OPS  = window.pdfjsLib.OPS;
          const pal  = [], seen = new Set();
          for (let i = 0; i < ops.fnArray.length; i++) {
            const fn = ops.fnArray[i], args = ops.argsArray[i];
            if ((fn === OPS.setFillCMYKColor || fn === OPS.setStrokeCMYKColor) && args && args.length === 4) {
              const key = args.map(v => v.toFixed(4)).join('_');
              if (!seen.has(key)) { seen.add(key); pal.push([...args]); }
            }
          }
          try {
            collectCmykMatchesFromText(new TextDecoder('latin1').decode(state.fileBytes), seen, pal);
            await collectCmykMatchesFromDecodedStreams(new Uint8Array(state.fileBytes), seen, pal);
          } catch (er) { /* ignore */ }
          state.pdfCmykPalette = pal.length > 0 ? pal : null;
        } catch (e) { state.pdfCmykPalette = null; }

        // Rasterise ~300 DPI für Vorschau
        const rasterScale = (300 / 25.4) / (72 / 25.4);
        const rasterVp    = page.getViewport({ scale: rasterScale });
        const rasterC     = document.createElement('canvas');
        rasterC.width     = Math.round(rasterVp.width);
        rasterC.height    = Math.round(rasterVp.height);
        await page.render({ canvasContext: rasterC.getContext('2d'), viewport: rasterVp, background: 'transparent' }).promise;
        state.designPreviewSource = rasterC;

        // BG-Farbe automatisch aus CMYK-Palette setzen, wenn vorhanden
        if (state.pdfCmykPalette && state.pdfCmykPalette.length > 0) {
          // hellster Wert (geringster K)
          let best = state.pdfCmykPalette[0];
          for (const c of state.pdfCmykPalette) {
            if (c[3] < best[3]) best = c;
          }
          // Nur setzen wenn nicht zu dunkel. Beim Re-Edit-Hydrate darf die
          // Quelldatei den gespeicherten Editor-State nicht erneut ueberschreiben.
          if (shouldApplyPdfPaletteColor && best[3] < 0.5) {
            const [r, g, b] = swopForward(...best);
            applyColor(rgbToHex(r, g, b), {
              cmyk: best,
              debug: {
                badge: 'PDF / CMYK-Palette',
                text:  'Hintergrundfarbe aus dem hellsten CMYK-Wert des Kunden-PDFs übernommen.',
                tone:  'cmyk',
              },
            });
          }
        }

      } else {
        // Live-Vorschau und alle PDF-Varianten lesen dieselbe normalisierte
        // Pixelquelle. state.fileBytes bleibt davon getrennt das Original.
        state.designPreviewSource = normalizedRaster.previewSource;
        state.imageW = normalizedRaster.width;
        state.imageH = normalizedRaster.height;
      }

      // Fileinfo-Anzeige
      if (dom.uploadZone) dom.uploadZone.classList.add('has-file');
      if (dom.fileinfo)   dom.fileinfo.hidden = false;
      if (dom.filename)   dom.filename.textContent = file.name;

      // Phase 3 — bei jedem echten Upload Transform-Defaults wiederherstellen
      // + Controls einblenden. Re-Edit-Hydrate bewahrt dagegen den gespeicherten
      // Kunden-Transform aus _Editor-State.
      if (shouldResetTransform) {
        resetDesignTransform({ silent: true });
      }
      updateTransformUiVisibility();

      updatePreview();
      updateButton();
    } catch (e) {
      showStatus(
        e && e.isRasterNormalizationError
          ? 'Bild konnte nicht druckfertig ausgerichtet werden. Bitte speichere es neu als JPG oder PNG und lade es erneut hoch.'
          : e && e.isPdfNormalizationError
            ? 'PDF konnte nicht druckfertig ausgerichtet werden. Bitte speichere es neu oder lade stattdessen ein JPG oder PNG hoch.'
            : 'Datei konnte nicht gelesen werden.',
        'error'
      );
      console.error(e);
    }
  }

  // ============================================================
  // ICC-Profil + PDF/X-3-Metadata (Quelle Z.1212–1264)
  // Asset-Pfad → state.assets.iccProfileUrl
  // ============================================================
  async function loadOutputProfileBytes() {
    if (_outputProfileBytes !== undefined) return _outputProfileBytes;
    try {
      const url = state.assets && state.assets.iccProfileUrl;
      if (!url) throw new Error('ICC-URL fehlt');
      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      _outputProfileBytes = new Uint8Array(await response.arrayBuffer());
    } catch (e) {
      console.warn('ICC output profile load failed', e);
      _outputProfileBytes = null;
    }
    return _outputProfileBytes;
  }

  function applyPrintPageBoxes(page, pageW, pageH, oX, oY, labelW, labelH) {
    page.setMediaBox(0, 0, pageW, pageH);
    if (typeof page.setCropBox === 'function') page.setCropBox(0, 0, pageW, pageH);
    page.setTrimBox(oX, oY, labelW, labelH);
    page.setBleedBox(0, 0, pageW, pageH);
    if (typeof page.setArtBox === 'function') page.setArtBox(oX, oY, labelW, labelH);
  }

  function attachPdfxMetadata(outPdf, profileBytes) {
    const PDFLib = window.PDFLib;
    if (!PDFLib) return;
    const { PDFName, PDFString } = PDFLib;
    const ctx     = outPdf.context;
    const catalog = outPdf.catalog;

    catalog.set(PDFName.of('GTS_PDFXVersion'), PDFString.of('PDF/X-3:2002'));
    outPdf.getInfoDict().set(PDFName.of('Trapped'), PDFName.of('False'));

    const oi = ctx.obj({});
    oi.set(PDFName.of('Type'),                      PDFName.of('OutputIntent'));
    oi.set(PDFName.of('S'),                         PDFName.of('GTS_PDFX'));
    oi.set(PDFName.of('OutputConditionIdentifier'), PDFString.of('ISO Coated v2 (ECI)'));
    oi.set(PDFName.of('RegistryName'),              PDFString.of('http://www.color.org'));
    oi.set(PDFName.of('OutputCondition'),           PDFString.of('ISO Coated v2 (ECI)'));
    oi.set(PDFName.of('Info'),                      PDFString.of('ISO Coated v2 (ECI)'));

    if (profileBytes && profileBytes.length) {
      const profileStream = ctx.flateStream(profileBytes, {
        N: 4,
        Alternate: PDFName.of('DeviceCMYK'),
        Range: [0, 1, 0, 1, 0, 1, 0, 1],
      });
      const profileRef = ctx.register(profileStream);
      oi.set(PDFName.of('DestOutputProfile'), profileRef);
    }

    const oiRef = ctx.register(oi);
    catalog.set(PDFName.of('OutputIntents'), ctx.obj([oiRef]));
  }

  // ============================================================
  // Bricolage-Font laden — Asset-Pfad → state.assets.bricolageFontUrl
  // ============================================================
  async function loadBricolageFontBytes() {
    if (_bricolageFont) return _bricolageFont;
    try {
      const url = state.assets && state.assets.bricolageFontUrl;
      if (!url) throw new Error('Bricolage-URL fehlt');
      const r = await fetch(url, { cache: 'force-cache' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      _bricolageFont = new Uint8Array(await r.arrayBuffer());
    } catch (e) {
      console.warn('Bricolage font load failed', e);
      _bricolageFont = null;
    }
    return _bricolageFont;
  }

  // ============================================================
  // Google-Font-TTF-Loader für PDF-Embedding (Phase 2)
  // ------------------------------------------------------------
  // Google Fonts CSS-API liefert WOFF2 — pdf-lib/fontkit braucht TTF.
  // Workaround: TTF-Mirror auf jsDelivr (raw github → google/fonts).
  // Variable-Fonts mit `[wght]` müssen URL-encoded werden (%5Bwght%5D).
  // Cache via state.customFontCache (per Font-Name).
  // ============================================================
  async function loadGoogleFontBytes(fontFamily, styleLabel) {
    const family = fontFamily || 'Bricolage Grotesque';
    const variant = getVariantByLabel(family, styleLabel || 'Regular');
    const variantLabel = variant ? variant.label : 'Regular';
    const cacheKey = `${family}|${variantLabel}`;
    if (state.customFontCache[cacheKey]) return state.customFontCache[cacheKey];

    // Sentinel: ttf=null → lokale Bricolage-TTF aus Theme-Assets (Variable-Font).
    // Das deckt Bricolage Regular ab, gibt aber für andere Bricolage-Schnitte
    // weiterhin die richtige Static-TTF aus jsDelivr zurück.
    if (!variant || variant.ttf === null) {
      const bytes = await loadBricolageFontBytes();
      if (bytes) state.customFontCache[cacheKey] = bytes;
      return bytes;
    }
    // ──────────────────────────────────────────────────────────────
    // Fallback-Kette pro Versuch:
    //   - Theme-Asset (state.fontsMap) wenn vorhanden — primär,
    //     keine externe CDN-Abhängigkeit
    //   - externe CDN-URL als Fallback
    // Reihenfolge der Versuche:
    //   1. gewünschte Variante (z. B. Playfair Bold Italic)
    //   2. gleicher Weight ohne Italic (Playfair Bold)
    //   3. Regular der Family
    //   4. Bricolage Regular (last resort)
    // ──────────────────────────────────────────────────────────────
    const fontsMap = state.fontsMap || {};
    const buildAttempt = (variantObj, label) => {
      const key = `${family}|${variantObj.label}`;
      const themeUrl = fontsMap[key];
      const urls = [];
      if (themeUrl) urls.push(themeUrl);
      if (variantObj.ttf) urls.push(variantObj.ttf);
      return urls.length ? { urls, label } : null;
    };

    const tryUrls = [];
    const primary = buildAttempt(variant, variantLabel);
    if (primary) tryUrls.push(primary);

    if (variant.italic) {
      const sameWeightRoman = getFontVariants(family).find(v => v.weight === variant.weight && !v.italic);
      if (sameWeightRoman) {
        const a = buildAttempt(sameWeightRoman, sameWeightRoman.label);
        if (a) tryUrls.push(a);
      }
    }

    const regular = getFontVariants(family).find(v => v.label === 'Regular');
    if (regular) {
      const a = buildAttempt(regular, 'Regular');
      if (a && !tryUrls.some(t => t.label === 'Regular')) tryUrls.push(a);
    }

    for (const attempt of tryUrls) {
      for (const url of attempt.urls) {
        try {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 5000);
          const r = await fetch(url, { cache: 'force-cache', signal: ctrl.signal });
          clearTimeout(timer);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const bytes = new Uint8Array(await r.arrayBuffer());
          if (attempt.label !== variantLabel) {
            console.warn(`[koebi-label] Font-Fallback für ${family} ${variantLabel} → ${attempt.label}`);
          }
          state.customFontCache[cacheKey] = bytes;
          return bytes;
        } catch (e) {
          console.warn(`[koebi-label] Font-Load fehlgeschlagen: ${family} ${attempt.label} (${url})`, e.message || e);
        }
      }
    }

    console.warn(`[koebi-label] Alle Font-URLs für ${family} fehlgeschlagen — Fallback auf Bricolage`);
    return await loadBricolageFontBytes();
  }

  // ============================================================
  // Phase 3 — PDF-Clipping-Helper
  // ------------------------------------------------------------
  // pdf-lib bietet Operator-Helper als Top-Level-Funktionen. Wir
  // pushen einen Graphics-State, definieren einen Rectangle-Path
  // und nutzen den `W n`-Operator (clip + endPath), damit
  // anschließende Drawing-Operationen auf das Rechteck beschränkt
  // sind. `popClipDesign` schließt den Graphics-State wieder.
  // ============================================================
  function pushClipDesign(page, x, y, w, h) {
    const PDFLib = window.PDFLib;
    if (!PDFLib) return;
    page.pushOperators(
      PDFLib.pushGraphicsState(),
      PDFLib.moveTo(x, y),
      PDFLib.lineTo(x + w, y),
      PDFLib.lineTo(x + w, y + h),
      PDFLib.lineTo(x, y + h),
      PDFLib.closePath(),
      PDFLib.clip(),
      PDFLib.endPath(),
    );
  }
  function popClipDesign(page) {
    const PDFLib = window.PDFLib;
    if (!PDFLib) return;
    page.pushOperators(PDFLib.popGraphicsState());
  }

  // ============================================================
  // Text-to-Outlines — vektorisierte Schriften für CMYK-Druck
  // ------------------------------------------------------------
  // Wandelt Custom-Text und MHD/Charge in Vektor-Pfade um, statt
  // Font-Subset einzubetten. Vorteile:
  // - keine Font-Streams im PDF → druckereifest gegen fehlende
  //   oder falsch installierte Schriften
  // - kein Hinting-/Subset-Drama bei Acrobat-Pre-Flight
  // - kein PDF/X-Embedding-Problem („Font nicht entnehmbar")
  //
  // Mechanik: fontkit.layout(text) liefert Glyph-Run mit Kerning,
  // glyph.path.commands liefert die Pfad-Befehle in Font-EM-Units.
  // PDF-Y und Font-EM-Y sind beide nach OBEN positiv → kein Flip.
  // TrueType-Quadratic-Curves werden in Cubic-Beziers konvertiert
  // (PDF kennt nur cubic).
  //
  // Coverage-Check: notdef-Glyphen (id===0) werden in
  // state.lastOutlineWarnings gesammelt — Caller prüft das und
  // entscheidet (warnen für Vorschau, blocken für Print).
  // ============================================================
  const _fontkitFontCache = new WeakMap();   // fontBytes (Uint8Array) → fontkit Font

  function _getFontkitFont(fontBytes) {
    if (!fontBytes || !window.fontkit) return null;
    // WeakMap-Cache: Solange dieselbe Uint8Array (state.customFontCache)
    // wiederverwendet wird, sparen wir fontkit.create() (5–30ms).
    const cached = _fontkitFontCache.get(fontBytes);
    if (cached) return cached;
    try {
      const f = window.fontkit.create(fontBytes);
      _fontkitFontCache.set(fontBytes, f);
      return f;
    } catch (e) {
      dwarn('fontkit.create failed:', (e && e.message) || e);
      return null;
    }
  }

  // Emittiert pdf-lib-Operatoren für einen einzelnen Glyph-Path.
  // Quadratic (TTF) → Cubic mit P0 + 2/3(C - P0), P + 2/3(C - P).
  // closePath setzt das aktuelle Pen auf den letzten moveTo-Punkt.
  function _emitGlyphOps(commands) {
    const PDFLib = window.PDFLib;
    const ops = [];
    let cx = 0, cy = 0, startX = 0, startY = 0;
    for (let i = 0; i < commands.length; i++) {
      const c = commands[i];
      const a = c.args;
      switch (c.command) {
        case 'moveTo':
          cx = a[0]; cy = a[1]; startX = cx; startY = cy;
          ops.push(PDFLib.moveTo(cx, cy));
          break;
        case 'lineTo':
          cx = a[0]; cy = a[1];
          ops.push(PDFLib.lineTo(cx, cy));
          break;
        case 'quadraticCurveTo': {
          const qcx = a[0], qcy = a[1], qx = a[2], qy = a[3];
          const c1x = cx + (2 / 3) * (qcx - cx);
          const c1y = cy + (2 / 3) * (qcy - cy);
          const c2x = qx + (2 / 3) * (qcx - qx);
          const c2y = qy + (2 / 3) * (qcy - qy);
          ops.push(PDFLib.appendBezierCurve(c1x, c1y, c2x, c2y, qx, qy));
          cx = qx; cy = qy;
          break;
        }
        case 'bezierCurveTo':
          ops.push(PDFLib.appendBezierCurve(a[0], a[1], a[2], a[3], a[4], a[5]));
          cx = a[4]; cy = a[5];
          break;
        case 'closePath':
          ops.push(PDFLib.closePath());
          cx = startX; cy = startY;
          break;
      }
    }
    return ops;
  }

  // Misst die Breite einer Textzeile (in Pt) mit fontkit-Layout.
  function _measureOutlineWidth(fkFont, text, fontSize) {
    if (!fkFont || !text) return 0;
    try {
      const run = fkFont.layout(text);
      const upm = fkFont.unitsPerEm || 1000;
      let adv = 0;
      for (let i = 0; i < run.positions.length; i++) adv += run.positions[i].xAdvance;
      return adv * (fontSize / upm);
    } catch (e) { return 0; }
  }

  // Zeichnet eine Textzeile als Glyph-Pfade auf `page`.
  // baselineY = PDF-Y der alphabetischen Baseline (Y wächst nach oben).
  // Returns: { width, missing[] }.  missing = {codepoint, char}-Liste
  // für nicht abgedeckte Zeichen (notdef-Glyph).
  function _drawTextAsOutlines(page, fkFont, text, x, baselineY, fontSize, color) {
    const PDFLib = window.PDFLib;
    const empty = { width: 0, missing: [] };
    if (!PDFLib || !fkFont || !text) return empty;

    let run;
    try { run = fkFont.layout(text); }
    catch (e) { dwarn('layout failed:', (e && e.message) || e); return empty; }

    const upm   = fkFont.unitsPerEm || 1000;
    const scale = fontSize / upm;

    // Coverage-Check.
    const missing = [];
    for (let i = 0; i < run.glyphs.length; i++) {
      const g = run.glyphs[i];
      if (g && g.id === 0 && g.codePoints && g.codePoints.length) {
        for (let k = 0; k < g.codePoints.length; k++) {
          const cp = g.codePoints[k];
          missing.push({ codepoint: cp, char: String.fromCodePoint(cp) });
        }
      }
    }

    // Total advance für width-Return (Caller braucht's fürs Centering).
    let totalAdvance = 0;
    for (let i = 0; i < run.positions.length; i++) totalAdvance += run.positions[i].xAdvance;
    const totalWidth = totalAdvance * scale;

    // Batch alle Operatoren in einem pushOperators-Call (weniger Overhead
    // als pro Glyph einzeln). Outer GState: setFillingColor; pro Glyph:
    // pushGS + cm-Transform + Path + fill + popGS.
    const opsBatch = [];
    opsBatch.push(PDFLib.pushGraphicsState());
    opsBatch.push(PDFLib.setFillingColor(color));

    let penX = x;
    for (let i = 0; i < run.glyphs.length; i++) {
      const glyph = run.glyphs[i];
      const pos   = run.positions[i];
      const xAdv  = (pos.xAdvance || 0) * scale;
      const xOff  = (pos.xOffset  || 0) * scale;
      const yOff  = (pos.yOffset  || 0) * scale;

      const commands = glyph && glyph.path && glyph.path.commands;
      if (!commands || commands.length === 0) {
        penX += xAdv;
        continue;
      }
      const gx = penX + xOff;
      const gy = baselineY + yOff;
      opsBatch.push(PDFLib.pushGraphicsState());
      // Matrix [scale 0 0 scale gx gy] — uniform scale + translate.
      opsBatch.push(PDFLib.concatTransformationMatrix(scale, 0, 0, scale, gx, gy));
      const glyphOps = _emitGlyphOps(commands);
      for (let k = 0; k < glyphOps.length; k++) opsBatch.push(glyphOps[k]);
      // Non-zero winding (PDF op `f`) — korrekt für TrueType und CFF.
      opsBatch.push(PDFLib.fill());
      opsBatch.push(PDFLib.popGraphicsState());

      penX += xAdv;
    }

    opsBatch.push(PDFLib.popGraphicsState());
    page.pushOperators.apply(page, opsBatch);

    return { width: totalWidth, missing };
  }

  // Coverage-Warnings: Generate-Flow leert vor jedem Build und
  // liest danach aus. Dedup nach Codepoint.
  function _resetOutlineWarnings() {
    state.lastOutlineWarnings = [];
  }
  function _registerOutlineWarnings(list) {
    if (!list || !list.length) return;
    if (!state.lastOutlineWarnings) state.lastOutlineWarnings = [];
    for (let i = 0; i < list.length; i++) state.lastOutlineWarnings.push(list[i]);
  }
  function _formatOutlineWarning() {
    const w = state.lastOutlineWarnings;
    if (!w || !w.length) return '';
    const seen = new Set();
    const uniq = [];
    for (let i = 0; i < w.length; i++) {
      if (seen.has(w[i].codepoint)) continue;
      seen.add(w[i].codepoint);
      uniq.push(w[i].char);
    }
    return 'Diese Zeichen werden von der gewählten Schrift nicht unterstützt: '
      + uniq.join(' ') + '. Bitte andere Schrift wählen oder Zeichen entfernen.';
  }
  // Status-Helper für Preview-Pfad: zeigt Success-Message, hängt aber
  // bei Coverage-Warnings eine Warning dran (non-blocking — Vorschau
  // bleibt nutzbar, damit Kunde sieht wie's wird).
  function _showPreviewStatusWithWarning(successMsg, successType) {
    if (state.lastOutlineWarnings && state.lastOutlineWarnings.length) {
      showStatus(_formatOutlineWarning(), 'error');
    } else {
      showStatus(successMsg, successType);
    }
  }

  // Hilfsfunktion: Custom-Text in eine PDF-Page zeichnen.
  // Erwartet bereits geöffnetes PDFDocument + Page sowie die
  // Etikett-Geometrie in PostScript-Pt. Registriert fontkit
  // einmalig auf outPdf.
  async function drawCustomTextOnPdfPage(outPdf, page, oX, oY, labelH) {
    const PDFLib = window.PDFLib;
    if (!PDFLib) return;
    const txt = state.customText || '';
    if (!txt.trim()) return;
    if (window.fontkit) {
      try { outPdf.registerFontkit(window.fontkit); } catch (e) { /* schon registriert */ }
    }
    // Phase 3.2: TTF abhängig von Family + Schnitt-ID
    const fontBytes = await loadGoogleFontBytes(state.customFont, state.customTextStyle);
    if (!fontBytes) return;

    const fontSize   = state.customTextSize || 32;
    const lines      = txt.split('\n');
    const lineHeight = fontSize * (state.customLineHeight || 1.2);

    // Center im Etikett (in Pt) + Phase-3-Text-Offset
    // Achtung: Screen-Y wächst nach unten, PDF-Y nach oben → Vorzeichen drehen.
    const designCenterX = oX + (DESIGN_X + DESIGN_W / 2 + state.textOffsetX) * MM2PT;
    const designCenterY = oY + (labelH / 2) - state.textOffsetY * MM2PT;
    // Pdf-lib zeichnet mit alphabetischer Baseline. Damit der Text optisch um
    // designCenterY zentriert ist (entsprechend Canvas-`textBaseline=middle`),
    // muss die Baseline um die halbe EM-Box-Höhe nach unten gegenüber der
    // Glyph-Mitte versetzt liegen: Cap-Höhe ≈ 0.7·fontSize, Descender ≈ 0.2,
    // EM-Mitte über Baseline ≈ 0.3·fontSize.
    const MIDLINE_OFFSET = 0.30 * fontSize;
    // Top-Line-Baseline (PDF-Y wächst nach oben; Folgezeilen liegen tiefer)
    const firstBaselineY = designCenterY + ((lines.length - 1) * lineHeight) / 2 - MIDLINE_OFFSET;

    // Phase 3.2: Schriftfarbe aus State (Color-Picker)
    const _rgb = hexToRgb01(state.customTextColor);
    const textColor = PDFLib.rgb(_rgb.r, _rgb.g, _rgb.b);

    // Phase 3 — Clip auf Design-Bereich. Horizontal: 25–155 mm. Vertikal: volle
    // Page-Höhe inkl. Bleed (page.getHeight() = labelH + 2*bleed).
    const clipX = oX + DESIGN_X * MM2PT;
    const clipW = DESIGN_W * MM2PT;
    pushClipDesign(page, clipX, 0, clipW, page.getHeight());

    // ──────────────────────────────────────────────────────────
    // Primärpfad: Vektor-Outlines (CMYK-Druck-freundlich).
    // Statt der Font als Glyph-Map einzubetten, zeichnen wir die
    // Glyph-Pfade direkt mit fontkit + pdf-lib-Operatoren.
    // Vorteile: keine Font-Streams im PDF, druckereifest, keine
    // Acrobat-„Font nicht entnehmbar"-Warnings.
    // Coverage-Check zählt notdef-Glyphen und sammelt sie in
    // state.lastOutlineWarnings — Caller (generatePDF) zeigt das
    // dann als Status-Warnung an und blockt beim Print-Submit.
    // ──────────────────────────────────────────────────────────
    const fkFont = _getFontkitFont(fontBytes);
    if (fkFont) {
      for (let idx = 0; idx < lines.length; idx++) {
        const line = lines[idx];
        if (!line) continue;
        const width = _measureOutlineWidth(fkFont, line, fontSize);
        const x = designCenterX - width / 2;
        const y = firstBaselineY - idx * lineHeight;
        const res = _drawTextAsOutlines(page, fkFont, line, x, y, fontSize, textColor);
        _registerOutlineWarnings(res.missing);
      }
      popClipDesign(page);
      return;
    }

    // ──────────────────────────────────────────────────────────
    // Fallback: legacy drawText + embedFont. Wird nur erreicht
    // wenn fontkit nicht verfügbar oder fontkit.create fehlschlägt.
    // subset:false — Variable-Bricolage erzeugt sonst Acrobat-
    // „nicht entnehmbar"-Warnings.
    // ──────────────────────────────────────────────────────────
    let customFont;
    try {
      customFont = await outPdf.embedFont(fontBytes, { subset: false });
    } catch (e) {
      console.warn('[koebi-label] embedFont failed, fallback Helvetica:', e);
      customFont = await outPdf.embedFont(PDFLib.StandardFonts.Helvetica);
    }
    lines.forEach((line, idx) => {
      let lineWidth = 0;
      try { lineWidth = customFont.widthOfTextAtSize(line, fontSize); }
      catch (e) { lineWidth = 0; }
      page.drawText(line, {
        x: designCenterX - lineWidth / 2,
        y: firstBaselineY - idx * lineHeight,
        font: customFont,
        size: fontSize,
        color: textColor,
      });
    });

    popClipDesign(page);
  }

  // ============================================================
  // PDF-Bytes bauen — wiederverwendbarer Helper.
  // ------------------------------------------------------------
  // `withBleed=true`  → Druckdatei mit 2.5mm Anschnitt + PDF/X-3
  // `withBleed=false` → Vorschau ohne Bleed, mit OpenAction=/Fit
  // ============================================================
  // ============================================================
  // MHD/Charge oder weißes Etikettierer-Feld auf ein Single-Label zeichnen.
  // Wird sowohl von buildPdfBytes (Kunden-Druckdatei) als auch von
  // buildLabelPdfBytes (A4-Bogen-Etiketten) genutzt, damit die Logik
  // genau einmal definiert ist.
  //
  // - state.directPrintMhd === true → MHD + Charge in 6 pt direkt aufs
  //   Etikett, an der Position des ehemaligen weißen Feldes
  //   (Trim-Top-Left + 5/18 mm Baseline). Textfarbe nach Luminanz.
  // - state.directPrintMhd === false → weißes 20×14.5 mm Rechteck mit
  //   0.4 mm Eckenradius (Position: 4/13 mm Trim-Top-Left), aber nur
  //   wenn der Hintergrund dunkel ist. Bei hellem Hintergrund kann der
  //   Etikettierer eh direkt drucken — kein Feld nötig.
  //
  // Z-Order: nach allem anderen (Frame, Design, Custom-Text), damit's
  // immer obenauf liegt.
  // ============================================================
  async function drawMhdChargeOrField(outPdf, page, oX, oY, labelH, cr, cg, cb) {
    const PDFLib = window.PDFLib;
    const luminance = cr * 0.299 + cg * 0.587 + cb * 0.114;

    if (state.directPrintMhd) {
      const mhd    = (getAutoMhd()    || '').trim();
      const charge = (getAutoCharge() || '').trim();
      if (!mhd && !charge) return;

      const fontBytes = await loadBricolageFontBytes();
      const fontSize  = 6;
      // Tim's Maße (InDesign, top-of-cap gemessen): X=4 mm beide Zeilen,
      // MHD-Top Y=13 mm, Charge-Top Y=16 mm. Cap-Height bei 6 pt Bricolage
      // ≈ 1.5 mm — Baseline liegt entsprechend tiefer. PDF/pdf-lib drawText
      // erwartet Baseline-Y, also addieren wir die Cap-Height auf Tim's Top-Y.
      const textX           = oX + 4 * MM2PT;
      const mhdBaselineY    = oY + labelH - 14.5 * MM2PT;
      const chargeBaselineY = oY + labelH - 17.5 * MM2PT;
      const textColor       = luminance >= 0.5
        ? PDFLib.rgb(0, 0, 0)
        : PDFLib.rgb(1, 1, 1);

      const fkFont = _getFontkitFont(fontBytes);
      if (fkFont) {
        if (mhd) {
          const r = _drawTextAsOutlines(page, fkFont, mhd, textX, mhdBaselineY, fontSize, textColor);
          _registerOutlineWarnings(r.missing);
        }
        if (charge) {
          const r = _drawTextAsOutlines(page, fkFont, charge, textX, chargeBaselineY, fontSize, textColor);
          _registerOutlineWarnings(r.missing);
        }
      } else {
        const font = (fontBytes && window.fontkit)
          ? await outPdf.embedFont(fontBytes, { subset: false })
          : await outPdf.embedFont(PDFLib.StandardFonts.Helvetica);
        if (mhd)    page.drawText(mhd,    { x: textX, y: mhdBaselineY,    font, size: fontSize, color: textColor });
        if (charge) page.drawText(charge, { x: textX, y: chargeBaselineY, font, size: fontSize, color: textColor });
      }
      return;
    }

    // Etikettierer-Modus: weißes Feld bei dunklem Hintergrund
    if (luminance >= 0.5) return;

    // Storefront-Position: X=4 mm / Y=13 mm vom Trim oben-links, 18×16 mm,
    // Eckenradius 0.4 mm — von Tim 2026-05-22 visuell verifiziert.
    const fieldX  = oX + 4 * MM2PT;
    const fieldYT = oY + labelH - 13 * MM2PT;
    const w = 18 * MM2PT;
    const h = 16 * MM2PT;
    const r = 0.4 * MM2PT;
    const path =
      `M ${r} 0 ` +
      `L ${w - r} 0 ` +
      `A ${r} ${r} 0 0 1 ${w} ${r} ` +
      `L ${w} ${h - r} ` +
      `A ${r} ${r} 0 0 1 ${w - r} ${h} ` +
      `L ${r} ${h} ` +
      `A ${r} ${r} 0 0 1 0 ${h - r} ` +
      `L 0 ${r} ` +
      `A ${r} ${r} 0 0 1 ${r} 0 ` +
      `Z`;
    page.drawSvgPath(path, {
      x: fieldX,
      y: fieldYT,
      color: PDFLib.rgb(1, 1, 1),
      borderWidth: 0,
    });
  }

  // Zeichnet das Kundendesign fuer Druckdatei, Vorschau-PDF und A4-Single-
  // Label. Rasterbilder duerfen ausschliesslich aus den beim Einlesen
  // normalisierten Exportbytes kommen; state.fileBytes bleibt das Original
  // fuer _Quelldatei-Upload und Re-Edit.
  async function drawCustomerDesignOnPdf(outPdf, page, geometry) {
    if (!state.fileBytes) return;
    const {
      oX, oY, labelH, pageH, designX, designW,
    } = geometry;
    const userScale = state.designScale || 1;
    const drawDesignW = designW * userScale;
    const offsetXpt = state.designOffsetX * MM2PT;
    const offsetYpt = state.designOffsetY * MM2PT;
    const designCenterXpt = oX + (DESIGN_X + DESIGN_W / 2) * MM2PT + offsetXpt;
    const designCenterYpt = oY + labelH / 2 - offsetYpt;

    pushClipDesign(page, designX, 0, designW, pageH);
    try {
      if (state.fileType === 'pdf') {
        if (!state.pdfExportBytes) {
          throw new Error('Normalisierte PDF-Daten fehlen; PDF-Export wurde abgebrochen');
        }
        const [embPage] = await outPdf.embedPdf(state.pdfExportBytes, [0]);
        const scale = drawDesignW / embPage.width;
        const scaledH = embPage.height * scale;
        const xOff = designCenterXpt - drawDesignW / 2;
        const yOff = designCenterYpt - scaledH / 2;
        page.drawPage(embPage, { x: xOff, y: yOff, xScale: scale, yScale: scale });
        return;
      }

      if (!state.rasterExportBytes || !state.rasterExportType) {
        throw new Error('Normalisierte Rasterdaten fehlen; PDF-Export wurde abgebrochen');
      }
      const imgBytes = state.rasterExportBytes instanceof Uint8Array
        ? state.rasterExportBytes
        : new Uint8Array(state.rasterExportBytes);
      const embImg = state.rasterExportType === 'jpg'
        ? await outPdf.embedJpg(imgBytes)
        : await outPdf.embedPng(imgBytes);
      const iDims = embImg.scale(1);
      const scale = drawDesignW / iDims.width;
      const scaledH = iDims.height * scale;
      const xOff = designCenterXpt - drawDesignW / 2;
      const yOff = designCenterYpt - scaledH / 2;
      page.drawImage(embImg, { x: xOff, y: yOff, width: drawDesignW, height: scaledH });
    } finally {
      popClipDesign(page);
    }
  }

  async function buildPdfBytes(withBleed) {
    const PDFLib = window.PDFLib;
    const { PDFDocument } = PDFLib;
    const isPrint = !!withBleed;

    const bleedMM = isPrint ? PRINT_BLEED_MM : 0;
      const bleedPt = bleedMM * MM2PT;
      const pageW   = (LABEL_W + bleedMM * 2) * MM2PT;
      const pageH   = (LABEL_H + bleedMM * 2) * MM2PT;
      const labelW  = LABEL_W * MM2PT;
      const labelH  = LABEL_H * MM2PT;
      const oX      = bleedPt;
      const oY      = bleedPt;
      const designX = DESIGN_X * MM2PT + oX;
      const designW = DESIGN_W * MM2PT;

      const outPdf = await PDFDocument.create();
      const page   = outPdf.addPage([pageW, pageH]);

      // 1. Hintergrundfarbe
      const hex = state.bgColor.replace('#', '');
      const cr  = parseInt(hex.substring(0, 2), 16) / 255;
      const cg  = parseInt(hex.substring(2, 4), 16) / 255;
      const cb  = parseInt(hex.substring(4, 6), 16) / 255;
      page.drawRectangle({ x: 0, y: 0, width: pageW, height: pageH, color: bgPdfColor(PDFLib, cr, cg, cb) });

      // 2. Kunden-Design (mit Phase-3 Scale + Offset + Clipping + Center-Anchor)
      // - Skalierung: vom Center des Bildes aus
      // - Clip: horizontal Design-Bereich (25–155 mm), vertikal volle Page (inkl. Bleed)
      // - Achtung: Screen-Y wächst nach unten, PDF-Y nach oben → designOffsetY-Vorzeichen drehen
      // Bild ist optional — wird komplett übersprungen wenn kein File hochgeladen wurde.
      await drawCustomerDesignOnPdf(outPdf, page, {
        oX, oY, labelH, pageH, designX, designW,
      });

      // 3. Frame-Overlay (PDF-Vektor oder PNG-Fallback)
      const framePdfKey  = state.selectedSorte;
      const framePageNum = state.variant === 'Weiss' ? 2 : 1;
      const framePdfBytes = await getFramePdfBytes(framePdfKey);
      if (framePdfBytes) {
        const framePdfDoc = await PDFDocument.load(framePdfBytes);
        const framePages  = framePdfDoc.getPages();
        const srcIdx      = (framePageNum <= framePages.length) ? framePageNum - 1 : 0;
        const [embFrame]  = await outPdf.embedPages([framePages[srcIdx]]);
        page.drawPage(embFrame, {
          x: oX, y: oY,
          xScale: labelW / embFrame.width,
          yScale: labelH / embFrame.height,
        });
      } else {
        const pngBytes = await getFramePngBytes(framePdfKey, state.variant);
        if (pngBytes) {
          const frameImg = await outPdf.embedPng(pngBytes);
          page.drawImage(frameImg, { x: oX, y: oY, width: labelW, height: labelH });
        }
      }

      // 3b. Custom-Text mittig im Design-Bereich (Phase 2)
      await drawCustomTextOnPdfPage(outPdf, page, oX, oY, labelH);

      // 3c. MHD/Charge oder weißes Etikettierer-Feld — entscheidet
      // sich anhand state.directPrintMhd (UI-Checkbox + Variant-Auto-Toggle).
      await drawMhdChargeOrField(outPdf, page, oX, oY, labelH, cr, cg, cb);

      // 4. PDF/X-3 Metadata (nur Druckdatei)
      if (isPrint) {
        applyPrintPageBoxes(page, pageW, pageH, oX, oY, labelW, labelH);
        attachPdfxMetadata(outPdf, await loadOutputProfileBytes());
      }

      // 5a. OpenAction = /Fit — Viewer öffnet das PDF automatisch
      //     bildschirmfüllend statt im 100%-Zoom. Nur für Vorschau,
      //     Druckdatei bleibt unverändert.
      if (!isPrint) {
        try {
          const { PDFName, PDFArray } = PDFLib;
          // Explizit PDFArray bauen — context.obj() konvertiert
          // JS-Arrays nicht zuverlässig zu PDFArray.
          const openAction = PDFArray.withContext(outPdf.context);
          openAction.push(page.ref);
          openAction.push(PDFName.of('Fit'));
          outPdf.catalog.set(PDFName.of('OpenAction'), openAction);
        } catch (oaErr) {
          console.warn('[koebi-label] OpenAction nicht setzbar:', oaErr);
        }
      }

    // 5b. Save → return Bytes
    const pdfOut = await outPdf.save();
    dlog('buildPdfBytes done, withBleed=', withBleed, 'bytes=', pdfOut.byteLength);
    return pdfOut;
  }

  // ============================================================
  // PDF-Generierung — Orchestrator.
  // ------------------------------------------------------------
  // mode='check' → Vorschau ohne Bleed im neuen Tab.
  // mode='print' → Druckdatei mit Bleed → submitToCart hängt sie an
  //                die Order. Der Cart erzeugt die beschnittfreie Ansicht
  //                beim Klick auf den Design-Button clientseitig aus dieser PDF.
  // ============================================================
  async function generatePDF(mode, previewWin) {
    if (!state.selectedSorte) {
      if (previewWin) previewWin.close();
      return;
    }
    const PDFLib = window.PDFLib;
    if (!PDFLib) {
      showStatus('Vorschau gerade nicht verfügbar. Bitte Seite neu laden.', 'error');
      if (previewWin) previewWin.close();
      return;
    }
    const isPrint = mode === 'print';

    // Beschnitt-Hinweis vor dem Cart-Add: wenn der Hintergrund oben/
    // unten nur bis zur Schnittkante (statt in den Anschnitt) reicht,
    // einmal nachfragen. Nur Hinweis — Kunde kann fortfahren.
    if (isPrint) {
      const bleed = checkBleedCoverage();
      if (bleed && (bleed.top || bleed.bottom)) {
        const edgeWord = (bleed.top && bleed.bottom) ? 'oben und unten'
                       : bleed.top ? 'oben' : 'unten';
        const msg = 'Dein Bild reicht ' + edgeWord + ' nicht ganz bis zum Rand. '
          + 'Damit nach dem Schneiden kein Rand in Hintergrundfarbe entsteht, zieh das '
          + 'Design ' + edgeWord + ' bis ganz an die äußere Kante (in den Beschnitt). '
          + 'Wenn das so gewollt ist (z. B. transparenter Hintergrund), kannst du fortfahren.';
        const proceed = await confirmBleed(msg);
        if (!proceed) return;
      }
    }

    showStatus(isPrint ? 'Datei wird vorbereitet…' : 'Vorschau wird erstellt…', 'loading');
    if (dom.btnCheck) dom.btnCheck.disabled = true;
    if (dom.btnPrint) dom.btnPrint.disabled = true;
    // Nur beim Cart-Add (Print) den In-Button-Spinner zeigen — die
    // Vorschau laeuft im neuen Tab, da reicht die Status-Bar.
    if (isPrint) setCartBtnLoading('Wird geladen…');

    try {
      // Reset Outline-Warnings vor jedem Build. Während des Builds
      // sammelt _drawTextAsOutlines notdef-Glyphen ein; danach wertet
      // die Generate-Funktion das aus (warnen oder blocken).
      _resetOutlineWarnings();

      if (isPrint) {
        const druckBytes = await buildPdfBytes(true);

        // Coverage-Block beim Print: Wenn Zeichen nicht abgedeckt
        // sind, würden sie in der Druckdatei fehlen. Lieber jetzt
        // abbrechen, statt Tim einen kaputten Etikett-Auftrag schicken.
        if (state.lastOutlineWarnings && state.lastOutlineWarnings.length) {
          showStatus(_formatOutlineWarning(), 'error');
          if (dom.btnCheck) dom.btnCheck.disabled = false;
          if (dom.btnPrint) dom.btnPrint.disabled = false;
          updateButton();
          return;
        }

        await submitToCart({ druck: druckBytes });
      } else {
        const pdfOut = await buildPdfBytes(false);

        // Vorschau-PDF in vorher geöffnetem Tab via HTML-Wrapper +
        // <embed>. Chrome respektiert dort #zoom=page-fit, anders als
        // bei direkter Blob-Navigation.
        const blob = new Blob([pdfOut], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);
        const viewerSrc = pdfUrl + '#zoom=page-fit&view=Fit&toolbar=1';

        if (previewWin && !previewWin.closed) {
          try {
            previewWin.document.open();
            previewWin.document.write(
              '<!DOCTYPE html><html><head>' +
              '<meta charset="utf-8">' +
              '<title>Vorschau Etikett</title>' +
              '<style>' +
              'html,body{margin:0;padding:0;height:100%;background:#1f1f1f;overflow:hidden}' +
              'embed,iframe{display:block;width:100%;height:100%;border:0;opacity:0;animation:k 120ms ease-out 220ms forwards}' +
              '@keyframes k{to{opacity:1}}' +
              '</style>' +
              '</head><body>' +
              '<embed src="' + viewerSrc + '" type="application/pdf">' +
              '</body></html>'
            );
            previewWin.document.close();
            _showPreviewStatusWithWarning('Vorschau geöffnet', 'success');
          } catch (writeErr) {
            console.warn('[koebi-label] Wrapper-Write fehlgeschlagen, Fallback:', writeErr);
            previewWin.location.href = viewerSrc;
            _showPreviewStatusWithWarning('Vorschau geöffnet', 'success');
          }
        } else {
          // Fallback: Download anbieten, falls Popup geblockt
          const a = document.createElement('a');
          a.href = pdfUrl;
          a.download = `vorschau-etikett-${Date.now()}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          _showPreviewStatusWithWarning('Datei wird heruntergeladen', 'info');
        }
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      }
    } catch (e) {
      if (previewWin && !previewWin.closed) previewWin.close();
      showStatus('Etwas ist schiefgelaufen. Bitte erneut versuchen.', 'error');
      console.error(e);
    }
    updateButton();
  }

  // ============================================================
  // buildLabelPdfBytes / generateA4Sheet / printSheet
  // (Quelle Z.1427–1661) — werden im Theme nur via btnDirectPrint
  // angefasst (Admin-Feature, im Shop versteckt)
  // ============================================================
  async function buildLabelPdfBytes() {
    const PDFLib = window.PDFLib;
    if (!PDFLib) throw new Error('PDF-Bibliothek nicht geladen');
    const { PDFDocument } = PDFLib;
    const bleedPt = PRINT_BLEED_MM * MM2PT;
    const pageW   = (LABEL_W + PRINT_BLEED_MM * 2) * MM2PT;
    const pageH   = (LABEL_H + PRINT_BLEED_MM * 2) * MM2PT;
    const labelW  = LABEL_W * MM2PT;
    const labelH  = LABEL_H * MM2PT;
    const oX      = bleedPt;
    const oY      = bleedPt;
    const designX = DESIGN_X * MM2PT + oX;
    const designW = DESIGN_W * MM2PT;

    const outPdf = await PDFDocument.create();
    const page   = outPdf.addPage([pageW, pageH]);
    applyPrintPageBoxes(page, pageW, pageH, oX, oY, labelW, labelH);

    const hex = state.bgColor.replace('#', '');
    const cr  = parseInt(hex.substring(0, 2), 16) / 255;
    const cg  = parseInt(hex.substring(2, 4), 16) / 255;
    const cb  = parseInt(hex.substring(4, 6), 16) / 255;
    page.drawRectangle({ x: 0, y: 0, width: pageW, height: pageH, color: bgPdfColor(PDFLib, cr, cg, cb) });

    // Phase-3 Scale + Offset + Center-Anchor auch im Admin-A4-Bogen (Konsistenz mit generatePDF).
    // Design-Render-Block nur, wenn auch ein Bild hochgeladen wurde — Kunden dürfen
    // ohne Upload bestellen (analog zum buildPdfBytes-Pfad).
    await drawCustomerDesignOnPdf(outPdf, page, {
      oX, oY, labelH, pageH, designX, designW,
    });

    const framePdfBytes = await getFramePdfBytes(state.selectedSorte);
    if (framePdfBytes) {
      const framePdfDoc = await PDFDocument.load(framePdfBytes);
      const framePages  = framePdfDoc.getPages();
      const srcIdx      = (state.variant === 'Weiss' && framePages.length >= 2) ? 1 : 0;
      const [embFrame]  = await outPdf.embedPages([framePages[srcIdx]]);
      page.drawPage(embFrame, { x: oX, y: oY, xScale: labelW / embFrame.width, yScale: labelH / embFrame.height });
    } else {
      const pngBytes = await getFramePngBytes(state.selectedSorte, state.variant);
      if (pngBytes) {
        const frameImg = await outPdf.embedPng(pngBytes);
        page.drawImage(frameImg, { x: oX, y: oY, width: labelW, height: labelH });
      }
    }

    // Custom-Text mittig im Design-Bereich (Phase 2)
    await drawCustomTextOnPdfPage(outPdf, page, oX, oY, labelH);

    // MHD/Charge oder weißes Etikettierer-Feld — selbe Helper wie buildPdfBytes,
    // damit das Single-Label im A4-Bogen exakt aussieht wie in der Kunden-Druckdatei.
    await drawMhdChargeOrField(outPdf, page, oX, oY, labelH, cr, cg, cb);

    return await outPdf.save();
  }

  async function buildA4SheetBlob() {
    const PDFLib = window.PDFLib;
    if (!PDFLib) throw new Error('PDF-Bibliothek nicht geladen');
    const { PDFDocument } = PDFLib;
    const A4_W = 210, A4_H = 297;
    const p = mm => mm * MM2PT;

    const labelBytes = await buildLabelPdfBytes();

    const lbW = LABEL_W + PRINT_BLEED_MM * 2;
    const lbH = LABEL_H + PRINT_BLEED_MM * 2;

    // Offset 2026-06-08: alle 3 Etiketten 2 mm nach rechts (104.7→106.7),
    // oberstes netto 1.5 mm hoch (56→54.5), unterstes 3 mm runter (240→243).
    const X_CENTER  = 106.7;
    const Y_CENTERS = [54.5, 148, 243];

    const outPdf = await PDFDocument.create();
    if (window.fontkit) outPdf.registerFontkit(window.fontkit);
    const page = outPdf.addPage([p(A4_W), p(A4_H)]);
    page.setMediaBox(0, 0, p(A4_W), p(A4_H));
    page.setBleedBox(0, 0, p(A4_W), p(A4_H));
    page.setTrimBox(0, 0, p(A4_W), p(A4_H));
    page.drawRectangle({ x: 0, y: 0, width: p(A4_W), height: p(A4_H), color: PDFLib.rgb(1, 1, 1) });

    const BG_EXT = 7; // Offset 2026-06-08: 5→7 mm pro Seite (+2 mm je Seite)
    const bgW = lbW + BG_EXT * 2;
    const hex = state.bgColor.replace('#', '');
    const cr  = parseInt(hex.substring(0, 2), 16) / 255;
    const cg  = parseInt(hex.substring(2, 4), 16) / 255;
    const cb  = parseInt(hex.substring(4, 6), 16) / 255;
    const bgColor = bgPdfColor(PDFLib, cr, cg, cb);

    const SCALE = 0.96;
    const [embLabel] = await outPdf.embedPdf(labelBytes, [0]);
    const lx = p(X_CENTER - lbW / 2);

    for (const yc of Y_CENTERS) {
      const ly  = p(A4_H - yc - lbH / 2);
      const cx  = lx + p(lbW) / 2;
      const cy  = ly + p(lbH) / 2;
      const sLbW = p(lbW) * SCALE;
      const sLbH = p(lbH) * SCALE;
      const sBgW = p(bgW) * SCALE;
      const newLbX = cx - sLbW / 2;
      const newLbY = cy - sLbH / 2;
      const newBgX = cx - sBgW / 2;

      page.drawRectangle({ x: newBgX, y: newLbY, width: sBgW, height: sLbH, color: bgColor });
      page.drawPage(embLabel, {
        x: newLbX, y: newLbY,
        xScale: sLbW / embLabel.width,
        yScale: sLbH / embLabel.height,
      });
    }

    // MHD/Charge-Block entfällt hier — die Texte stecken jetzt in jedem
    // eingebetteten Single-Label (siehe buildLabelPdfBytes → drawMhdChargeOrField).
    // So sind A4-Bogen-Etiketten und die Kunden-Druckdatei deckungsgleich.

    const pdfOut = await outPdf.save();
    return new Blob([pdfOut], { type: 'application/pdf' });
  }

  async function generateA4Sheet() {
    // Bild ist optional — Kunden dürfen auch ohne Upload bestellen
    // (nur Hintergrundfarbe + Frame + ggf. Custom-Text). Sorten-Wahl
    // bleibt Pflicht, sonst weiß der A4-Bogen nicht welchen Frame er holt.
    if (!state.selectedSorte) return;
    showStatus('A4-Druckbogen wird generiert…', 'loading');
    if (dom.btnCheck)       dom.btnCheck.disabled = true;
    if (dom.btnPrint)       dom.btnPrint.disabled = true;
    if (dom.btnDirectPrint) dom.btnDirectPrint.disabled = true;
    try {
      _resetOutlineWarnings();
      const blob = await buildA4SheetBlob();
      const sorteName = sanitizeName(sortenLabelFromKey(state.selectedSorte));
      // Customer-Name-UI entfernt — Filename nutzt generischen Platzhalter.
      const customer  = 'Kunde';
      const now       = new Date();
      const dateStr   = now.getFullYear().toString().slice(-2)
                      + String(now.getMonth() + 1).padStart(2, '0')
                      + String(now.getDate()).padStart(2, '0');
      const filename  = `${dateStr}_${customer}_${sorteName}_A4-Bogen.pdf`;
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      showStatus(`✓ ${filename} gespeichert`, 'success');
    } catch (e) {
      showStatus('Fehler: ' + (e.message || e), 'error');
      console.error(e);
    }
    updateButton();
  }

  async function printSheet() {
    // Bild ist optional — wie generateA4Sheet, Sorte bleibt Pflicht.
    if (!state.selectedSorte) return;
    showStatus('Druckvorschau wird vorbereitet…', 'loading');
    if (dom.btnCheck)       dom.btnCheck.disabled = true;
    if (dom.btnPrint)       dom.btnPrint.disabled = true;
    if (dom.btnDirectPrint) dom.btnDirectPrint.disabled = true;
    try {
      _resetOutlineWarnings();
      const blob = await buildA4SheetBlob();
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => {
          win.print();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        });
      } else {
        URL.revokeObjectURL(url);
        showStatus('Popup blockiert — bitte Popup-Blocker deaktivieren', 'error');
      }
      showStatus('', '');
    } catch (e) {
      showStatus('Fehler: ' + (e.message || e), 'error');
      console.error(e);
    }
    updateButton();
  }

  // ============================================================
  // Editor-State-Snapshot für _Editor-State Cart-Property.
  // Schema v1 — bei jeder Struktur-Änderung version hochziehen, damit
  // ein späterer Re-Import-Pfad alte Bestellungen erkennen kann.
  // KEINE Bytes/Buffer/Caches reinpacken — nur Werte, mit denen der
  // Editor sich später deterministisch wiederherstellen lässt. Die
  // hochgeladene Druckdatei steckt schon in _Druckdatei.
  // ============================================================
  function buildEditorStateSnapshot() {
    const hex = (v) => {
      const s = (v || '').toUpperCase();
      return /^#[0-9A-F]{6}$/.test(s) ? s : '';
    };
    return {
      version: 1,
      sorte:  state.selectedSorte || null,
      frame:  state.variant || null,            // "Schwarz" | "Weiss"
      bg: {
        hex:  hex(state.bgColor) || '#FFFFFF',
        cmyk: Array.isArray(state.bgColorCmyk) ? state.bgColorCmyk : null,
      },
      design: {
        offsetX:  Number(state.designOffsetX) || 0,
        offsetY:  Number(state.designOffsetY) || 0,
        scale:    Number(state.designScale)   || 1,
        imageW:   Number(state.imageW)        || 0,
        imageH:   Number(state.imageH)        || 0,
        fileType: state.fileType || '',
      },
      text: {
        content:    state.customText || '',
        font:       state.customFont || '',
        style:      state.customTextStyle || 'Regular',
        size:       Number(state.customTextSize)   || 0,
        lineHeight: Number(state.customLineHeight) || 1.2,
        color:      hex(state.customTextColor) || '#000000',
        offsetX:    Number(state.textOffsetX) || 0,
        offsetY:    Number(state.textOffsetY) || 0,
      },
      // Direkt-aufdrucken-Modus für MHD/Charge (Session 2). Wird beim Re-Edit
      // wieder hergestellt, damit eine bestehende Bestellung mit dem gleichen
      // Modus regeneriert werden kann (sonst wäre die Wahl zwischen direktem
      // Druck und Etikettierer-Feld nicht reproduzierbar).
      directPrintMhd: !!state.directPrintMhd,
    };
  }

  // ============================================================
  // App-Proxy-Upload der Quelldatei (vor dem Cart-Submit)
  // ------------------------------------------------------------
  // POSTet die Kunden-Originaldatei an /apps/label/upload. Shopify
  // proxied das HMAC-signiert an apps.koebi.bayern, die Plugin-Route
  // legt das File via stagedUploadsCreate + fileCreate in Shopify
  // Files ab und gibt eine GenericFile-GID zurueck. Die GID kommt
  // dann als _Quelldatei-gid Hidden-Property in den Cart — ~80 Byte
  // statt 20 MB, das Cart-Add-Limit ist nicht mehr Quelldatei-
  // abhaengig.
  //
  // Fehlerbehandlung (Session B, 2026-05-21):
  //   - 413 / 415 / 401 / 400        → Block-Case (Cart-Submit stoppen,
  //                                    klare Meldung an den Kunden)
  //   - 429 / 5xx / Netzwerk-Fehler  → 1 Retry mit 1 s Backoff, dann
  //                                    Skip-Case (Cart-Submit ohne
  //                                    Quelldatei durchlassen, Hinweis-
  //                                    Banner nach Bestelleingang)
  //   - sonstige 4xx                 → Block-Case (unerwartet, lieber
  //                                    sichtbar als verschleiern)
  //
  // Returntyp (discriminated union):
  //   { ok:true,  gid, fileName, byteSize, mimeType }
  //   { ok:false, kind:'block', message }
  //   { ok:false, kind:'skip',  reason }
  // ============================================================
  const PROXY_UPLOAD_ENDPOINT      = '/apps/label/upload';
  const PROXY_UPLOAD_TIMEOUT_MS    = 60_000;
  const PROXY_UPLOAD_RETRY_DELAY_MS = 1000;

  async function uploadQuelldateiToProxy(bytes, originalName) {
    // Server liest die Bytes via arrayBuffer() und macht eigene Magic-
    // Bytes-Detection — Client-MIME ist ohnehin nicht vertrauenswuerdig,
    // wir senden bewusst octet-stream.
    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const form = new FormData();
    form.append('source', blob, originalName || 'quelldatei');

    async function singleAttempt() {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), PROXY_UPLOAD_TIMEOUT_MS);
      try {
        const r = await fetch(PROXY_UPLOAD_ENDPOINT, {
          method: 'POST',
          body: form,
          signal: controller.signal,
        });
        return { kind: 'response', response: r };
      } catch (err) {
        // fetch wirft bei Netzwerkfehler oder AbortController-Timeout.
        return { kind: 'networkError', error: err };
      } finally {
        clearTimeout(timer);
      }
    }

    // Server-/Netzwerk-Fehler sind retry-wuerdig. Client-Fehler (4xx
    // ausser 429) sind es nicht — ein Retry waere reine Latenz.
    function isRetryable(att) {
      if (att.kind === 'networkError') return true;
      const s = att.response.status;
      return s === 429 || (s >= 500 && s < 600);
    }

    let attempt = await singleAttempt();
    if (isRetryable(attempt)) {
      await new Promise((res) => setTimeout(res, PROXY_UPLOAD_RETRY_DELAY_MS));
      attempt = await singleAttempt();
      if (isRetryable(attempt)) {
        const reason = attempt.kind === 'networkError'
          ? `network:${attempt.error && attempt.error.name || 'fetch'}`
          : `http_${attempt.response.status}`;
        return { ok: false, kind: 'skip', reason };
      }
    }

    if (attempt.kind === 'networkError') {
      // Defensive Sicherheit — sollte durch isRetryable bereits abgefangen sein.
      return { ok: false, kind: 'skip', reason: 'network' };
    }

    const r = attempt.response;

    // Block-Cases: Client-seitige Probleme, an denen ein Retry nichts aendert
    // und die der Kunde sehen+verstehen soll.
    if (r.status === 413) {
      return {
        ok: false,
        kind: 'block',
        message: 'Datei zu groß (max. 20 MB). Bitte das Bild verkleinern und erneut versuchen.',
      };
    }
    if (r.status === 415) {
      return {
        ok: false,
        kind: 'block',
        message: 'Dateityp wird nicht unterstützt. Bitte PDF, PNG, JPG, TIFF, WebP oder BMP hochladen.',
      };
    }
    if (r.status === 401) {
      return {
        ok: false,
        kind: 'block',
        message: 'Upload-Sitzung ungültig. Bitte die Seite neu laden und erneut versuchen.',
      };
    }
    if (r.status === 400) {
      // missing_file_field / invalid_multipart / empty_file — defensiv.
      return {
        ok: false,
        kind: 'block',
        message: 'Originaldatei konnte nicht hochgeladen werden. Bitte erneut versuchen.',
      };
    }
    if (!r.ok) {
      // Andere 4xx (404 etc.) — unerwartet. Sichtbar machen.
      return {
        ok: false,
        kind: 'block',
        message: `Unerwarteter Fehler beim Upload (HTTP ${r.status}). Bitte erneut versuchen.`,
      };
    }

    let payload;
    try {
      payload = await r.json();
    } catch (err) {
      // 2xx ohne valides JSON — Plugin-Bug, lieber Skip als Block (Druckdatei reicht).
      console.warn('[koebi-label] Proxy-Upload-Antwort kein JSON:', err);
      return { ok: false, kind: 'skip', reason: 'invalid_json' };
    }
    if (!payload || payload.ok !== true || !payload.gid) {
      console.warn('[koebi-label] Proxy-Upload-Payload unvollstaendig:', payload);
      return { ok: false, kind: 'skip', reason: 'invalid_payload' };
    }

    return {
      ok: true,
      gid:      String(payload.gid),
      fileName: String(payload.fileName || ''),
      byteSize: Number(payload.byteSize) || (bytes.byteLength || 0),
      mimeType: String(payload.mimeType || ''),
    };
  }

  // ============================================================
  // Re-Edit-Save (Session C, 2026-05-21)
  // ------------------------------------------------------------
  // Wird statt Cart-Add aufgerufen, wenn state.reedit gesetzt ist
  // (= der Editor wurde mit einem Re-Edit-Link geoeffnet).
  // POSTed Druckdatei + optional neue Quelldatei + Editor-State an
  // /apps/label/order/save. Plugin schreibt das Ergebnis als
  // LineItem-Metafield `custom.reedit_state` und optional aktualisiert
  // das Order-Metafield `custom.quelldateien` auf die neue GID.
  //
  // KEIN Cart-Add. KEIN cartUpdate-Event. Erfolg = Success-Status mit
  // Hinweis „Bestellung wurde aktualisiert".
  // ============================================================
  async function submitReeditSave(pdfs) {
    const druckBytes = pdfs && pdfs.druck;
    if (!druckBytes) {
      showStatus('Etwas ist schiefgelaufen. Bitte erneut versuchen.', 'error');
      return;
    }
    const ctx = state.reedit;
    if (!ctx) {
      showStatus('Re-Edit-Kontext verloren. Bitte den Link aus dem Admin neu öffnen.', 'error');
      return;
    }
    if (druckBytes.byteLength > MAX_FILE_BYTES) {
      showStatus('Datei zu groß. Bitte Bild verkleinern.', 'error');
      return;
    }

    showStatus('Bestellung wird aktualisiert…', 'loading');

    // Editor-State-Snapshot — gleiche Builder-Funktion wie beim
    // Cart-Submit-Pfad, damit der Plugin-Server den Snapshot identisch
    // versteht (Schema v1).
    let editorStateJson;
    try {
      editorStateJson = JSON.stringify(buildEditorStateSnapshot());
    } catch (err) {
      console.error('[koebi-label] Re-Edit-Snapshot-Build fehlgeschlagen:', err);
      showStatus('Re-Edit konnte nicht gespeichert werden (State-Snapshot).', 'error');
      return;
    }

    // Druckdatei in File verpacken — semantischer Name (Server vergibt
    // ohnehin sein eigenes deterministisches Naming, das hier ist nur
    // Form-Field-Filename).
    const sorteLabel = (sortenLabelFromKey(state.selectedSorte) || 'Etikett')
      .replace(/\s+/g, '-');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
                  `_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const druckFile = new File(
      [druckBytes],
      `Etikett_${sorteLabel}_Druckdatei_Reedit_${stamp}.pdf`,
      { type: 'application/pdf' },
    );

    const form = new FormData();
    form.append('orderId',     ctx.orderId);
    form.append('lineItemId',  ctx.lineItemId);
    form.append('exp',         ctx.exp);
    form.append('token',       ctx.token);
    form.append('editorState', editorStateJson);
    form.append('druck',       druckFile, druckFile.name);

    // Quelldatei-Strategie:
    //   - Falls der Kunde im Re-Edit-Flow ein neues Bild hochgeladen
    //     hat (handleFile wurde nach dem Initial-Hydrate erneut
    //     aufgerufen, hasUploadedNewSource ist true), schicken wir
    //     die neuen Bytes als `source`. Plugin laedt sie in Shopify
    //     Files hoch und linkt die neue GID ans LineItem.
    //   - Sonst behalten wir die bestehende Quelldatei. Plugin
    //     erkennt das am fehlenden `source`-Feld; die alte GID
    //     bleibt ueber den Fallback (Order-Metafield > _Quelldatei-
    //     gid-Property) weiter referenziert.
    if (ctx.hasUploadedNewSource && state.fileBytes) {
      // state.fileBytes ist ArrayBuffer; in Blob/File packen.
      const sourceBlob = new Blob([state.fileBytes], { type: 'application/octet-stream' });
      const sourceFile = new File(
        [sourceBlob],
        state.fileName || 'quelldatei',
        { type: 'application/octet-stream' },
      );
      form.append('source', sourceFile, sourceFile.name);
    }

    try {
      const r = await fetch(REEDIT_SAVE_ENDPOINT, { method: 'POST', body: form });
      let payload = null;
      try { payload = await r.json(); } catch (_) { /* defensive */ }
      if (!r.ok || !payload || !payload.ok) {
        const code = (payload && payload.error) || `http_${r.status}`;
        console.error('[koebi-label] Re-Edit-Save fehlgeschlagen:', code, payload);
        let msg = 'Re-Edit konnte nicht gespeichert werden.';
        if (code === 'expired')                  msg = 'Der Re-Edit-Link ist abgelaufen. Bitte im Admin neu öffnen.';
        else if (code === 'druckdatei_too_large') msg = 'Druckdatei zu groß. Bitte Bild verkleinern.';
        else if (code === 'quelldatei_too_large') msg = 'Originaldatei zu groß. Bitte Bild verkleinern.';
        else if (code === 'unsupported_quelldatei_type') msg = 'Original-Dateiformat nicht unterstützt.';
        showStatus(msg, 'error');
        return;
      }

      const v = Number(payload.reeditVersion || 0);
      const versionSuffix = v > 1 ? ` (v${v})` : '';
      const orderLabel = ctx.orderName ? ` ${ctx.orderName}` : '';
      showStatus(`Bestellung${orderLabel} wurde aktualisiert${versionSuffix}.`, 'success');

      // Re-Edit-Kontext aktualisieren (damit weitere Saves den Counter
      // wieder korrekt inkrementieren, falls Tim ohne Reload nochmal
      // klickt). Quelldatei-Pfad zuruecksetzen — wir haben sie soeben
      // serverseitig gespeichert, ein erneuter Klick ohne weiteren
      // Upload soll nicht erneut hochladen.
      state.reedit.reeditVersion = v;
      state.reedit.hasUploadedNewSource = false;
    } catch (err) {
      console.error('[koebi-label] Re-Edit-Save Netzwerk-Fehler:', err);
      showStatus('Re-Edit konnte nicht gespeichert werden (Netzwerk).', 'error');
    }
  }

  // ============================================================
  // Cart-Submit (NEU für Theme-Port)
  // PDF-Bytes via DataTransfer-API in hidden file input,
  // hidden Properties füllen, Cart-Form submitten.
  // ============================================================
  async function submitToCart(pdfs) {
    // Session C — Re-Edit-Modus: statt Cart-Add ueberschreiben wir
    // das LineItem-Metafield `custom.reedit_state` an der bestehenden
    // Order. submitReeditSave kuemmert sich um den abweichenden
    // Lifecycle (Save-Endpoint, kein Cart-Notification, kein PubSub
    // cartUpdate). Bestellung bleibt strukturell identisch — nur die
    // Override-Daten am Line-Item werden aktualisiert.
    if (state.reedit && state.reedit.orderId) {
      return submitReeditSave(pdfs);
    }

    // pdfs = { druck: Uint8Array }
    // Wird im Quelldatei-Skip-Case (Server-/Netzwerk-Fehler nach Retry)
    // gesetzt und im Cart-Success-Pfad unten als persistenter Hinweis-
    // Banner ausgespielt, sodass der Kunde transparent sieht: Bestellung
    // ging durch, aber Re-Edit ist fuer diese Order nicht moeglich.
    let quelldateiSkipReason = '';
    const druckBytes = pdfs && pdfs.druck;
    if (!druckBytes) {
      showStatus('Etwas ist schiefgelaufen. Bitte erneut versuchen.', 'error');
      return;
    }
    if (!state.cartVariantId) {
      showStatus('Hinzufügen gerade nicht möglich. Bitte Seite neu laden.', 'error');
      return;
    }
    if (!dom.cartForm || !dom.cartFileInputPrint) {
      showStatus('Hinzufügen gerade nicht möglich. Bitte Seite neu laden.', 'error');
      return;
    }
    const totalLen = druckBytes.byteLength || 0;
    if (totalLen > MAX_FILE_BYTES) {
      showStatus('Datei zu groß. Bitte Bild verkleinern.', 'error');
      return;
    }

    // Semantische Filenames: Etikett_{Sorte}_Druckdatei_{YYYY-MM-DD_HH-MM}.pdf
    const sorteLabel = (sortenLabelFromKey(state.selectedSorte) || 'Etikett')
      .replace(/\s+/g, '-');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
                  `_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const fileDruck = new File(
      [druckBytes],
      `Etikett_${sorteLabel}_Druckdatei_${stamp}.pdf`,
      { type: 'application/pdf' }
    );

    try {
      const dt2 = new DataTransfer();
      dt2.items.add(fileDruck);
      dom.cartFileInputPrint.files = dt2.files;
    } catch (e) {
      showStatus('Bitte einen aktuellen Browser nutzen (Chrome, Firefox, Safari).', 'error');
      console.error(e);
      return;
    }

    // Semantischer Filename in hidden Property — Order-Detail rendert
    // diesen statt des CDN-Hashes.
    if (dom.cartFilenamePrint) dom.cartFilenamePrint.value = fileDruck.name;

    // ──────────────────────────────────────────────────────────────
    // _Quelldatei-gid — Shopify-Files-GID der Kunden-Originaldatei.
    // Wird VOR dem Cart-Submit ueber den App-Proxy (POST /apps/label/
    // upload) ins Plugin hochgeladen; das Plugin legt die Datei in
    // Shopify Files ab und gibt die GID zurueck. Hier landet nur die
    // ~80 Byte GID im Cart — damit ist das Cart-Add-Limit unabhaengig
    // von der Quelldatei-Groesse (vorher: Druckdatei + Quelldatei = bis
    // 2× MAX_FILE_BYTES = 40 MB, sprengte 413).
    //
    // Beide Inputs starten leer + disabled. Disabled Felder werden per
    // HTML-Spec nicht ins FormData aufgenommen — kein leeres Property im
    // Cart, kein Stale-State aus einem frueheren Submit-Versuch. Werden
    // bei erfolgreichem Proxy-Upload mit GID + Filename gefuellt und
    // enabled. Bei Upload-Fehlern siehe `uploadQuelldateiToProxy()` oben:
    // 413/415/401/400/4xx → Block, 429/5xx/Netzwerk → 1 Retry, dann Skip.
    // ──────────────────────────────────────────────────────────────
    if (dom.cartFilenameSource) {
      dom.cartFilenameSource.value    = '';
      dom.cartFilenameSource.disabled = true;
    }
    if (dom.cartSourceGid) {
      dom.cartSourceGid.value    = '';
      dom.cartSourceGid.disabled = true;
    }
    if (dom.cartSourceGid && state.fileBytes) {
      const sourceLen = state.fileBytes.byteLength || 0;
      if (sourceLen > MAX_FILE_BYTES) {
        // Source allein ueber Limit — sollte handleFile() schon abfangen,
        // defensiv hier nochmal. Block, da Kunden-fixbar (kleinere Datei).
        showStatus('Quelldatei zu groß. Bitte Bild verkleinern.', 'error');
        return;
      }
      showStatus('Originaldatei wird gesichert…', 'loading');
      const upload = await uploadQuelldateiToProxy(state.fileBytes, state.fileName || '');
      if (!upload.ok) {
        if (upload.kind === 'block') {
          showStatus(upload.message, 'error');
          return;
        }
        // Skip-Case: Cart-Submit ohne Quelldatei, Warn-Banner nach Erfolg.
        console.warn(
          '[koebi-label] Quelldatei-Upload uebersprungen — reason:',
          upload.reason,
          '(Re-Edit im Admin fuer diese Order nicht moeglich)'
        );
        quelldateiSkipReason = upload.reason;
      } else {
        dom.cartSourceGid.value    = upload.gid;
        dom.cartSourceGid.disabled = false;
        if (dom.cartFilenameSource) {
          dom.cartFilenameSource.value    = upload.fileName;
          dom.cartFilenameSource.disabled = !upload.fileName;
        }
      }
    }

    // Phase 4c — Editor-State an die Cart-Properties durchreichen.
    // Inputs sind in der Liquid mit `disabled` markiert, damit sie ohne
    // Wert nicht in den Cart submitted werden (Shopify ignoriert disabled
    // Felder im FormData). Sobald wir einen Wert haben, enablen wir sie.
    const sorteLabelForCart = sortenLabelFromKey(state.selectedSorte) || '';
    if (dom.cartSorte) {
      dom.cartSorte.value    = sorteLabelForCart;
      dom.cartSorte.disabled = !sorteLabelForCart;
    }
    if (dom.cartVariant) {
      dom.cartVariant.value    = state.variant || '';
      dom.cartVariant.disabled = !state.variant;
    }
    if (dom.cartBg) {
      const bgHex = (state.bgColor || '').toUpperCase();
      // Normalisiert auf "#RRGGBB"; Default-Weiß bewusst mit-schreiben,
      // damit das Plugin sich nie auf einen Fallback verlassen muss.
      const bgOut = /^#[0-9A-F]{6}$/.test(bgHex) ? bgHex : '#FFFFFF';
      dom.cartBg.value    = bgOut;
      dom.cartBg.disabled = false;
    }
    if (dom.cartEditorState) {
      try {
        dom.cartEditorState.value    = JSON.stringify(buildEditorStateSnapshot());
        dom.cartEditorState.disabled = false;
      } catch (e) {
        // Snapshot ist defensiv aufgebaut, sollte nie werfen — aber
        // wenn doch, lieber State weglassen als Cart-Submit blockieren.
        console.warn('[koebi-label] _Editor-State Snapshot fehlgeschlagen:', e);
        dom.cartEditorState.value    = '';
        dom.cartEditorState.disabled = true;
      }
    }
    // Defensive Migrations-Sicherheit: falls die alten Hidden-Inputs
    // (Kundenname/MHD/Charge) noch im DOM hängen, leer schicken.
    if (dom.cartCustomer) dom.cartCustomer.value = '';
    if (dom.cartMhd)      dom.cartMhd.value      = '';
    if (dom.cartCharge)   dom.cartCharge.value   = '';

    // Im Produkt-Mode wird die Variant-ID aus dem aktuellen
    // Variant-Picker übernommen (state.cartVariantId wird vom
    // PubSub/Form-Watcher synchron gehalten).
    const idInput = dom.cartForm.querySelector('input[name="id"]');
    if (idInput && state.cartVariantId) idInput.value = state.cartVariantId;
    syncCartRequiredInfoInputs();

    showStatus('Wird in den Warenkorb gelegt…', 'loading');

    // ──────────────────────────────────────────────────────────────
    // AJAX-Submit nach Dawn-Pattern (siehe assets/product-form.js
    // onSubmitHandler Z.20+). Triggert das Cart-Notification-Overlay
    // (oder Cart-Drawer falls eingestellt) statt zum /cart zu navigieren.
    // ──────────────────────────────────────────────────────────────
    const cart = document.querySelector('cart-notification')
              || document.querySelector('cart-drawer');

    const formData = new FormData(dom.cartForm);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((s) => s.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    const config = (typeof fetchConfig === 'function')
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type']; // Browser setzt multipart-Boundary
    config.body = formData;

    const cartAddUrl = (window.routes && window.routes.cart_add_url)
                    || '/cart/add.js';

    try {
      const r = await fetch(cartAddUrl, config);
      const responseText = await r.text();
      let response = {};

      try {
        response = responseText ? JSON.parse(responseText) : {};
      } catch (parseErr) {
        if (r.status === 413) {
          showStatus('Dateien zu groß für den Warenkorb. Bitte Bild verkleinern oder niedriger aufgelöst hochladen.', 'error');
        } else {
          showStatus('Hinzufügen fehlgeschlagen. Bitte erneut versuchen.', 'error');
        }
        console.error('[koebi-label] Cart-Add Antwort war kein JSON:', parseErr, responseText.slice(0, 300));
        return;
      }

      if (!r.ok || response.status) {
        // Shopify-Cart-Error (z. B. zu wenig Lager, falsche Variant-ID)
        showStatus(response.description || 'Hinzufügen fehlgeschlagen.', 'error');
        return;
      }

      // ──────────────────────────────────────────────────────────────
      // GA4/GTM: Konfigurator-Add-to-Cart (Key-Event laut 13_tracking-spec.md).
      // Defensiv: Tracking darf den Cart-Flow niemals blockieren.
      // ──────────────────────────────────────────────────────────────
      try {
        window.dataLayer = window.dataLayer || [];
        const _koebiLineQty = (response && response.quantity != null) ? response.quantity : 1;
        // Flaschenzahl steckt in der Varianten-Option "Menge" (option2),
        // z. B. variant_title "Helles / 16" → 16. Die Warenkorb-Stückzahl
        // (response.quantity) ist NICHT die Flaschenzahl, sondern fast immer 1.
        let _koebiPerUnit = 0;
        if (response && Array.isArray(response.variant_options) && response.variant_options[1] != null) {
          _koebiPerUnit = parseInt(response.variant_options[1], 10) || 0;
        }
        if (!_koebiPerUnit && response && response.variant_title) {
          const _m = String(response.variant_title).match(/(\d+)\s*$/);
          if (_m) _koebiPerUnit = parseInt(_m[1], 10) || 0;
        }
        const _koebiMenge = _koebiPerUnit ? _koebiPerUnit * _koebiLineQty : _koebiLineQty;
        const _koebiCents = (response && response.final_line_price != null)
          ? response.final_line_price
          : ((response && response.price != null ? response.price : 0) * _koebiLineQty);
        const _koebiSorte = String(state.selectedSorte || '').toLowerCase().replace(/_bg$/, '');
        window.dataLayer.push({
          event: 'configurator_add_to_cart',
          sorte: _koebiSorte || null,
          menge: _koebiMenge,
          value: Math.round(_koebiCents) / 100,
        });
      } catch (e) { /* defensive */ }

      // PubSub-Event, damit Header-Cart-Bubble & Co. updaten
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'koebi-label-editor',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        } catch (pubErr) { /* defensive */ }
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        // Wenn der Proxy-Upload uebersprungen wurde (Server-/Netzwerk-
        // Fehler nach Retry), persistenten Hinweis-Banner ausspielen
        // statt die Statusbar zu clearen. 'error' = rot, persistent
        // (kein Auto-Dismiss wie 'success'), damit es nicht uebersehen wird.
        if (quelldateiSkipReason) {
          showStatus(
            'Bestellung im Warenkorb. Hinweis: Originaldatei konnte gerade nicht gespeichert werden — nachträgliche Layout-Änderungen für diese Bestellung sind nicht möglich.',
            'error',
          );
        } else {
          showStatus('', '');
        }
      } else {
        // Fallback: kein Notification-/Drawer-Element auf der Seite
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (err) {
      console.error('[koebi-label] Cart-Add fehlgeschlagen:', err);
      showStatus('Etwas ist schiefgelaufen. Bitte erneut versuchen.', 'error');
    }
  }

  // ============================================================
  // Eyedropper (Quelle Z.2186–2291)
  // ============================================================
  function deactivateCanvasPicker() {
    document.body.classList.remove('canvas-picking');
    if (dom.btnEyedropper) dom.btnEyedropper.classList.remove('active');
    if (_pickerCleanup) { _pickerCleanup(); _pickerCleanup = null; }
  }

  function activateCanvasPicker() {
    const wrap   = dom.previewWrap;
    const canvas = dom.previewCanvas;
    if (!canvas || canvas.style.display === 'none') return;
    document.body.classList.add('canvas-picking');
    if (dom.btnEyedropper) dom.btnEyedropper.classList.add('active');

    function samplePixel(e) {
      const rect   = canvas.getBoundingClientRect();
      const scaleX = canvas.width  / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = Math.round((e.clientX - rect.left) * scaleX);
      const y = Math.round((e.clientY - rect.top)  * scaleY);
      const bounds       = state.previewDesignBounds;
      const designCanvas = state.previewDesignCanvas;
      if (!bounds || !designCanvas) return { x, y, inside: false };
      if (x < bounds.x || x >= bounds.x + bounds.width || y < bounds.y || y >= bounds.y + bounds.height) {
        return { x, y, inside: false };
      }
      const localX = Math.max(0, Math.min(designCanvas.width  - 1, Math.round((x - bounds.x) * (designCanvas.width  / bounds.width))));
      const localY = Math.max(0, Math.min(designCanvas.height - 1, Math.round((y - bounds.y) * (designCanvas.height / bounds.height))));
      const d = designCanvas.getContext('2d', { colorSpace: 'srgb', willReadFrequently: true })
                            .getImageData(localX, localY, 1, 1).data;
      return { x, y, inside: true, r: d[0], g: d[1], b: d[2], a: d[3] };
    }

    function onClick(e) {
      e.stopPropagation();
      const sample = samplePixel(e);
      if (!sample.inside) {
        showStatus('Bitte direkt im Kundendesign klicken', 'error');
        return;
      }
      if ((sample.a ?? 255) < 8) {
        showStatus('Bitte eine bedruckte Stelle im Kundendesign klicken', 'error');
        return;
      }
      if (state.fileType === 'pdf') {
        const result = findBestCmyk(sample.r, sample.g, sample.b);
        if (result.source === 'palette' && result.exact) {
          applyCmykColor(result.cmyk, {
            debug: {
              badge: 'PDF / Exaktes CMYK',
              text:  'Eindeutig aus dem Kunden-PDF übernommen; Vorschau und Export nutzen denselben CMYK-Wert.',
              tone:  'cmyk',
            },
          });
          showStatus('Farbe übernommen', 'success');
        } else {
          applyColor(rgbToHex(sample.r, sample.g, sample.b), {
            debug: {
              badge: 'PDF / RGB-Fallback',
              text: result.ambiguous
                ? 'Mehrere CMYK-Werte treffen dieselbe sichtbare PDF-Farbe; Export bleibt deshalb bewusst bei der sichtbaren Vorschau.'
                : 'Kein eindeutiger CMYK-Treffer im PDF; Export bleibt deshalb bewusst bei der sichtbaren Vorschau.',
              tone: 'warn',
            },
          });
          setCmykFields(result.cmyk);
          showStatus(
            result.ambiguous
              ? 'Mehrere CMYK-Werte treffen dieselbe sichtbare PDF-Farbe; Export bleibt daher bewusst identisch zur Vorschau'
              : 'PDF-Farbe übernommen; Export bleibt bewusst identisch zur sichtbaren Vorschau',
            'success'
          );
        }
      } else {
        applyColor(rgbToHex(sample.r, sample.g, sample.b), {
          debug: {
            badge: 'Bild / RGB',
            text:  'Farbe stammt aus PNG, JPG oder einem anderen Bildformat; Export übernimmt denselben sichtbaren RGB-Wert.',
            tone:  'rgb',
          },
        });
        showStatus('Farbe übernommen', 'success');
      }
      deactivateCanvasPicker();
    }
    function onEsc(e) { if (e.key === 'Escape') deactivateCanvasPicker(); }

    wrap.addEventListener('click', onClick);
    document.addEventListener('keydown', onEsc);
    _pickerCleanup = () => {
      wrap.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }

  function openEyedropper() {
    if (_pickerCleanup) { deactivateCanvasPicker(); return; }
    if (!state.fileBytes || !state.previewDesignBounds || !state.previewDesignCanvas) {
      showStatus('Bitte zuerst ein Kundendesign laden', 'error');
      return;
    }
    activateCanvasPicker();
  }

  // ============================================================
  // Confirm-Dialog (Promise-basiert) — Markup nutzt
  // #btnConfirmOk / #btnConfirmCancel (statt Quelle-Original)
  // ============================================================
  let _confirmResolver = null;
  function confirmDelete(text) {
    if (!dom.confirmOverlay) return Promise.resolve(false);
    if (dom.confirmText) dom.confirmText.textContent = text || 'Wirklich fortfahren?';
    dom.confirmOverlay.hidden = false;
    dom.confirmOverlay.classList.add('open');
    return new Promise((resolve) => {
      _confirmResolver = resolve;
      const onOk = () => { closeConfirm(true); };
      if (dom.btnConfirmOk) {
        const handler = () => {
          dom.btnConfirmOk.removeEventListener('click', handler);
          onOk();
        };
        dom.btnConfirmOk.addEventListener('click', handler);
      }
    });
  }
  function closeConfirm(ok) {
    if (!dom.confirmOverlay) return;
    dom.confirmOverlay.classList.remove('open');
    dom.confirmOverlay.hidden = true;
    if (_confirmResolver) {
      _confirmResolver(!!ok);
      _confirmResolver = null;
    }
  }

  // ============================================================
  // Beschnitt-Hinweis (Anschnitt oben/unten)
  // ------------------------------------------------------------
  // Kunden ziehen ihr Hintergrundbild oft nur bis zur Schnittkante
  // und lassen den 2,5 mm Anschnitt frei. Nach dem Schneiden bleibt
  // dann oben/unten ein Streifen in Hintergrundfarbe stehen. Dieser
  // Check ermittelt die Ober-/Unterkante des Bildes in Label-mm
  // (gleiches Scale/Offset-Modell wie renderPreview + buildPdfBytes)
  // und meldet, wenn eine Kante im kritischen Band liegt:
  //   von  2,5 mm INNERHALB des Endformats  bis  zur Beschnitt-
  //   Aussenkante (Endformat + 2,5 mm), aber NICHT ganz bis an die
  //   Aussenkante. Reicht das Bild voll bis zum Beschnitt-Rand →
  //   ok. Endet es mehr als 2,5 mm innen → vermutlich Absicht,
  //   kein Hinweis. Nur oben/unten, links/rechts ignoriert.
  // Rein informativ (nicht blockierend) — transparente PNGs koennen
  // den freien Rand bewusst nutzen.
  // ============================================================
  function checkBleedCoverage() {
    if (!state.fileBytes || !state.designPreviewSource) return null;
    const { width: srcW, height: srcH } = getRenderableSize(state.designPreviewSource);
    if (!srcW || !srcH) return null;
    const scale    = state.designScale || 1;
    const imgW_mm  = DESIGN_W * scale;
    const imgH_mm  = imgW_mm * (srcH / srcW);
    const centerY  = LABEL_H / 2 + (state.designOffsetY || 0);
    const topMm    = centerY - imgH_mm / 2;
    const bottomMm = centerY + imgH_mm / 2;
    // EPS = 0,1 mm Toleranz an der Beschnitt-Aussenkante, damit ein
    // exakt bis zum Rand gezogenes Bild nicht faelschlich warnt.
    const EPS = 0.1;
    const top    = topMm    > (-BLEED + EPS)            && topMm    <= BLEED;
    const bottom = bottomMm < (LABEL_H + BLEED - EPS)   && bottomMm >= (LABEL_H - BLEED);
    return { top, bottom };
  }

  // Hinweis-Dialog ueber das bestehende Confirm-Overlay, aber mit
  // eigenen Button-Labels. Liefert true = trotzdem fortfahren.
  // Ohne Overlay-Markup wird nicht blockiert (return true).
  async function confirmBleed(text) {
    if (!dom.confirmOverlay) return true;
    const okOrig     = dom.btnConfirmOk     ? dom.btnConfirmOk.textContent     : '';
    const cancelOrig = dom.btnConfirmCancel ? dom.btnConfirmCancel.textContent : '';
    if (dom.btnConfirmOk)     dom.btnConfirmOk.textContent     = 'Trotzdem in den Warenkorb';
    if (dom.btnConfirmCancel) dom.btnConfirmCancel.textContent = 'Zurück zum Bearbeiten';
    let proceed = false;
    try {
      proceed = await confirmDelete(text);
    } finally {
      if (dom.btnConfirmOk)     dom.btnConfirmOk.textContent     = okOrig;
      if (dom.btnConfirmCancel) dom.btnConfirmCancel.textContent = cancelOrig;
    }
    return proceed;
  }

  // ============================================================
  // (Hier stand eine zweite, ältere window.renderCleanLabelCanvas-
  // Bridge ohne Custom-Text-Rendering. Sie überschrieb beim Modul-
  // Init die richtige Definition weiter oben (~Z. 1583) und war
  // damit der Grund, warum Custom-Text in der 3D-Vorschau gefehlt
  // hat. Die richtige Version bleibt direkt nach renderPreview()
  // stehen — siehe oben. 2026-05-11 entfernt.)
  // ============================================================

  // ============================================================
  // UI-Helpers (Quelle Z.1664–1683)
  // ============================================================
  // ============================================================
  // In-Button-Ladeindikator (Cart-Add)
  // ------------------------------------------------------------
  // Zwischen Klick auf „In den Warenkorb legen" und dem Aufgehen
  // des Cart-Drawers laeuft PDF-Bau + Quelldatei-Upload + Cart-
  // Add — mehrere Sekunden ohne sichtbares Feedback am Button.
  // Die Status-Bar sitzt oben in der Vorschau und wird (v. a. mobil,
  // Vorschau ist sticky) leicht uebersehen. Diese Helfer blenden
  // direkt im Button einen Spinner ein und tauschen das Label gegen
  // den aktuellen Phasen-Text. clearCartBtnLoading() stellt den
  // Originaltext wieder her (kennt auch das Re-Edit-Label
  // „Aenderungen speichern", da lazy beim ersten Loading erfasst).
  // ============================================================
  function setCartBtnLoading(text) {
    const btn = dom.btnPrint;
    if (!btn) return;
    const label = btn.querySelector('[data-koebi-label-print-label]');
    if (!btn.dataset.koebiDefaultLabel) {
      btn.dataset.koebiDefaultLabel = label ? label.textContent : btn.textContent;
    }
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    if (label && text) label.textContent = text;
  }

  function clearCartBtnLoading() {
    const btn = dom.btnPrint;
    if (!btn || !btn.classList.contains('is-loading')) return;
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    const label = btn.querySelector('[data-koebi-label-print-label]');
    const orig  = btn.dataset.koebiDefaultLabel;
    if (label && orig != null) label.textContent = orig;
  }

  function updateButton() {
    // Cart-Flow ist am Ende — Ladeindikator im Button zuruecksetzen.
    // updateButton() ist der gemeinsame Endpunkt aller generatePDF-
    // Pfade (Erfolg, Validierungs-Abbruch, Fehler), daher hier sicher.
    clearCartBtnLoading();
    // Bild ist optional — Download/Bestellen sobald Sorte gesetzt ist.
    // Direktdruck (Admin) braucht weiter eine Datei, um sinnvoll zu sein.
    const ok       = !!state.selectedSorte;
    const adminOk  = !!(state.fileBytes && state.selectedSorte);
    if (dom.btnCheck)       dom.btnCheck.disabled       = !ok;
    if (dom.btnPrint)       dom.btnPrint.disabled       = !ok;
    if (dom.btnDirectPrint) dom.btnDirectPrint.disabled = !adminOk;
    if (dom.btnReset)       dom.btnReset.classList.toggle('visible', !!state.fileBytes);
  }

  function showStatus(text, type) {
    const bar = dom.statusBar;
    if (!bar) return;
    if (!text) {
      bar.hidden = true;
      bar.className = 'status-bar';
      return;
    }
    bar.hidden = false;
    bar.className = 'status-bar visible'
      + (type === 'success' ? ' success' : type === 'error' ? ' error' : '');
    const spinner = bar.querySelector('.spinner');
    if (spinner) spinner.style.display = type === 'loading' ? 'block' : 'none';
    if (dom.statusText) dom.statusText.textContent = text;
    if (type === 'success') {
      setTimeout(() => bar.classList.remove('visible'), 6000);
    }
  }

  function resetColors() {
    applyColor('#ffffff');
  }

  // Phase 3 – Drag/Scale-Resets (separat aufrufbar)
  function resetDesignTransform({ silent = false } = {}) {
    state.designOffsetX = 0;
    state.designOffsetY = 0;
    state.designScale   = 1;
    // Nur abgeskaliertes Cache-Canvas invalidieren — designPreviewSource bleibt intakt!
    // (Sonst würde ein Reset direkt nach Upload das gerade geladene Bild verschwinden lassen.)
    resetDesignPreviewCache({ source: false });
    if (dom.designScaleRange) {
      dom.designScaleRange.value = '100';
      updateSliderProgress(dom.designScaleRange);
    }
    if (dom.designScaleValue) dom.designScaleValue.textContent = '100';
    if (!silent) updatePreview();
  }
  function resetTextTransform({ silent = false } = {}) {
    state.textOffsetX = 0;
    state.textOffsetY = 0;
    if (!silent) updatePreview();
  }

  function resetAll() {
    state.fileBytes         = null;
    state.fileName          = '';
    state.fileType          = '';
    state.pdfExportBytes    = null;
    state.rasterExportBytes = null;
    state.rasterExportType  = '';
    state.imageDataUrl      = null;
    state.imageW            = 0;
    state.imageH            = 0;
    resetDesignPreviewCache();
    resetFramePreviewCache();
    state.previewDesignCanvas = null;
    state.previewDesignBounds = null;
    state.previewTextBounds   = null;
    state.pdfCmykPalette = null;
    state.bgColorCmyk    = null;
    setCmykFields(null);

    // Phase 3 – Transforms zurücksetzen + UI-Slider syncen
    resetDesignTransform({ silent: true });
    resetTextTransform({ silent: true });
    updateTransformUiVisibility();

    if (dom.uploadZone) dom.uploadZone.classList.remove('has-file');
    if (dom.fileinfo)   dom.fileinfo.hidden = true;
    if (dom.filename)   dom.filename.textContent = '';
    if (dom.fileInput)  dom.fileInput.value = '';

    applyColor('#ffffff');
    updateButton();
    updatePreview();
    showStatus('', '');
  }

  // ============================================================
  // Sticky-Top-Tracking: Header-State live verfolgen
  // ------------------------------------------------------------
  // Der Theme-Header (`<sticky-header>` aus sections/header.liquid)
  // hat drei sichtbare States, die als Klassen am `.section-header`-
  // Element toggeln (siehe header.liquid Z. 669–685):
  //   1. weder `…-sticky` noch `…-hidden` (ganz oben am Page-Top):
  //      Header ist im normalen Flow, scrollt mit der Page weg.
  //   2. `shopify-section-header-sticky` (ohne `…-hidden`):
  //      Header ist sticky-fixed am Viewport-Top, sichtbar.
  //   3. `shopify-section-header-sticky shopify-section-header-hidden`:
  //      Header ist sticky-fixed aber per transform versteckt
  //      (User scrollt runter — Header rastet weg).
  //
  // Nur in State #2 muss der Vorschau-Sticky-Wrapper unter dem
  // Header sitzen. In #1 und #3 darf er bis 16 px ans Viewport-Top
  // ranrutschen. CSS-Var `--editor-sticky-top` wird hier live je
  // nach Header-Klasse gesetzt; `.preview-area__sticky` liest den
  // Wert über `top: var(--editor-sticky-top, 16px)`.
  // ============================================================
  function setupStickyHeaderTracking(editorRoot) {
    const header = document.querySelector('.section-header');
    if (!header || !editorRoot) return;

    function applyOffset() {
      const isStickyVisible = header.classList.contains('shopify-section-header-sticky')
        && !header.classList.contains('shopify-section-header-hidden');
      if (isStickyVisible) {
        const headerH = header.offsetHeight || 0;
        editorRoot.style.setProperty('--editor-sticky-top', `${headerH + 16}px`);
      } else {
        editorRoot.style.setProperty('--editor-sticky-top', '16px');
      }
    }

    // Initial applyen (kann zu früh sein wenn Header offset noch
    // nicht messbar — applyOffset ist idempotent, ein zweiter Call
    // beim ersten Scroll-Event korrigiert das).
    applyOffset();

    // Klassen-Toggles am Header beobachten (sticky-header.js wechselt
    // sie in den hide/reveal/reset-Methoden bei Scroll).
    const observer = new MutationObserver(applyOffset);
    observer.observe(header, { attributes: true, attributeFilter: ['class'] });

    // Bei Resize Header-Höhe neu vermessen (z. B. Mobile-Breakpoint
    // wechselt → 60 px / 90 px Header).
    window.addEventListener('resize', applyOffset, { passive: true });
  }

  // ============================================================
  // Mobile-Copy-Button (eigenständig, läuft auch auf Mobile)
  // ============================================================
  function setupMobileCopyButton() {
    const btn = document.querySelector('[data-koebi-label-copy-link]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const span = btn.querySelector('span');
        const original = span ? span.textContent : '';
        if (span) span.textContent = 'Kopiert!';
        setTimeout(() => { if (span) span.textContent = original; }, 1600);
      } catch (e) {
        // Clipboard-API kann fehlschlagen (z. B. http) — still failen
      }
    });
  }

  // ============================================================
  // Browser-Forward/Back: hard reload bei bfcache-Restore
  // ------------------------------------------------------------
  // Wenn die Seite über Browser-History (zurück / vor) restauriert
  // wird, sind die Form-Inputs (Textarea, Slider, Color-Picker)
  // noch mit alten Werten befüllt, aber JS-State (state.fileBytes,
  // state.designScale, Preview-Canvas) sind weg → Vorschau passt
  // nicht zu den Eingabefeldern. Sauberster Reset: hard reload —
  // bypasst bfcache komplett, Editor startet auf Default. Greift
  // nur wenn die Page WIRKLICH aus dem bfcache kommt
  // (`event.persisted === true`), bei normaler Erst-Navigation
  // wird nichts neu geladen.
  // ============================================================
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });

  // Schmaler Test-Hook fuer die isolierte Browser-Regression. Im Store ist
  // er nicht aktiv; die Testseite muss ihn vor dem Script explizit freigeben.
  if (window.koebiLabelEnableTestHooks) {
    window.__koebiLabelImageTest = {
      state,
      getFileType,
      readJpegExifOrientation,
      normalizeRasterImage,
      normalizePdfPageForExport,
      handleFile,
      buildPdfBytes,
      buildLabelPdfBytes,
      buildA4SheetBlob,
    };
  }

  // ============================================================
  // Bootstrap
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
